import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '@ohif/ui-next';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import femaleIcon from '../../assets/female-icon.png';
import maleIcon from '../../assets/male-icon.png';
import resetIcon from '../../assets/reset-icon.png';
import series3DThumbnail from '../../assets/thumbnail-3d.png';
import seriesAxialThumbnail from '../../assets/thumbnail-axial.png';
import seriesCoronalThumbnail from '../../assets/thumbnail-coronel.png';
import seriesSagittalThumbnail from '../../assets/thumbnail-sagittal.png';
import parcelizationThumbnail from '../../assets/parcelization-thumbnail.png';
import { useAuth } from '../context/AuthContext';
import {
  buildDicomSeriesKey,
  mapApiSeriesToDisplaySeries,
  type DisplayDicomSeries,
} from '../services/dicomSeriesAdapters';
import { ApiError } from '../services/api';
import {
  analyzeDicomFiles,
  deleteDicomUpload,
  getDicomUploadAnalysis,
} from '../services/dicomService';
import {
  getAtlasCatalog,
  getAtlasThumbnail,
  getParcelizationCatalog,
  type AtlasCatalogItem,
  type ParcelizationCatalogItem,
} from '../services/atlasService';
import {
  prepareRemainingStudyData,
  prepareSelectedSeriesData,
  rotatePreparedNiftiAsset,
  startRemainingUploadAlignment,
  type NiftiRotationOperation,
  type PreparedNiftiAsset,
  type RotatePreparedNiftiResponse,
} from '../services/niftiPreparationService';
import type { DicomAlignmentStatus, DicomSeriesAnalysis, DicomStudyAnalysis } from '../types/dicom';
import { DicomSeriesPanel } from '../components/NewProjectModal';
import DicomSeriesThumbnail from '../components/NewProjectModal/DicomSeriesThumbnail';
import { getPatient, type PatientRecord } from '../services/patientService';
import ViewerPatientInfo from './ProjectCreation/ViewerPatientInfo';
import AtlasSelectionSections from './ProjectCreation/AtlasSelectionSections';
import selectAtlasTemplates, {
  getAtlasDisplayName,
  getAtlasFamily,
  getAtlasFamilyTitle,
  getSelectedAtlasChoices,
} from '../utils/selectAtlasTemplates';
import PatientPhoto from '../components/PatientPhoto';
import { WorkflowIcon, type WorkflowIconName } from '../components/StudyWorkflowMenu';
import Topbar from '../components/Topbar';
import OHIFEmbeddedViewer, {
  buildOHIFViewerUrl,
  type FixedViewerLayoutId,
  type ProjectViewerOrientation,
  type ProjectViewerSelectedView,
  type ProjectViewerResetTarget,
  type ProjectViewerSliceThumbnail,
  type ProjectViewerPanResetRequest,
  type ProjectViewerGridLayout,
  type ProjectViewerRotation,
  type ProjectViewerParcelizationVolume,
  type ProjectViewerRegistrationViewportState,
  type ProjectViewerRegistrationTransform,
} from './ProjectCreation/OHIFEmbeddedViewer';
import { formatDateDDMMYYYY } from '../utils/dateFormat';
import { getNiftiAlignmentLabel } from '../utils/niftiAlignmentLabel';
import { publicUrl } from '../utils/publicUrl';
import './ProjectCreation.css';
import './ProjectCreationGrid.css';

/* ── Types ── */
type ViewerSlot = 'left' | 'center' | 'right';
type ResizablePanelId = 'series' | ViewerSlot | 'parcelization';
type ScanToAlignRole =
  | 'moving1'
  | 'moving2'
  | 'moving3'
  | 'moving4'
  | 'moving5'
  | 'moving6'
  | 'moving7'
  | 'moving8'
  | 'moving9'
  | 'moving10';
type CtRole = 'ct' | 'ct2' | 'ct3' | 'ct4' | 'ct5' | 'ct6' | 'ct7' | 'ct8' | 'ct9' | 'ct10';
type AtlasRole = 'atlas';
type DicomViewerRole = 'fixed' | ScanToAlignRole;
type DicomUploadRole = DicomViewerRole | CtRole;
// CT remains outside the MR-to-MR registration roles, but it uses the same
// original/aligned viewer asset lifecycle and edit controls.
type ViewerRole = DicomViewerRole | AtlasRole | CtRole;
type SeriesDisplayVariant = 'original' | 'aligned';
type ViewerRoleOption = {
  id: ViewerRole;
  label: string;
  variant: SeriesDisplayVariant;
  alignmentMode?: 'auto' | 'manual' | null;
  key: string;
  /** The complete source object represented by this registration option. */
  sourceSeries?: DicomSeries;
  thumbnailSrc?: string;
  /** The uploaded series used to resolve the server-generated thumbnail set. */
  thumbnailSeries?: {
    uploadId?: string;
    studyInstanceUid: string;
    seriesInstanceUid: string;
    label: string;
    thumbnailDataUrls?: string[];
  };
  atlasId?: string;
  isCurrentAtlas?: boolean;
  atlasTag?: string;
};

const isAtlasCtOption = (option: ViewerRoleOption) =>
  isAtlasRole(option.id) &&
  (option.atlasTag?.trim().toUpperCase() === 'CT' ||
    option.sourceSeries?.modality?.trim().toUpperCase() === 'CT');
type ToolMode = ViewerRole | 'nifti';
type ViewerAssetStatus = 'missing' | 'loaded' | 'registered';
type ViewerEditSaveStatus = 'idle' | 'saving';
type LeftViewerGrid = ProjectViewerGridLayout;
type WorkspacePreset =
  | 'workspace'
  | 'viewer'
  | 'edit'
  | 'image-fusion'
  | 'distortion-correction'
  | 'object-management'
  | 'smartbrush'
  | 'stereotaxy'
  | 'lead-localization';
type DicomSeries = DisplayDicomSeries & {
  uploadId?: string;
  atlasContrast?: string;
  workspaceUpload?: boolean;
  niftiAsset?: PreparedNiftiAsset;
  alignedNiftiAsset?: PreparedNiftiAsset;
  viewerConfigPath?: string;
  viewerKey?: string;
};
type FixedViewerLayoutOption = {
  id: FixedViewerLayoutId;
  label: string;
  isParcelization?: boolean;
};

interface PatientInfo {
  name: string;
  dob: string;
  age?: number;
  gender?: string;
  studyId: string;
  studyInstanceUid?: string;
  patientId?: string;
  hasPhoto?: boolean;
  photoVersion?: string;
}

const calculatePatientAge = (dob?: string | null) => {
  const formattedDob = formatDateDDMMYYYY(dob ?? '', '');
  const [day, month, year] = formattedDob.split('.').map(Number);

  if (!day || !month || !year) return undefined;

  const today = new Date();
  let age = today.getFullYear() - year;

  if (today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day)) {
    age -= 1;
  }

  return age >= 0 ? age : undefined;
};

interface ProjectWorkspaceLocationState {
  openedFrom?: 'dashboard' | 'new-project';
  workspacePreset?: WorkspacePreset;
  uploadId?: string;
  fixedStudyInstanceUid?: string | null;
  fixedSeriesInstanceUid?: string | null;
  dicomStudies?: DicomStudyAnalysis[];
  dicomSeries?: DicomSeriesAnalysis[];
  preparedNiftiAssets?: PreparedNiftiAsset[];
  alignedNiftiAssets?: PreparedNiftiAsset[];
  alignmentStatus?: DicomAlignmentStatus;
  study?: {
    id: string;
    patientName: string;
    dob?: string;
    age?: number;
    gender?: string;
    studyId?: string;
    studyInstanceUid?: string;
    method?: string;
    modalities?: string[];
    date?: string;
    status?: string;
    source?: string;
    patientId?: string;
    hasPhoto?: boolean;
  };
}

interface ToolUploadMeta {
  name: string;
  count: number;
  size: number;
  source: 'files' | 'folder';
}

type RoleDicomUploadStatus = 'idle' | 'uploading' | 'success' | 'error';

interface RoleDicomUploadState {
  uploadId: string | null;
  series: DicomSeries[];
  status: RoleDicomUploadStatus;
  error: string | null;
  alignmentStatus: DicomAlignmentStatus;
}

interface ProjectCreationProps {
  routeLocation?: Location;
}

interface FixedDicomViewportState {
  openedFrom?: 'dashboard' | 'new-project';
  uploadId?: string;
  fixedStudyInstanceUid?: string | null;
  fixedSeriesInstanceUid?: string | null;
}

interface ResolvedFixedDicomViewportState {
  shouldRender: boolean;
  hasMissingIdentifiers: boolean;
  uploadId: string | null;
  studyInstanceUid: string | null;
  seriesInstanceUid: string | null;
  niftiAssetId: string | null;
  viewerConfigPath: string | null;
}

export function resolveFixedDicomViewportState(
  slot: ViewerSlot,
  selectedRole: ViewerRole,
  fixedDicomViewportState?: FixedDicomViewportState,
  series?: DicomSeries | null
): ResolvedFixedDicomViewportState {
  const seriesUploadId = series?.uploadId;
  const isAtlasSeries = isAtlasRole(selectedRole) && Boolean(series?.viewerConfigPath);
  const isInitialFixedProjectSeries =
    slot === 'left' &&
    selectedRole === 'fixed' &&
    fixedDicomViewportState?.openedFrom === 'new-project';
  const uploadId = isAtlasSeries
    ? 'atlas'
    : (seriesUploadId ?? fixedDicomViewportState?.uploadId ?? null);
  const isTargetViewport =
    isAtlasSeries ||
    (!isAtlasRole(selectedRole) && Boolean(uploadId && (series || isInitialFixedProjectSeries)));

  if (!isTargetViewport) {
    const hasIncompleteInitialProjectSeries =
      isInitialFixedProjectSeries &&
      !Boolean(
        uploadId &&
          (series?.studyInstanceUid ?? fixedDicomViewportState?.fixedStudyInstanceUid) &&
          (series?.seriesInstanceUid ?? fixedDicomViewportState?.fixedSeriesInstanceUid)
      );

    return {
      shouldRender: false,
      hasMissingIdentifiers: hasIncompleteInitialProjectSeries,
      uploadId: null,
      studyInstanceUid: null,
      seriesInstanceUid: null,
      niftiAssetId: null,
      viewerConfigPath: null,
    };
  }

  const studyInstanceUid =
    series?.studyInstanceUid ?? fixedDicomViewportState?.fixedStudyInstanceUid ?? null;
  const seriesInstanceUid =
    series?.seriesInstanceUid ?? fixedDicomViewportState?.fixedSeriesInstanceUid ?? null;
  // CT and MR use exactly the same prepared-NIfTI viewer path. Modality only
  // affects SOP metadata and the initial VOI; it must not select a separate
  // DICOM rendering implementation.
  const niftiAssetId = series?.niftiAsset?.asset_id ?? series?.viewerKey ?? null;
  const viewerConfigPath = series?.viewerConfigPath ?? null;
  const hasIdentifiers = Boolean(
    uploadId && studyInstanceUid && seriesInstanceUid && (niftiAssetId || viewerConfigPath)
  );

  return {
    shouldRender: hasIdentifiers,
    hasMissingIdentifiers: !hasIdentifiers,
    uploadId,
    studyInstanceUid,
    seriesInstanceUid,
    niftiAssetId,
    viewerConfigPath,
  };
}

export function resolveViewerRoleSeries(
  role: ViewerRole,
  selectedSeriesByRole: Record<ViewerRole, string | null>,
  activeDicomSeries: DicomSeries[],
  roleDicomUploads: Record<DicomUploadRole, RoleDicomUploadState>,
  preparedNiftiAssets: PreparedNiftiAsset[]
): DicomSeries | null {
  if (isAtlasRole(role)) {
    return null;
  }

  const selectedSeriesId = selectedSeriesByRole[role];
  if (!selectedSeriesId) {
    return null;
  }

  const roleUpload = roleDicomUploads[role];
  const seriesOptions = roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
  const selectedSeries = seriesOptions.find(series => series.id === selectedSeriesId);
  if (!selectedSeries || !isSeriesCompatibleWithRole(selectedSeries, role)) {
    return null;
  }

  const niftiAsset =
    selectedSeries.niftiAsset ??
    preparedNiftiAssets.find(
      asset => asset.series_instance_uid === selectedSeries.seriesInstanceUid
    );

  return niftiAsset && niftiAsset !== selectedSeries.niftiAsset
    ? { ...selectedSeries, niftiAsset }
    : selectedSeries;
}

export function resolveRenderableViewerRole(
  slot: ViewerSlot,
  selectedRole: ViewerRole,
  secondaryRole?: ViewerRole,
  tertiaryRole?: ViewerRole,
  showTertiaryRole = true,
  hasExplicitSelection = false,
  hasSelectedPrimarySource = false
): ViewerRole | null {
  if (slot === 'left' || hasExplicitSelection) {
    return selectedRole;
  }

  // The first center control is the fixed/primary source. Once that source
  // has a selected series, keep it visible in the middle viewer instead of
  // waiting for a comparison source or registration workflow to be active.
  if (slot === 'center' && hasSelectedPrimarySource) {
    return selectedRole;
  }

  // The comparison workspaces intentionally remain empty until their own
  // workflows are implemented. Atlas and CT are complete viewer sources and
  // must render in whichever strip selector contains them, including the
  // secondary center selector.
  if (isAtlasRole(selectedRole) || isCtRole(selectedRole)) {
    return selectedRole;
  }

  if (
    slot === 'center' &&
    secondaryRole &&
    (isAtlasRole(secondaryRole) || isCtRole(secondaryRole))
  ) {
    return secondaryRole;
  }

  return null;
}

/* ── Mock patient ── */
const DEFAULT_PATIENT: PatientInfo = {
  name: 'Jane Doe',
  dob: '1985-04-12',
  gender: 'Female',
  studyId: 'NP-2026-001',
};

/* ── Mock DICOM series ── */
const MOCK_DICOM_SERIES: DicomSeries[] = [
  {
    id: '1',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-1',
    modality: 'Unknown',
    label: 'Head 1.0',
    slices: 1101,
    type: 'CT',
    matchLevel: 'Uncertain',
    thumbIndex: 0,
    eligibleForFixed: false,
    title: 'Head 1.0 · Unknown date',
  },
  {
    id: '2',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-2',
    modality: 'Unknown',
    label: '0.5',
    slices: 463,
    type: 'CT',
    matchLevel: 'Uncertain',
    thumbIndex: 1,
    eligibleForFixed: false,
    title: '0.5 · Unknown date',
  },
  {
    id: '3',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-3',
    modality: 'T1w',
    label: 'AX T1 +C',
    slices: 360,
    type: 'MR',
    tr: '6 ms',
    te: '3.2 ms',
    dim: '3D',
    matchLevel: 'High match',
    thumbIndex: 2,
    eligibleForFixed: true,
    title: 'AX T1 +C · Unknown date',
  },
  {
    id: '4',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-4',
    modality: 'T2w',
    label: '[Corrected] AX T2 2MM',
    slices: 80,
    type: 'MR',
    matchLevel: 'High match',
    thumbIndex: 3,
    eligibleForFixed: true,
    title: '[Corrected] AX T2 2MM · Unknown date',
  },
  {
    id: '5',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-5',
    modality: 'T2w',
    label: 'AX T2 2MM',
    slices: 80,
    type: 'MR',
    tr: '11200 ms',
    te: '77 ms',
    dim: '2D',
    matchLevel: 'High match',
    thumbIndex: 4,
    eligibleForFixed: true,
    title: 'AX T2 2MM · Unknown date',
  },
  {
    id: '6',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-6',
    modality: 'Unknown',
    label: '4.000',
    slices: 48,
    type: 'CT',
    matchLevel: 'Uncertain',
    thumbIndex: 5,
    eligibleForFixed: false,
    title: '4.000 · Unknown date',
  },
  {
    id: '7',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-7',
    modality: 'FLAIR',
    label: 'AX FLAIR',
    slices: 32,
    type: 'MR',
    tr: '9000 ms',
    te: '90 ms',
    dim: '2D',
    matchLevel: 'High match',
    thumbIndex: 6,
    eligibleForFixed: true,
    title: 'AX FLAIR · Unknown date',
  },
  {
    id: '8',
    studyInstanceUid: 'mock-study-1',
    seriesInstanceUid: 'mock-series-8',
    modality: 'DWI',
    label: 'DWI b1000',
    slices: 60,
    type: 'MR',
    matchLevel: 'Uncertain',
    thumbIndex: 7,
    eligibleForFixed: false,
    title: 'DWI b1000 · Unknown date',
  },
];

const MAX_SCAN_TO_ALIGN_COUNT = 10;
const SCAN_TO_ALIGN_ROLES: ScanToAlignRole[] = [
  'moving1',
  'moving2',
  'moving3',
  'moving4',
  'moving5',
  'moving6',
  'moving7',
  'moving8',
  'moving9',
  'moving10',
];
const MAX_CT_COUNT = 10;
const CT_ROLES: CtRole[] = ['ct', 'ct2', 'ct3', 'ct4', 'ct5', 'ct6', 'ct7', 'ct8', 'ct9', 'ct10'];
const ATLAS_ROLES: AtlasRole[] = ['atlas'];
const PARCELLIZATION_OPTIONS: ParcelizationCatalogItem[] = [
  { id: 'stn', label: 'STN', color: [34, 211, 238], source_atlas: 'LeadDBS', volumes: [] },
  { id: 'gpi', label: 'GPi', color: [244, 114, 182], source_atlas: 'LeadDBS', volumes: [] },
  { id: 'gpe', label: 'GPe', color: [250, 204, 21], source_atlas: 'LeadDBS', volumes: [] },
  { id: 'snr', label: 'SNr', color: [167, 139, 250], source_atlas: 'LeadDBS', volumes: [] },
  { id: 'rn', label: 'Red Nucleus', color: [248, 113, 113], source_atlas: 'LeadDBS', volumes: [] },
  { id: 'vim', label: 'VIM', color: [74, 222, 128], source_atlas: 'LeadDBS', volumes: [] },
];
const isAtlasRole = (role: ToolMode | null | undefined): role is AtlasRole =>
  Boolean(role && ATLAS_ROLES.includes(role as AtlasRole));

const isCtRole = (role: ViewerRole | ToolMode): role is CtRole => CT_ROLES.includes(role as CtRole);

const isSeriesCompatibleWithRole = (series: DisplayDicomSeries, role: DicomUploadRole) =>
  isCtRole(role) ? series.type === 'CT' : series.type === 'MR' && series.modality !== 'CT';

const getViewerRoleLabel = (role: ViewerRole) => {
  if (role === 'fixed') return 'MR';
  if (isAtlasRole(role)) return 'Atlas';
  if (isCtRole(role)) {
    const ctNumber = CT_ROLES.indexOf(role) + 1;
    return ctNumber === 1 ? 'CT' : `CT ${ctNumber}`;
  }

  const scanNumber = SCAN_TO_ALIGN_ROLES.indexOf(role) + 1;
  return `MR ${scanNumber}`;
};

const LEFT_VIEWER_GRID_OPTIONS: LeftViewerGrid[] = [1, 2, 4, 6];
const LEFT_VIEWER_GRID_ICONS: Record<LeftViewerGrid, string> = {
  1: 'layout-common-1x1',
  2: 'layout-common-1x2',
  4: 'layout-common-2x2',
  6: 'layout-common-2x3',
};
export const FIXED_VIEWER_LAYOUT_OPTIONS: readonly FixedViewerLayoutOption[] = [
  {
    id: 'singleAxial',
    label: 'Axial',
  },
  {
    id: 'singleCoronal',
    label: 'Coronal',
  },
  {
    id: 'singleSagittal',
    label: 'Sagittal',
  },
  {
    id: 'only3D',
    label: '3D',
  },
];
const PARCELIZATION_LAYOUT_OPTION: FixedViewerLayoutOption = {
  id: 'only3D',
  label: 'Parcelization',
  isParcelization: true,
};
const SERIES_LAYOUT_THUMBNAILS: Record<FixedViewerLayoutId, string> = {
  singleAxial: seriesAxialThumbnail,
  singleCoronal: seriesCoronalThumbnail,
  singleSagittal: seriesSagittalThumbnail,
  only3D: series3DThumbnail,
};
// Keep the bundled artwork as a fallback, while preferring the actual
// server/viewport thumbnail for each loaded source in registration.
const USE_STATIC_SERIES_THUMBNAILS = false;
const FIXED_VIEWER_LAYOUT_RESET_TARGETS: Record<FixedViewerLayoutId, ProjectViewerResetTarget> = {
  singleAxial: 'axial',
  singleCoronal: 'coronal',
  singleSagittal: 'sagittal',
  only3D: '3d',
};
const buildOrientedThumbnailKey = (seriesKey: string, orientation: ProjectViewerOrientation) =>
  `${seriesKey}:${orientation}`;
const getMiddleSeriesThumbnail = (series?: DicomSeries) => {
  const thumbnails = series?.thumbnailDataUrls?.filter(source => {
    if (!source || typeof source !== 'string') return false;
    return (
      source.startsWith('data:image/') ||
      source.startsWith('blob:') ||
      source.startsWith('http://') ||
      source.startsWith('https://') ||
      source.startsWith('/')
    );
  });
  return thumbnails?.[Math.floor(thumbnails.length / 2)];
};
const RESIZABLE_PANEL_ORDER: ResizablePanelId[] = [
  'series',
  'left',
  'center',
  'parcelization',
  'right',
];
const COLLAPSED_VIEWER_WORKFLOW_ICONS: Record<ViewerSlot, WorkflowIconName> = {
  left: 'viewer',
  center: 'fusion',
  right: 'stereotaxy',
};
const RESIZE_BOUNDARIES = [0, 1, 2, 3] as const;
const PANEL_RESIZE_LIMITS = {
  horizontal: {
    series: {
      min: 96,
      max: 168,
      maxShare: 0.28,
    },
    viewer: {
      min: 300,
      maxShare: 0.75,
    },
  },
  vertical: {
    series: {
      min: 160,
      maxShare: 0.75,
    },
    viewer: {
      min: 180,
      maxShare: 0.75,
    },
  },
} as const;

interface WorkspacePresetConfig {
  seriesOpen: boolean;
  visibleViewers: Record<ViewerSlot, boolean>;
  renderedViewerSlots: ViewerSlot[];
  leftViewerGrid: LeftViewerGrid;
  viewerRoles: Record<ViewerSlot, ViewerRole>;
  centerSecondaryRole: ViewerRole;
  centerTertiaryRole: ViewerRole;
  showCenterTertiaryRole: boolean;
  showNiftiTool: boolean;
}

const DEFAULT_VIEWER_ROLES: Record<ViewerSlot, ViewerRole> = {
  left: 'fixed',
  center: 'moving1',
  right: 'moving1',
};

const WORKSPACE_PRESETS: Record<WorkspacePreset, WorkspacePresetConfig> = {
  workspace: {
    seriesOpen: true,
    visibleViewers: { left: true, center: false, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },
  viewer: {
    seriesOpen: true,
    visibleViewers: { left: true, center: false, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },
  /* Edit: same layout as Viewer - it opens the same workspace and, once the
     left viewer's aligned NIfTI is ready, drops straight into edit mode
     (see the auto-start effects near startViewerEditMode) instead of
     requiring the extra click on the pencil button. */
  edit: {
    seriesOpen: true,
    visibleViewers: { left: true, center: false, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 4,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },

  /* Image Fusion: Series + Center açık, Left ve Right collapsed.
     Center içinde yalnızca iki MR seçicisi gösterilir. */
  'image-fusion': {
    seriesOpen: true,
    visibleViewers: { left: false, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: {
      left: 'fixed',
      center: 'fixed',
      right: 'moving1',
    },
    centerSecondaryRole: 'moving1',
    centerTertiaryRole: 'moving1',
    showCenterTertiaryRole: false,
    showNiftiTool: false,
  },
  /* Distortion Correction Cranial: yalnızca Center açık gelir.
     Series, Left ve Right collapsed durumdadır. Center iki MR kaynağı ile açılır.
     NIfTI yükleme düğmesi yalnızca bu preset içinde gösterilir. */
  'distortion-correction': {
    seriesOpen: false,
    visibleViewers: { left: false, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: {
      left: 'fixed',
      center: 'fixed',
      right: 'moving1',
    },
    centerSecondaryRole: 'moving1',
    centerTertiaryRole: 'moving1',
    showCenterTertiaryRole: false,
    showNiftiTool: true,
  },
  /* Object Management: Series, Left ve Center açık; Right collapsed.
     Left tek gridde Atlas ile, Center ise MR + Atlas ile açılır. */
  'object-management': {
    seriesOpen: true,
    visibleViewers: { left: true, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: {
      left: 'atlas',
      center: 'moving1',
      right: 'moving1',
    },
    centerSecondaryRole: 'atlas',
    centerTertiaryRole: 'fixed',
    showCenterTertiaryRole: false,
    showNiftiTool: false,
  },
  smartbrush: {
    seriesOpen: true,
    visibleViewers: { left: true, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },
  stereotaxy: {
    seriesOpen: true,
    visibleViewers: { left: true, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },
  'lead-localization': {
    seriesOpen: true,
    visibleViewers: { left: true, center: true, right: false },
    renderedViewerSlots: ['left', 'center', 'right'],
    leftViewerGrid: 1,
    viewerRoles: { ...DEFAULT_VIEWER_ROLES },
    centerSecondaryRole: 'fixed',
    centerTertiaryRole: 'atlas',
    showCenterTertiaryRole: true,
    showNiftiTool: false,
  },
};

const isWorkspacePreset = (value: unknown): value is WorkspacePreset => {
  return typeof value === 'string' && value in WORKSPACE_PRESETS;
};

const resolveInitialFixedSeriesKey = (
  analyzedSeries: DicomSeriesAnalysis[] | null,
  fixedStudyInstanceUid?: string | null,
  fixedSeriesInstanceUid?: string | null
) => {
  if (fixedStudyInstanceUid && fixedSeriesInstanceUid) {
    return buildDicomSeriesKey(fixedStudyInstanceUid, fixedSeriesInstanceUid);
  }

  if (!analyzedSeries || !fixedSeriesInstanceUid) {
    return null;
  }

  const matchedSeries = analyzedSeries.find(
    series => series.series_instance_uid === fixedSeriesInstanceUid
  );

  if (!matchedSeries) {
    return null;
  }

  return buildDicomSeriesKey(matchedSeries.study_instance_uid, matchedSeries.series_instance_uid);
};

const getPanelResizeLimits = (
  panelId: ResizablePanelId,
  isVertical: boolean,
  layoutSize: number
) => {
  const axisLimits = isVertical ? PANEL_RESIZE_LIMITS.vertical : PANEL_RESIZE_LIMITS.horizontal;
  const panelLimits = panelId === 'series' ? axisLimits.series : axisLimits.viewer;
  const sharedMaximum = layoutSize * panelLimits.maxShare;
  const cappedMaximum =
    'max' in panelLimits ? Math.min(panelLimits.max, sharedMaximum) : sharedMaximum;

  return {
    minSize: panelLimits.min,
    maxSize: Math.max(panelLimits.min, cappedMaximum),
  };
};

const resolveResizeRange = (
  leftLimits: ReturnType<typeof getPanelResizeLimits>,
  rightLimits: ReturnType<typeof getPanelResizeLimits>,
  pairSize: number
) => {
  if (pairSize <= 0) {
    return {
      minimumAllowedLeft: 0,
      maximumAllowedLeft: 0,
    };
  }

  let leftMinimum = Math.max(0, leftLimits.minSize);
  let rightMinimum = Math.max(0, rightLimits.minSize);
  let leftMaximum = Math.max(leftMinimum, leftLimits.maxSize);
  let rightMaximum = Math.max(rightMinimum, rightLimits.maxSize);

  const minimumTotal = leftMinimum + rightMinimum;
  if (minimumTotal > pairSize && minimumTotal > 0) {
    const minimumScale = pairSize / minimumTotal;
    leftMinimum *= minimumScale;
    rightMinimum *= minimumScale;
  }

  leftMaximum = Math.max(leftMinimum, Math.min(leftMaximum, pairSize));
  rightMaximum = Math.max(rightMinimum, Math.min(rightMaximum, pairSize));

  let minimumAllowedLeft = Math.max(leftMinimum, pairSize - rightMaximum);
  let maximumAllowedLeft = Math.min(leftMaximum, pairSize - rightMinimum);

  if (minimumAllowedLeft > maximumAllowedLeft) {
    const midpoint = Math.min(
      pairSize - rightMinimum,
      Math.max(leftMinimum, startClamp(pairSize / 2, leftMinimum, leftMaximum))
    );

    minimumAllowedLeft = midpoint;
    maximumAllowedLeft = midpoint;
  }

  return {
    minimumAllowedLeft: Math.max(0, minimumAllowedLeft),
    maximumAllowedLeft: Math.max(0, maximumAllowedLeft),
  };
};

const startClamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

/* ── Left viewer layout SVG icons ── */
interface LeftViewerLayoutIconProps {
  layout: LeftViewerGrid;
}

const LeftViewerLayoutIcon: React.FC<LeftViewerLayoutIconProps> = ({ layout }) => {
  const commonRectProps = {
    rx: 1.5,
    fill: 'currentColor',
    fillOpacity: 0.08,
    stroke: 'currentColor',
    strokeWidth: 1.35,
    vectorEffect: 'non-scaling-stroke' as const,
  };

  return (
    <svg
      className="pc-layout-svg"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {layout === 1 && (
        <rect
          x="4"
          y="4"
          width="16"
          height="16"
          {...commonRectProps}
        />
      )}

      {layout === 2 && (
        <>
          <rect
            x="3"
            y="4"
            width="8"
            height="16"
            {...commonRectProps}
          />
          <rect
            x="13"
            y="4"
            width="8"
            height="16"
            {...commonRectProps}
          />
        </>
      )}

      {layout === 4 && (
        <>
          <rect
            x="3"
            y="3"
            width="8"
            height="8"
            {...commonRectProps}
          />
          <rect
            x="13"
            y="3"
            width="8"
            height="8"
            {...commonRectProps}
          />
          <rect
            x="3"
            y="13"
            width="8"
            height="8"
            {...commonRectProps}
          />
          <rect
            x="13"
            y="13"
            width="8"
            height="8"
            {...commonRectProps}
          />
        </>
      )}

      {layout === 6 && (
        <>
          <rect
            x="2"
            y="4"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
          <rect
            x="9.25"
            y="4"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
          <rect
            x="16.5"
            y="4"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
          <rect
            x="2"
            y="13"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
          <rect
            x="9.25"
            y="13"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
          <rect
            x="16.5"
            y="13"
            width="5.5"
            height="7"
            {...commonRectProps}
          />
        </>
      )}
    </svg>
  );
};

const DEFAULT_ROLE_SERIES = {
  fixed: '4',
  ...Object.fromEntries(ATLAS_ROLES.map(role => [role, null])),
  ...Object.fromEntries(CT_ROLES.map(role => [role, null])),
  ...Object.fromEntries(SCAN_TO_ALIGN_ROLES.map(role => [role, role === 'moving1' ? '8' : null])),
} as Record<ViewerRole, string | null>;

const createEmptyToolUploads = () =>
  ({
    fixed: null,
    ...Object.fromEntries(ATLAS_ROLES.map(role => [role, null])),
    nifti: null,
    ...Object.fromEntries(CT_ROLES.map(role => [role, null])),
    ...Object.fromEntries(SCAN_TO_ALIGN_ROLES.map(role => [role, null])),
  }) as Record<ToolMode, ToolUploadMeta | null>;

const createEmptyRoleDicomUploads = () =>
  Object.fromEntries(
    (['fixed', ...SCAN_TO_ALIGN_ROLES, ...CT_ROLES] as DicomUploadRole[]).map(role => [
      role,
      {
        uploadId: null,
        series: [],
        status: 'idle',
        error: null,
        alignmentStatus: 'ready',
      },
    ])
  ) as Record<DicomUploadRole, RoleDicomUploadState>;

const isDicomViewerRole = (toolId: ToolMode): toolId is DicomViewerRole =>
  toolId === 'fixed' || SCAN_TO_ALIGN_ROLES.includes(toolId as ScanToAlignRole);

const isDicomUploadRole = (toolId: ToolMode): toolId is DicomUploadRole =>
  isCtRole(toolId) || isDicomViewerRole(toolId);

export const getViewerAssetStatus = (
  isLoaded: boolean,
  isRegistrationActive: boolean
): ViewerAssetStatus => {
  if (!isLoaded) {
    return 'missing';
  }

  return isRegistrationActive ? 'registered' : 'loaded';
};

const isAbortError = (error: unknown) =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';

const getWorkspaceUploadError = (error: unknown) => {
  if (error instanceof ApiError) {
    return error.message || 'DICOM analysis failed.';
  }

  if (error instanceof TypeError) {
    return 'Backend unavailable.';
  }

  return error instanceof Error ? error.message : 'DICOM analysis failed.';
};

const createInitialSelectedSeriesByRole = (
  isNewProjectFlow: boolean,
  initialFixedSeriesKey: string | null,
  initialRole: DicomUploadRole = 'fixed'
) =>
  ({
    fixed: isNewProjectFlow
      ? initialRole === 'fixed'
        ? initialFixedSeriesKey
        : null
      : DEFAULT_ROLE_SERIES.fixed,
    ...Object.fromEntries(ATLAS_ROLES.map(role => [role, null])),
    ...Object.fromEntries(CT_ROLES.map(role => [role, null])),
    ...Object.fromEntries(SCAN_TO_ALIGN_ROLES.map(role => [role, null])),
    ...(isNewProjectFlow && initialRole === 'ct' ? { ct: initialFixedSeriesKey } : {}),
  }) as Record<ViewerRole, string | null>;

const mapAtlasToViewerSeries = (atlas: AtlasCatalogItem): DicomSeries => ({
  id: `atlas:${atlas.id}`,
  studyInstanceUid: atlas.study_instance_uid,
  seriesInstanceUid: atlas.series_instance_uid,
  modality: atlas.classification ?? 'Unknown',
  atlasContrast: atlas.contrast ?? undefined,
  label: getAtlasDisplayName(atlas),
  slices: atlas.dimensions?.[2] ?? 1,
  type: 'MR',
  dim: '3D',
  matchLevel: 'High match',
  thumbIndex: 0,
  eligibleForFixed: false,
  title: `${getAtlasDisplayName(atlas)} · ${atlas.space}`,
  uploadId: 'atlas',
  viewerConfigPath: `/atlases/${encodeURIComponent(atlas.id)}/viewer-config`,
  viewerKey: `atlas:${atlas.id}${atlas.content_version ? `:${atlas.content_version}` : ''}`,
});

const NeutralDicomThumbnail: React.FC = () => {
  return (
    <svg
      className="pc-neutral-dicom-thumbnail"
      viewBox="0 0 64 64"
      width="100%"
      height="100%"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="DICOM thumbnail"
    >
      <rect
        x="1"
        y="1"
        width="62"
        height="62"
        rx="7"
        fill="#080f1c"
        stroke="#334155"
        strokeWidth="2"
      />
      <ellipse
        cx="32"
        cy="31"
        rx="17"
        ry="22"
        fill="#111827"
        stroke="#64748b"
        strokeWidth="1.5"
      />
      <path
        d="M32 10v42M20 20c7 4 17 4 24 0M18 32c9 4 19 4 28 0M20 44c7-4 17-4 24 0"
        fill="none"
        stroke="#475569"
        strokeWidth="1.25"
        strokeLinecap="round"
      />
      <path
        d="M27 27c2.5-3 7.5-3 10 0v8c-2.5 3-7.5 3-10 0z"
        fill="#1e293b"
        stroke="#64748b"
        strokeWidth="1"
      />
    </svg>
  );
};

const ViewerEmptyState: React.FC<{ slot: ViewerSlot | 'series'; error?: boolean }> = ({
  slot,
  error,
}) => (
  <div
    className={`pc-viewer-empty-state ${error ? 'is-error' : ''}`}
    data-testid={`viewer-empty-state-${slot}`}
  >
    <span
      className="pc-viewer-empty-state-icon"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <rect
          x="3.5"
          y="4"
          width="17"
          height="16"
          rx="2.5"
        />
        <path d="M8 9h8M8 12h8M8 15h5" />
      </svg>
    </span>
    <p>{error ? 'DICOM series could not be displayed' : 'No image selected'}</p>
    {error && <span>Select another available series.</span>}
  </div>
);

const ProjectCreation = ({ routeLocation }: ProjectCreationProps) => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const location = routeLocation ?? routerLocation;
  const { accessToken, logout } = useAuth();

  const locationState = location.state as ProjectWorkspaceLocationState | null;
  const queryParams = new URLSearchParams(location.search);

  const openedStudy = locationState?.study;
  const [savedPatient, setSavedPatient] = useState<PatientRecord | null>(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientLoadError, setPatientLoadError] = useState<string | null>(null);
  const [patientLoadVersion, setPatientLoadVersion] = useState(0);
  const [isPatientInfoEditing, setIsPatientInfoEditing] = useState(false);
  const currentPatient = savedPatient?.id === openedStudy?.patientId ? savedPatient : null;
  useEffect(() => {
    if (!openedStudy?.patientId || !accessToken) return;
    const controller = new AbortController();
    setPatientLoading(true);
    setPatientLoadError(null);
    getPatient(openedStudy.patientId, accessToken, controller.signal)
      .then(patient => {
        if (!controller.signal.aborted) setSavedPatient(patient);
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          setPatientLoadError(
            error instanceof Error ? error.message : 'Patient information could not be loaded.'
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setPatientLoading(false);
      });
    return () => controller.abort();
  }, [openedStudy?.patientId, accessToken, patientLoadVersion]);
  const isNewProjectFlow = locationState?.openedFrom === 'new-project';
  const incomingAnalyzedSeries = locationState?.dicomSeries ?? null;
  const [preparedNiftiAssets, setPreparedNiftiAssets] = useState<PreparedNiftiAsset[]>(
    locationState?.preparedNiftiAssets ?? []
  );
  const [preparedAlignedNiftiAssets, setPreparedAlignedNiftiAssets] = useState<
    PreparedNiftiAsset[]
  >(locationState?.alignedNiftiAssets ?? []);
  const hasInitialUploadSnapshot = Boolean(
    locationState?.alignmentStatus &&
      Array.isArray(locationState.preparedNiftiAssets) &&
      Array.isArray(locationState.alignedNiftiAssets)
  );
  const [initialUploadAlignmentStatus, setInitialUploadAlignmentStatus] =
    useState<DicomAlignmentStatus>(locationState?.alignmentStatus ?? 'ready');
  const [shouldHydrateInitialUpload, setShouldHydrateInitialUpload] = useState(
    Boolean(locationState?.uploadId && !hasInitialUploadSnapshot)
  );
  const requestedPreset = locationState?.workspacePreset ?? queryParams.get('preset');
  const workspacePreset: WorkspacePreset = isWorkspacePreset(requestedPreset)
    ? requestedPreset
    : 'workspace';
  const presetConfig = WORKSPACE_PRESETS[workspacePreset];
  const fallbackPatientInfo = isNewProjectFlow
    ? {
        name: '',
        dob: '',
        gender: undefined,
        studyId: '',
        studyInstanceUid: undefined,
      }
    : DEFAULT_PATIENT;
  const routedDicomSeries = useMemo(
    () =>
      incomingAnalyzedSeries?.map((series, index) => ({
        ...mapApiSeriesToDisplaySeries(series, index),
        uploadId: locationState?.uploadId,
        niftiAsset: preparedNiftiAssets.find(
          asset => asset.series_instance_uid === series.series_instance_uid
        ),
        alignedNiftiAsset: preparedAlignedNiftiAssets.find(
          asset => asset.series_instance_uid === series.series_instance_uid
        ),
      })) ?? [],
    [
      incomingAnalyzedSeries,
      locationState?.uploadId,
      preparedAlignedNiftiAssets,
      preparedNiftiAssets,
    ]
  );
  const activeDicomSeries = isNewProjectFlow ? routedDicomSeries : MOCK_DICOM_SERIES;

  const patientInfo: PatientInfo = {
    name:
      currentPatient?.patient_name ??
      openedStudy?.patientName ??
      queryParams.get('patientName') ??
      fallbackPatientInfo.name,
    dob: formatDateDDMMYYYY(
      currentPatient?.birth_date ??
        openedStudy?.dob ??
        queryParams.get('dob') ??
        fallbackPatientInfo.dob,
      ''
    ),
    age: currentPatient
      ? calculatePatientAge(currentPatient.birth_date)
      : (openedStudy?.age ??
        calculatePatientAge(openedStudy?.dob ?? queryParams.get('dob') ?? fallbackPatientInfo.dob)),
    gender:
      currentPatient?.gender ??
      openedStudy?.gender ??
      queryParams.get('gender') ??
      fallbackPatientInfo.gender,
    studyId:
      openedStudy?.studyId ??
      openedStudy?.studyInstanceUid ??
      openedStudy?.id ??
      queryParams.get('studyId') ??
      fallbackPatientInfo.studyId,
    studyInstanceUid:
      openedStudy?.studyInstanceUid ??
      openedStudy?.studyId ??
      openedStudy?.id ??
      queryParams.get('studyInstanceUid') ??
      queryParams.get('studyId') ??
      fallbackPatientInfo.studyInstanceUid,
    patientId: openedStudy?.patientId,
    hasPhoto: currentPatient?.has_photo ?? Boolean(openedStudy?.hasPhoto),
    photoVersion: currentPatient?.updated_at,
  };
  const initialFixedSeriesKey = isNewProjectFlow
    ? resolveInitialFixedSeriesKey(
        incomingAnalyzedSeries,
        locationState?.fixedStudyInstanceUid,
        locationState?.fixedSeriesInstanceUid
      )
    : DEFAULT_ROLE_SERIES.fixed;

  const initialProjectRole: DicomUploadRole =
    isNewProjectFlow &&
    routedDicomSeries.find(series => series.id === initialFixedSeriesKey)?.type === 'CT'
      ? 'ct'
      : 'fixed';

  const normalizedPatientGender = patientInfo.gender?.trim().toLowerCase() ?? '';
  const patientGenderIcon = normalizedPatientGender.startsWith('f')
    ? femaleIcon
    : normalizedPatientGender.startsWith('m')
      ? maleIcon
      : null;
  const patientGenderLabel = normalizedPatientGender.startsWith('f')
    ? 'Female'
    : normalizedPatientGender.startsWith('m')
      ? 'Male'
      : '';

  const [activeTool, setActiveTool] = useState<ToolMode>(initialProjectRole);
  const [isPatientInfoOpen, setIsPatientInfoOpen] = useState(false);
  const [openToolDropdown, setOpenToolDropdown] = useState<ToolMode | null>(null);
  // Bumped on every click inside a viewer's embedded MR viewport (a
  // same-origin iframe, so it never fires the outer document's pointerdown
  // listeners) so ViewerStrip role menus close on that click too, not just
  // on clicks elsewhere on the page.
  const [viewerInteractionSignal, setViewerInteractionSignal] = useState(0);
  const [scanToAlignRoles, setScanToAlignRoles] = useState<ScanToAlignRole[]>([]);
  const [pendingScanToAlignRole, setPendingScanToAlignRole] = useState<ScanToAlignRole | null>(
    null
  );
  const [ctRoles, setCtRoles] = useState<CtRole[]>(['ct']);
  const [toolUploads, setToolUploads] =
    useState<Record<ToolMode, ToolUploadMeta | null>>(createEmptyToolUploads);
  const [roleDicomUploads, setRoleDicomUploads] = useState<
    Record<DicomUploadRole, RoleDicomUploadState>
  >(createEmptyRoleDicomUploads);
  const initialSelectedSeriesByRole = createInitialSelectedSeriesByRole(
    isNewProjectFlow,
    initialFixedSeriesKey,
    initialProjectRole
  );

  const [selectedSeriesByRole, setSelectedSeriesByRole] = useState<
    Record<ViewerRole, string | null>
  >(initialSelectedSeriesByRole);
  const [confirmedCtRoles, setConfirmedCtRoles] = useState<Set<CtRole>>(
    () => new Set(initialProjectRole === 'ct' ? ['ct'] : [])
  );
  const [fixedViewerLayout, setFixedViewerLayout] = useState<FixedViewerLayoutId>('singleAxial');
  const [activeFixedViewerLayout, setActiveFixedViewerLayout] =
    useState<FixedViewerLayoutId>('singleAxial');
  const [isParcelization3DView, setIsParcelization3DView] = useState(false);
  const [fixedViewerOrientationRequestVersion, setFixedViewerOrientationRequestVersion] =
    useState(0);
  const [fixedViewerPanResetRequest, setFixedViewerPanResetRequest] =
    useState<ProjectViewerPanResetRequest | null>(null);

  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [liveSeriesThumbnailByKey, setLiveSeriesThumbnailByKey] = useState<Record<string, string>>(
    {}
  );
  const [viewerReadySeriesKeys, setViewerReadySeriesKeys] = useState<Set<string>>(() => new Set());
  const [isSeriesOpen, setIsSeriesOpen] = useState(presetConfig.seriesOpen);
  const [isParcelizationActive, setIsParcelizationActive] = useState(false);
  const [isParcelizationOpen, setIsParcelizationOpen] = useState(false);
  const [parcelizationOwnerSlot, setParcelizationOwnerSlot] = useState<ViewerSlot | null>(null);
  const [orientationPopoverSlot, setOrientationPopoverSlot] = useState<ViewerSlot | null>(null);
  const [isCenterRegistrationOpen, setIsCenterRegistrationOpen] = useState(false);
  // Registration owns its fixed source independently from the center strip.
  // Keep the resolved series as well as its role so changing the Move source
  // cannot make the fixed layer disappear while the overlay is re-rendering.
  const [registrationFixedSeries, setRegistrationFixedSeries] = useState<DicomSeries | null>(null);
  const [isParcelizationPanelOpen, setIsParcelizationPanelOpen] = useState(false);
  const [selectedParcellizations, setSelectedParcellizations] = useState<Set<string>>(
    () => new Set()
  );
  const [parcelizationSearch, setParcelizationSearch] = useState('');
  const [parcelizationCatalog, setParcelizationCatalog] = useState<ParcelizationCatalogItem[]>([]);
  const [parcelizationCatalogStatus, setParcelizationCatalogStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [atlasCatalog, setAtlasCatalog] = useState<AtlasCatalogItem[]>([]);
  const [atlasCatalogReloadVersion, setAtlasCatalogReloadVersion] = useState(0);
  const [atlasCatalogStatus, setAtlasCatalogStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [atlasCatalogError, setAtlasCatalogError] = useState<string | null>(null);
  const [atlasThumbnailById, setAtlasThumbnailById] = useState<Record<string, string>>({});
  const [selectedAtlases, setSelectedAtlases] = useState<
    Partial<Record<AtlasRole, AtlasCatalogItem>>
  >({});
  const defaultAtlasInitializedRef = useRef(false);

  const toolUploadRef = useRef<HTMLDivElement | null>(null);
  const parcelizationControlRef = useRef<HTMLDivElement | null>(null);
  const patientInfoMenuRef = useRef<HTMLDivElement | null>(null);
  const viewersLayoutRef = useRef<HTMLDivElement | null>(null);
  const resizeCleanupRef = useRef<(() => void) | null>(null);
  const roleUploadAbortControllersRef = useRef<Partial<Record<DicomUploadRole, AbortController>>>(
    {}
  );
  const selectedNiftiPreparationKeyRef = useRef<string | null>(null);
  const backgroundNiftiPreparationKeyRef = useRef<string | null>(null);
  const viewerEditNotAlignedWarningTimeoutsRef = useRef<
    Partial<Record<ViewerSlot, ReturnType<typeof setTimeout>>>
  >({});
  // Set once the user explicitly cancels out of the "Edit" workflow's
  // auto-started edit mode on the left slot, so the auto-start effect below
  // stops trying to put them back into it.
  const editWorkflowCancelledRef = useRef(false);

  const [panelWeights, setPanelWeights] = useState<Record<ResizablePanelId, number> | null>(null);
  const [activeResizeBoundary, setActiveResizeBoundary] = useState<number | null>(null);
  const [maximizedViewerSlot, setMaximizedViewerSlot] = useState<ViewerSlot | null>(null);
  const [isSeriesOpenWhileMaximized, setIsSeriesOpenWhileMaximized] = useState(false);

  const [visibleViewers, setVisibleViewers] = useState<Record<ViewerSlot, boolean>>({
    ...presetConfig.visibleViewers,
  });
  const [viewerEditModes, setViewerEditModes] = useState<Record<ViewerSlot, boolean>>({
    left: false,
    center: false,
    right: false,
  });
  const [viewerHasExplicitSelection, setViewerHasExplicitSelection] = useState<
    Record<ViewerSlot, boolean>
  >({
    left: false,
    center: false,
    right: false,
  });
  // Shown when the selected series has no aligned asset available yet.
  const [viewerEditNotAlignedWarning, setViewerEditNotAlignedWarning] = useState<
    Record<ViewerSlot, boolean>
  >({
    left: false,
    center: false,
    right: false,
  });
  const [viewerEditRotations, setViewerEditRotations] = useState<
    Record<ViewerSlot, NiftiRotationOperation[]>
  >({ left: [], center: [], right: [] });
  const [viewerEditSaveStatus, setViewerEditSaveStatus] = useState<
    Record<ViewerSlot, ViewerEditSaveStatus>
  >({ left: 'idle', center: 'idle', right: 'idle' });
  const [viewerEditErrors, setViewerEditErrors] = useState<Record<ViewerSlot, string | null>>({
    left: null,
    center: null,
    right: null,
  });
  const [viewerReloadVersions, setViewerReloadVersions] = useState<Record<ViewerSlot, number>>({
    left: 0,
    center: 0,
    right: 0,
  });

  const [viewerRoles, setViewerRoles] = useState<Record<ViewerSlot, ViewerRole>>({
    ...presetConfig.viewerRoles,
    ...(initialProjectRole === 'ct' ? { left: 'ct' } : {}),
  });
  const initialFixedAlignedAsset = routedDicomSeries.find(
    series => series.id === initialSelectedSeriesByRole[initialProjectRole]
  )?.alignedNiftiAsset;
  const [alignedSeriesByRole, setAlignedSeriesByRole] = useState<
    Partial<Record<DicomUploadRole, boolean>>
  >({});
  const [alignedNiftiAssetsByRole, setAlignedNiftiAssetsByRole] = useState<
    Partial<Record<DicomUploadRole, PreparedNiftiAsset>>
  >(initialFixedAlignedAsset ? { [initialProjectRole]: initialFixedAlignedAsset } : {});
  // Keep the first oriented output for each source. This is the immutable
  // reset target for the Orientation workflow, even after a later manual save
  // replaces the currently displayed aligned asset.
  const initialOrientedAssetsByRoleRef = useRef<
    Partial<Record<DicomUploadRole, PreparedNiftiAsset>>
  >(initialFixedAlignedAsset ? { [initialProjectRole]: initialFixedAlignedAsset } : {});
  const orientationEditSessionAssetsRef = useRef<
    Partial<Record<ViewerSlot, { role: DicomUploadRole; asset: PreparedNiftiAsset }>>
  >({});
  const [viewerRoleVariants, setViewerRoleVariants] = useState<
    Record<ViewerSlot, SeriesDisplayVariant>
  >({
    left: 'original',
    center: 'original',
    right: 'original',
  });
  // Both source pickers describe the same aligned asset as the viewer header,
  // including edits not saved yet. Never write this transient state to the asset.
  const editedAlignedAssetIds = useMemo(() => {
    const ids = new Set<string>();
    (Object.keys(viewerRoles) as ViewerSlot[]).forEach(slot => {
      const role = viewerRoles[slot];
      if (
        !isAtlasRole(role) &&
        viewerRoleVariants[slot] === 'aligned' &&
        viewerEditModes[slot] &&
        viewerEditRotations[slot].length > 0
      ) {
        const asset = alignedNiftiAssetsByRole[role];
        if (asset) ids.add(asset.asset_id);
      }
    });
    return ids;
  }, [
    alignedNiftiAssetsByRole,
    viewerEditModes,
    viewerEditRotations,
    viewerRoles,
    viewerRoleVariants,
  ]);

  const [centerSecondaryRole, setCenterSecondaryRole] = useState<ViewerRole>(
    presetConfig.centerSecondaryRole
  );
  const [centerSecondaryVariant, setCenterSecondaryVariant] =
    useState<SeriesDisplayVariant>('original');
  const [registrationFixedRole, setRegistrationFixedRole] = useState<ViewerRole | null>(null);
  const [registrationFixedVariant, setRegistrationFixedVariant] =
    useState<SeriesDisplayVariant>('original');
  const [registrationMovingSeries, setRegistrationMovingSeries] = useState<DicomSeries | null>(
    null
  );
  const [registrationMovingRole, setRegistrationMovingRole] = useState<ViewerRole | null>(null);
  const [registrationMovingVariant, setRegistrationMovingVariant] =
    useState<SeriesDisplayVariant>('original');
  const [registrationMovingOptionKey, setRegistrationMovingOptionKey] = useState<string | null>(
    null
  );
  const [registrationMovingOptions, setRegistrationMovingOptions] = useState<ViewerRoleOption[]>(
    []
  );
  const [isCenterRegistrationReady, setIsCenterRegistrationReady] = useState(false);
  const [isCenterRegistrationAlignmentOn, setIsCenterRegistrationAlignmentOn] = useState(false);
  const [centerTertiaryRole, setCenterTertiaryRole] = useState<ViewerRole>(
    presetConfig.centerTertiaryRole
  );
  const [centerTertiaryVariant, setCenterTertiaryVariant] =
    useState<SeriesDisplayVariant>('original');
  const [leftViewerGrid, setLeftViewerGrid] = useState<LeftViewerGrid>(presetConfig.leftViewerGrid);

  useEffect(() => {
    if (!isCenterRegistrationReady) {
      setIsCenterRegistrationAlignmentOn(false);
    }
  }, [isCenterRegistrationReady]);

  useEffect(() => {
    (Object.keys(alignedNiftiAssetsByRole) as DicomUploadRole[]).forEach(role => {
      const asset = alignedNiftiAssetsByRole[role];
      if (!asset) return;
      const initialAsset = initialOrientedAssetsByRoleRef.current[role];
      const assetSource =
        asset.source_asset_id ??
        asset.reference_asset_id ??
        asset.series_instance_uid ??
        asset.asset_id;
      const initialSource =
        initialAsset?.source_asset_id ??
        initialAsset?.reference_asset_id ??
        initialAsset?.series_instance_uid ??
        initialAsset?.asset_id;
      // A different original source means a new orientation session. Keep its
      // first available oriented output as the new reset baseline.
      if (!initialAsset || assetSource !== initialSource) {
        initialOrientedAssetsByRoleRef.current[role] = asset;
      }
    });
  }, [alignedNiftiAssetsByRole]);

  const renderedViewerSlots = presetConfig.renderedViewerSlots;
  const isWorkspacePopupOpen = openToolDropdown !== null || isParcelizationOpen;
  const openPanelCount =
    Number(isSeriesOpen) +
    Number(isParcelizationPanelOpen) +
    renderedViewerSlots.reduce((count, slot) => count + Number(visibleViewers[slot]), 0);
  const isSeriesPresentedOpen = maximizedViewerSlot ? isSeriesOpenWhileMaximized : isSeriesOpen;
  const atlasSeriesByRole = useMemo(
    () =>
      Object.fromEntries(
        ATLAS_ROLES.filter(role => selectedAtlases[role]).map(role => [
          role,
          mapAtlasToViewerSeries(
            atlasCatalog.find(atlas => atlas.id === selectedAtlases[role].id) ??
              selectedAtlases[role]
          ),
        ])
      ) as Partial<Record<AtlasRole, DicomSeries>>,
    [atlasCatalog, selectedAtlases]
  );
  const getToolbarRoleLabel = useCallback(
    (role: DicomUploadRole) => {
      const roleUpload = roleDicomUploads[role];
      const seriesOptions = roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
      const selectedSeries = seriesOptions.find(series => series.id === selectedSeriesByRole[role]);
      const classification = selectedSeries?.modality;

      if (classification && classification !== 'Unknown' && isCtRole(role)) {
        return getViewerRoleLabel(role);
      }

      return classification && classification !== 'Unknown'
        ? classification
        : getViewerRoleLabel(role);
    },
    [activeDicomSeries, roleDicomUploads, selectedSeriesByRole]
  );
  const getViewerRoleOptionLabel = useCallback(
    (role: ViewerRole) => {
      if (isAtlasRole(role)) {
        return atlasSeriesByRole[role]?.label ?? getViewerRoleLabel(role);
      }

      return getToolbarRoleLabel(role);
    },
    [getToolbarRoleLabel, atlasSeriesByRole]
  );
  const dicomRoleToolButtons = (['fixed', ...scanToAlignRoles] as DicomViewerRole[]).map(role => ({
    id: role,
    label:
      role === pendingScanToAlignRole || !selectedSeriesByRole[role]
        ? '+ MR'
        : getToolbarRoleLabel(role),
  }));
  const viewerRoleOptions = useMemo(
    () =>
      [
        { id: 'fixed' as const, label: getViewerRoleOptionLabel('fixed') },
        ...scanToAlignRoles.map(role => ({ id: role, label: getViewerRoleOptionLabel(role) })),
        ...ATLAS_ROLES.map(role => ({ id: role, label: getViewerRoleOptionLabel(role) })),
        ...ctRoles.map(role => ({ id: role, label: getViewerRoleOptionLabel(role) })),
      ]
        .filter(option => {
          if (option.id === pendingScanToAlignRole) {
            return false;
          }

          if (isAtlasRole(option.id)) {
            return Boolean(atlasSeriesByRole[option.id]);
          }

          const roleUpload = roleDicomUploads[option.id];
          const availableSeries =
            roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
          const selectedSeriesId = selectedSeriesByRole[option.id];

          return Boolean(
            selectedSeriesId &&
              availableSeries.some(
                series =>
                  series.id === selectedSeriesId &&
                  isSeriesCompatibleWithRole(series, option.id as DicomUploadRole)
              )
          );
        })
        // The viewer's orientation/edit button owns the Original/Oriented
        // choice. Keep one source entry per selected MR or CT series and let
        // its feedback follow that button.
        .flatMap<ViewerRoleOption>(option => {
          if (isAtlasRole(option.id)) {
            return getSelectedAtlasChoices(
              atlasCatalog,
              selectedAtlases.atlas
            ).map<ViewerRoleOption>(atlas => ({
              id: 'atlas',
              label: getAtlasFamilyTitle(getAtlasFamily(atlas)),
              variant: 'original',
              atlasTag: atlas.contrast ?? undefined,
              key: `atlas:${atlas.id}`,
              sourceSeries: mapAtlasToViewerSeries(atlas),
              atlasId: atlas.id,
              isCurrentAtlas: atlas.id === selectedAtlases.atlas?.id,
              thumbnailSrc:
                atlasThumbnailById[atlas.id] ??
                (() => {
                  const atlasSeries = mapAtlasToViewerSeries(atlas);
                  const seriesKey = buildDicomSeriesKey(
                    atlasSeries.studyInstanceUid,
                    atlasSeries.seriesInstanceUid
                  );
                  return (
                    liveSeriesThumbnailByKey[buildOrientedThumbnailKey(seriesKey, 'axial')] ??
                    liveSeriesThumbnailByKey[seriesKey]
                  );
                })(),
            }));
          }
          const roleUpload = roleDicomUploads[option.id];
          const availableSeries =
            roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
          const selectedSeries = availableSeries.find(
            series => series.id === selectedSeriesByRole[option.id]
          );
          const seriesKey = selectedSeries
            ? buildDicomSeriesKey(selectedSeries.studyInstanceUid, selectedSeries.seriesInstanceUid)
            : null;
          const thumbnailSrc =
            getMiddleSeriesThumbnail(selectedSeries) ??
            (seriesKey
              ? (liveSeriesThumbnailByKey[buildOrientedThumbnailKey(seriesKey, 'axial')] ??
                liveSeriesThumbnailByKey[seriesKey])
              : undefined);
          const variant: SeriesDisplayVariant =
            !isAtlasRole(option.id) && alignedSeriesByRole[option.id] ? 'aligned' : 'original';
          const asset = !isAtlasRole(option.id) ? alignedNiftiAssetsByRole[option.id] : undefined;
          const sourceSeries =
            selectedSeries && variant === 'aligned' && asset
              ? { ...selectedSeries, niftiAsset: asset }
              : selectedSeries;
          return {
            ...option,
            variant,
            key: `${option.id}:${variant}`,
            sourceSeries,
            thumbnailSrc,
            thumbnailSeries: selectedSeries,
            alignmentMode:
              asset && editedAlignedAssetIds.has(asset.asset_id) ? 'manual' : asset?.alignment_mode,
          };
        }),
    [
      alignedSeriesByRole,
      alignedNiftiAssetsByRole,
      editedAlignedAssetIds,
      activeDicomSeries,
      atlasThumbnailById,
      ctRoles,
      getViewerRoleOptionLabel,
      liveSeriesThumbnailByKey,
      pendingScanToAlignRole,
      roleDicomUploads,
      scanToAlignRoles,
      selectedSeriesByRole,
      atlasSeriesByRole,
      atlasCatalog,
      selectedAtlases,
    ]
  );

  useEffect(() => {
    if (!accessToken || (!isNewProjectFlow && !isAtlasRole(openToolDropdown))) {
      return;
    }

    setAtlasCatalogStatus('loading');
    setAtlasCatalogError(null);
    let cancelled = false;
    void getAtlasCatalog(accessToken)
      .then(atlases => {
        if (cancelled) return;
        setAtlasCatalog(atlases);
        setAtlasCatalogStatus('success');
      })
      .catch(error => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        setAtlasCatalogError(error instanceof Error ? error.message : 'Atlas catalog failed.');
        setAtlasCatalogStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, atlasCatalogReloadVersion, isNewProjectFlow, logout, openToolDropdown]);

  useEffect(() => {
    if (
      atlasCatalogStatus !== 'success' ||
      !isNewProjectFlow ||
      defaultAtlasInitializedRef.current ||
      selectedAtlases.atlas ||
      atlasCatalog.length === 0
    ) {
      return;
    }

    const mniAtlases = atlasCatalog.filter(atlas => getAtlasFamily(atlas) === 'MNI');
    const icbm2009Atlases = mniAtlases.filter(atlas =>
      /(?:ICBM|MNI).*2009/i.test(`${atlas.id} ${atlas.name}`)
    );
    const icbmTemplates = selectAtlasTemplates(
      icbm2009Atlases.length > 0 ? icbm2009Atlases : mniAtlases
    );
    const atlas =
      icbmTemplates.find(template => template.contrast?.toUpperCase().startsWith('T1W')) ??
      icbmTemplates[0];
    if (!atlas) return;

    defaultAtlasInitializedRef.current = true;
    setSelectedAtlases({ atlas });
    setSelectedSeriesByRole(current => ({ ...current, atlas: `atlas:${atlas.id}` }));
    setToolUploads(current => ({
      ...current,
      atlas: {
        name: getAtlasDisplayName(atlas),
        count: 1,
        size: atlas.template_size_bytes,
        source: 'files',
      },
    }));
  }, [atlasCatalog, atlasCatalogStatus, isNewProjectFlow, selectedAtlases.atlas]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const selectedAtlas = selectedAtlases.atlas;
    const atlases = selectedAtlas
      ? getSelectedAtlasChoices(atlasCatalog, selectedAtlas).filter(
          (atlas, index, choices) => choices.findIndex(choice => choice.id === atlas.id) === index
        )
      : [];
    if (atlases.length === 0) {
      setAtlasThumbnailById(current => (Object.keys(current).length === 0 ? current : {}));
      return;
    }

    const controller = new AbortController();
    let active = true;
    const objectUrls: string[] = [];

    void Promise.all(
      atlases.map(atlas =>
        getAtlasThumbnail(atlas, accessToken, controller.signal)
          .then(blob => {
            if (!active) return null;
            const url = URL.createObjectURL(blob);
            objectUrls.push(url);
            return [atlas.id, url] as const;
          })
          .catch(() => null)
      )
    ).then(entries => {
      if (!active) return;
      setAtlasThumbnailById(
        entries.reduce<Record<string, string>>((result, entry) => {
          if (entry) result[entry[0]] = entry[1];
          return result;
        }, {})
      );
    });

    return () => {
      active = false;
      controller.abort();
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [accessToken, atlasCatalog, selectedAtlases]);

  useEffect(() => {
    if (!isParcelizationOpen || !accessToken || parcelizationCatalogStatus !== 'idle') {
      return;
    }
    let cancelled = false;
    setParcelizationCatalogStatus('loading');
    void getParcelizationCatalog(accessToken)
      .then(items => {
        if (cancelled) return;
        setParcelizationCatalog(items);
        setParcelizationCatalogStatus('success');
      })
      .catch(error => {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        setParcelizationCatalogStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken, isParcelizationOpen, logout]);

  useEffect(() => {
    if (!isPatientInfoOpen || isPatientInfoEditing) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsPatientInfoOpen(false);
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (!patientInfoMenuRef.current?.contains(event.target as Node)) {
        setIsPatientInfoOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isPatientInfoOpen, isPatientInfoEditing]);

  useEffect(() => {
    const fallbackRole = viewerRoleOptions[0]?.id;
    if (!fallbackRole) {
      return;
    }

    const availableRoleIds = new Set(viewerRoleOptions.map(option => option.id));
    const resolveAvailableRole = (role: ViewerRole) => {
      if (
        availableRoleIds.has(role) ||
        (isDicomViewerRole(role) && roleDicomUploads[role].status === 'uploading')
      ) {
        return role;
      }

      return fallbackRole;
    };

    setViewerRoles(current => {
      let changed = false;
      const next = { ...current };

      (Object.keys(next) as ViewerSlot[]).forEach(slot => {
        const resolvedRole = resolveAvailableRole(next[slot]);
        if (resolvedRole !== next[slot]) {
          next[slot] = resolvedRole;
          changed = true;
        }
      });

      return changed ? next : current;
    });
    setCenterSecondaryRole(resolveAvailableRole);
    setCenterTertiaryRole(resolveAvailableRole);
  }, [roleDicomUploads, viewerRoleOptions]);
  const uploadedDicomSeries = (['fixed', ...SCAN_TO_ALIGN_ROLES] as DicomViewerRole[]).flatMap(
    role => roleDicomUploads[role].series
  );
  const displayedDicomSeries = [...activeDicomSeries, ...uploadedDicomSeries];

  const selectedSeriesIds = [
    ...renderedViewerSlots
      .filter(slot => visibleViewers[slot])
      .map(slot => selectedSeriesByRole[viewerRoles[slot]]),
    ...(renderedViewerSlots.includes('center') && visibleViewers.center
      ? [
          selectedSeriesByRole[centerSecondaryRole],
          ...(presetConfig.showCenterTertiaryRole
            ? [selectedSeriesByRole[centerTertiaryRole]]
            : []),
        ]
      : []),
  ].filter((seriesId): seriesId is string => Boolean(seriesId));
  const mergePreparedNiftiAssets = useCallback((assets: PreparedNiftiAsset[]) => {
    setPreparedNiftiAssets(current => {
      const bySeriesUid = new Map(
        current.map(asset => [asset.series_instance_uid, asset] as const)
      );
      assets.forEach(asset => bySeriesUid.set(asset.series_instance_uid, asset));
      return Array.from(bySeriesUid.values());
    });
  }, []);
  const mergePreparedAlignedNiftiAssets = useCallback((assets: PreparedNiftiAsset[]) => {
    setPreparedAlignedNiftiAssets(current => {
      const bySourceAsset = new Map(
        current.map(asset => [asset.source_asset_id ?? asset.asset_id, asset] as const)
      );
      assets.forEach(asset => bySourceAsset.set(asset.source_asset_id ?? asset.asset_id, asset));
      return Array.from(bySourceAsset.values());
    });
  }, []);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const targets: Array<{ uploadId: string; role?: DicomUploadRole }> = [];
    if (
      locationState?.uploadId &&
      (shouldHydrateInitialUpload || initialUploadAlignmentStatus !== 'ready')
    ) {
      targets.push({ uploadId: locationState.uploadId });
    }
    (Object.keys(roleDicomUploads) as DicomUploadRole[]).forEach(role => {
      const upload = roleDicomUploads[role];
      if (upload.uploadId && upload.alignmentStatus !== 'ready') {
        targets.push({ uploadId: upload.uploadId, role });
      }
    });
    if (!targets.length) {
      return;
    }

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async () => {
      let shouldPollAgain = false;
      let nextPollDelay = 15000;
      for (const target of targets) {
        try {
          const result = await getDicomUploadAnalysis(
            target.uploadId,
            accessToken,
            controller.signal
          );
          if (cancelled) return;

          const originalAssets = result.prepared_nifti_assets ?? [];
          const alignedAssets = result.aligned_nifti_assets ?? [];
          const alignmentStatus = result.alignment_status ?? 'ready';
          mergePreparedNiftiAssets(originalAssets);
          mergePreparedAlignedNiftiAssets(alignedAssets);

          if (!target.role) {
            setInitialUploadAlignmentStatus(alignmentStatus);
            setShouldHydrateInitialUpload(false);
          } else {
            setRoleDicomUploads(current => {
              const upload = current[target.role!];
              if (!upload || upload.uploadId !== target.uploadId) return current;
              const nextSeries = upload.series.map(series => {
                const originalAsset =
                  series.niftiAsset ??
                  originalAssets.find(
                    asset => asset.series_instance_uid === series.seriesInstanceUid
                  );
                const alignedAsset = alignedAssets.find(
                  asset =>
                    asset.source_asset_id === originalAsset?.asset_id ||
                    asset.series_instance_uid === series.seriesInstanceUid
                );
                return alignedAsset &&
                  (alignedAsset.asset_id !== series.alignedNiftiAsset?.asset_id ||
                    alignedAsset.sha256 !== series.alignedNiftiAsset?.sha256 ||
                    alignedAsset.alignment_mode !== series.alignedNiftiAsset?.alignment_mode)
                  ? { ...series, niftiAsset: originalAsset, alignedNiftiAsset: alignedAsset }
                  : series;
              });
              const seriesChanged = nextSeries.some(
                (series, index) => series !== upload.series[index]
              );
              if (!seriesChanged && upload.alignmentStatus === alignmentStatus) return current;
              return {
                ...current,
                [target.role!]: {
                  ...upload,
                  series: nextSeries,
                  alignmentStatus,
                },
              };
            });
          }
          shouldPollAgain ||= alignmentStatus !== 'ready';
          if (alignmentStatus === 'pending' || alignmentStatus === 'running') {
            nextPollDelay = 3000;
          }
        } catch (error) {
          if (isAbortError(error)) return;
          if (error instanceof ApiError && error.status === 401) {
            logout();
            return;
          }
          shouldPollAgain = true;
        }
      }

      if (!cancelled && shouldPollAgain) {
        timer = setTimeout(poll, nextPollDelay);
      }
    };

    timer = setTimeout(poll, 1000);
    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [
    accessToken,
    initialUploadAlignmentStatus,
    locationState?.uploadId,
    logout,
    mergePreparedAlignedNiftiAssets,
    mergePreparedNiftiAssets,
    roleDicomUploads,
    shouldHydrateInitialUpload,
  ]);

  useEffect(() => {
    const roles = ['fixed', ...scanToAlignRoles, ...ctRoles] as DicomUploadRole[];
    setAlignedNiftiAssetsByRole(current => {
      const next = { ...current };

      roles.forEach(role => {
        const selectedSeriesId = selectedSeriesByRole[role];
        if (!selectedSeriesId) {
          delete next[role];
          return;
        }

        const roleUpload = roleDicomUploads[role];
        const seriesOptions = roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
        const selectedSeries = seriesOptions.find(series => series.id === selectedSeriesId);

        if (!selectedSeries) {
          // The series list (e.g. activeDicomSeries, while its underlying
          // prepared-assets fetch is still in flight) hasn't caught up with
          // the current selection yet - leave any already-known aligned
          // asset alone rather than wiping it over a transient gap; a later
          // pass will pick up the real state once the list settles.
          return;
        }

        if (selectedSeries.alignedNiftiAsset) {
          next[role] = selectedSeries.alignedNiftiAsset;
        } else {
          delete next[role];
        }
      });

      const currentKeys = Object.keys(current) as DicomUploadRole[];
      const nextKeys = Object.keys(next) as DicomUploadRole[];
      const unchanged =
        currentKeys.length === nextKeys.length &&
        nextKeys.every(role => current[role] === next[role]);
      return unchanged ? current : next;
    });
  }, [activeDicomSeries, ctRoles, roleDicomUploads, scanToAlignRoles, selectedSeriesByRole]);

  const handleProjectViewerSliceThumbnail = useCallback(
    ({
      studyInstanceUid,
      seriesInstanceUid,
      orientation,
      thumbnailSrc,
    }: ProjectViewerSliceThumbnail) => {
      const seriesKey = buildDicomSeriesKey(studyInstanceUid, seriesInstanceUid);
      setViewerReadySeriesKeys(current => {
        if (current.has(seriesKey)) {
          return current;
        }
        const next = new Set(current);
        next.add(seriesKey);
        return next;
      });

      if (USE_STATIC_SERIES_THUMBNAILS) {
        return;
      }

      const orientedSeriesKey = buildOrientedThumbnailKey(seriesKey, orientation);

      setLiveSeriesThumbnailByKey(current => {
        if (current[seriesKey] === thumbnailSrc && current[orientedSeriesKey] === thumbnailSrc) {
          return current;
        }

        return {
          ...current,
          [seriesKey]: thumbnailSrc,
          [orientedSeriesKey]: thumbnailSrc,
        };
      });
    },
    []
  );

  useEffect(() => {
    const uploadId = locationState?.uploadId;
    const studyInstanceUid = locationState?.fixedStudyInstanceUid;
    const seriesInstanceUid = locationState?.fixedSeriesInstanceUid;
    if (
      !isNewProjectFlow ||
      !accessToken ||
      !uploadId ||
      !studyInstanceUid ||
      !seriesInstanceUid ||
      !selectedSeriesByRole[initialProjectRole] ||
      preparedNiftiAssets.some(asset => asset.series_instance_uid === seriesInstanceUid)
    ) {
      return;
    }

    const preparationKey = `${uploadId}:${studyInstanceUid}:${seriesInstanceUid}`;
    if (selectedNiftiPreparationKeyRef.current === preparationKey) {
      return;
    }
    selectedNiftiPreparationKeyRef.current = preparationKey;

    void prepareSelectedSeriesData(uploadId, studyInstanceUid, seriesInstanceUid, accessToken)
      .then(response => {
        mergePreparedNiftiAssets(response.assets);
        mergePreparedAlignedNiftiAssets(response.aligned_assets);
      })
      .catch(error => {
        selectedNiftiPreparationKeyRef.current = null;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        console.error('Selected NIfTI preparation failed.', error);
      });
  }, [
    accessToken,
    isNewProjectFlow,
    locationState?.fixedSeriesInstanceUid,
    locationState?.fixedStudyInstanceUid,
    locationState?.uploadId,
    logout,
    mergePreparedNiftiAssets,
    mergePreparedAlignedNiftiAssets,
    preparedNiftiAssets,
    selectedSeriesByRole,
    initialProjectRole,
  ]);

  useEffect(() => {
    const uploadId = locationState?.uploadId;
    const studyInstanceUid = locationState?.fixedStudyInstanceUid;
    const seriesInstanceUid = locationState?.fixedSeriesInstanceUid;
    const seriesKey =
      studyInstanceUid && seriesInstanceUid
        ? buildDicomSeriesKey(studyInstanceUid, seriesInstanceUid)
        : null;
    if (
      !isNewProjectFlow ||
      !accessToken ||
      !uploadId ||
      !studyInstanceUid ||
      !seriesKey ||
      !viewerReadySeriesKeys.has(seriesKey)
    ) {
      return;
    }

    const preparationKey = `${uploadId}:${studyInstanceUid}`;
    if (backgroundNiftiPreparationKeyRef.current === preparationKey) {
      return;
    }
    backgroundNiftiPreparationKeyRef.current = preparationKey;

    void prepareRemainingStudyData(uploadId, studyInstanceUid, accessToken)
      .then(async response => {
        mergePreparedNiftiAssets(response.assets);
        mergePreparedAlignedNiftiAssets(response.aligned_assets);
        await startRemainingUploadAlignment(uploadId, accessToken);
      })
      .catch(error => {
        backgroundNiftiPreparationKeyRef.current = null;
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        console.warn('Background NIfTI preparation failed.', error);
      });
  }, [
    accessToken,
    isNewProjectFlow,
    locationState?.fixedSeriesInstanceUid,
    locationState?.fixedStudyInstanceUid,
    locationState?.uploadId,
    logout,
    mergePreparedNiftiAssets,
    mergePreparedAlignedNiftiAssets,
    viewerReadySeriesKeys,
  ]);

  useEffect(() => {
    const nextPresetConfig = WORKSPACE_PRESETS[workspacePreset];
    const availableRoleIds = new Set(viewerRoleOptions.map(option => option.id));
    const fallbackRole = viewerRoleOptions[0]?.id;
    const resolvePresetRole = (role: ViewerRole) =>
      availableRoleIds.has(role) ? role : (fallbackRole ?? role);

    setIsSeriesOpen(nextPresetConfig.seriesOpen);
    setVisibleViewers({ ...nextPresetConfig.visibleViewers });
    setLeftViewerGrid(nextPresetConfig.leftViewerGrid);
    editWorkflowCancelledRef.current = false;
    setViewerRoles(current => ({
      left: resolvePresetRole(
        workspacePreset === 'edit' ? current.left : nextPresetConfig.viewerRoles.left
      ),
      center: resolvePresetRole(nextPresetConfig.viewerRoles.center),
      right: resolvePresetRole(nextPresetConfig.viewerRoles.right),
    }));
    setCenterSecondaryRole(resolvePresetRole(nextPresetConfig.centerSecondaryRole));
    setCenterTertiaryRole(resolvePresetRole(nextPresetConfig.centerTertiaryRole));
    setIsCenterRegistrationOpen(false);
    setRegistrationFixedRole(null);
    setRegistrationFixedSeries(null);
    setIsParcelizationPanelOpen(false);
    setPanelWeights(null);
    setMaximizedViewerSlot(null);
    setIsSeriesOpenWhileMaximized(false);
    // Preset identity is the only reset trigger. Source availability is handled
    // by the dedicated reconciliation effect above without resetting the layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspacePreset]);

  useEffect(() => {
    if (!maximizedViewerSlot) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaximizedViewerSlot(null);
        setIsSeriesOpenWhileMaximized(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [maximizedViewerSlot]);

  useEffect(() => {
    if (!openToolDropdown) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (!toolUploadRef.current) return;

      const openPopover =
        toolUploadRef.current.querySelector<HTMLElement>('.pc-role-dicom-popover');
      const openDropdownItem = openPopover?.closest<HTMLElement>('.pc-tool-upload-item');

      if (!openDropdownItem?.contains(event.target as Node)) {
        setOpenToolDropdown(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [openToolDropdown]);

  useEffect(() => {
    if (!isParcelizationOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!parcelizationControlRef.current?.contains(event.target as Node)) {
        setIsParcelizationOpen(false);
        setParcelizationOwnerSlot(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isParcelizationOpen]);

  useEffect(() => {
    return () => {
      resizeCleanupRef.current?.();
      Object.values(roleUploadAbortControllersRef.current).forEach(controller =>
        controller?.abort()
      );
    };
  }, []);

  const isResizablePanelOpen = (panelId: ResizablePanelId) => {
    if (panelId === 'series') return isSeriesOpen;
    if (panelId === 'parcelization') return isParcelizationPanelOpen;

    return renderedViewerSlots.includes(panelId) && visibleViewers[panelId];
  };

  const getNearestOpenPanelsForBoundary = (boundaryIndex: number) => {
    const leftPanel = [...RESIZABLE_PANEL_ORDER]
      .slice(0, boundaryIndex + 1)
      .reverse()
      .find(panelId => isResizablePanelOpen(panelId));

    const rightPanel = RESIZABLE_PANEL_ORDER.slice(boundaryIndex + 1).find(panelId =>
      isResizablePanelOpen(panelId)
    );

    return { leftPanel, rightPanel };
  };

  const canResizeBoundary = (boundaryIndex: number) => {
    if (isWorkspacePopupOpen) return false;

    const { leftPanel, rightPanel } = getNearestOpenPanelsForBoundary(boundaryIndex);

    if (!leftPanel || !rightPanel) return false;
    // The series strip never resizes - it only collapses/expands - so it can
    // never be a drag target on either side of a boundary.
    if (leftPanel === 'series' || rightPanel === 'series') return false;

    return RESIZABLE_PANEL_ORDER.indexOf(leftPanel) === boundaryIndex;
  };

  const getPanelResizeStyle = (
    panelId: ResizablePanelId,
    isOpen: boolean
  ): React.CSSProperties | undefined => {
    // The series strip keeps its fixed CSS width regardless of any weights
    // captured while resizing the viewer panels - it's collapse/expand only.
    if (panelId === 'series' || !isOpen || !panelWeights) return undefined;

    return {
      '--pc-panel-weight': Math.max(panelWeights[panelId], 0.01),
    } as React.CSSProperties;
  };

  const resetPanelSizes = () => {
    setPanelWeights(null);
  };

  const startPanelResize = (event: React.PointerEvent<HTMLDivElement>, boundaryIndex: number) => {
    if (isWorkspacePopupOpen || event.button !== 0 || !canResizeBoundary(boundaryIndex)) return;

    const layoutElement = viewersLayoutRef.current;
    if (!layoutElement) return;

    const { leftPanel, rightPanel } = getNearestOpenPanelsForBoundary(boundaryIndex);

    if (!leftPanel || !rightPanel) return;

    const leftElement = layoutElement.querySelector<HTMLElement>(
      `[data-resizable-panel="${leftPanel}"]`
    );
    const rightElement = layoutElement.querySelector<HTMLElement>(
      `[data-resizable-panel="${rightPanel}"]`
    );

    if (!leftElement || !rightElement) return;

    event.preventDefault();
    event.stopPropagation();

    resizeCleanupRef.current?.();

    const flexDirection = window.getComputedStyle(layoutElement).flexDirection;
    const isVertical = flexDirection.startsWith('column');
    const startCoordinate = isVertical ? event.clientY : event.clientX;
    const leftRect = leftElement.getBoundingClientRect();
    const rightRect = rightElement.getBoundingClientRect();
    const startLeftSize = isVertical ? leftRect.height : leftRect.width;
    const startRightSize = isVertical ? rightRect.height : rightRect.width;
    const pairSize = startLeftSize + startRightSize;

    if (pairSize <= 0) return;

    const openPanelElements = RESIZABLE_PANEL_ORDER.filter(panelId => isResizablePanelOpen(panelId))
      .map(panelId => {
        const element = layoutElement.querySelector<HTMLElement>(
          `[data-resizable-panel="${panelId}"]`
        );

        if (!element) return null;

        const rect = element.getBoundingClientRect();
        return {
          panelId,
          size: isVertical ? rect.height : rect.width,
        };
      })
      .filter(
        (item): item is { panelId: ResizablePanelId; size: number } =>
          item !== null && item.size > 0
      );

    const measuredTotal = openPanelElements.reduce((total, item) => total + item.size, 0);
    const layoutSize = measuredTotal > 0 ? measuredTotal : pairSize;

    const initialWeights: Record<ResizablePanelId, number> = panelWeights
      ? { ...panelWeights }
      : { series: 1, left: 1, center: 1, parcelization: 1, right: 1 };

    if (!panelWeights && measuredTotal > 0) {
      openPanelElements.forEach(({ panelId, size }) => {
        initialWeights[panelId] = size / measuredTotal;
      });
    }

    const pairWeight = initialWeights[leftPanel] + initialWeights[rightPanel];
    const leftLimits = getPanelResizeLimits(leftPanel, isVertical, layoutSize);
    const rightLimits = getPanelResizeLimits(rightPanel, isVertical, layoutSize);
    const { minimumAllowedLeft, maximumAllowedLeft } = resolveResizeRange(
      leftLimits,
      rightLimits,
      pairSize
    );

    let animationFrameId: number | null = null;
    let latestCoordinate = startCoordinate;

    const applyResize = () => {
      animationFrameId = null;

      const delta = latestCoordinate - startCoordinate;
      const nextLeftSize = Math.min(
        maximumAllowedLeft,
        Math.max(minimumAllowedLeft, startLeftSize + delta)
      );
      const safeNextLeftSize = Number.isFinite(nextLeftSize)
        ? nextLeftSize
        : startClamp(startLeftSize, minimumAllowedLeft, maximumAllowedLeft);
      const leftRatio = safeNextLeftSize / pairSize;
      const nextLeftWeight = pairWeight * leftRatio;
      const nextRightWeight = pairWeight - nextLeftWeight;

      setPanelWeights({
        ...initialWeights,
        [leftPanel]: Number.isFinite(nextLeftWeight)
          ? Math.max(nextLeftWeight, 0.01)
          : initialWeights[leftPanel],
        [rightPanel]: Number.isFinite(nextRightWeight)
          ? Math.max(nextRightWeight, 0.01)
          : initialWeights[rightPanel],
      });
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      latestCoordinate = isVertical ? moveEvent.clientY : moveEvent.clientX;

      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(applyResize);
      }
    };

    const cleanup = () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', cleanup);
      document.removeEventListener('pointercancel', cleanup);
      document.body.classList.remove('pc-panel-resize-active');
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
      if (layoutElement.isConnected) {
        setActiveResizeBoundary(null);
      }
      resizeCleanupRef.current = null;
    };

    resizeCleanupRef.current = cleanup;
    setActiveResizeBoundary(boundaryIndex);
    document.body.classList.add('pc-panel-resize-active');
    document.body.style.cursor = isVertical ? 'row-resize' : 'col-resize';
    document.body.style.userSelect = 'none';

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', cleanup);
    document.addEventListener('pointercancel', cleanup);
  };

  useEffect(() => {
    if (isWorkspacePopupOpen) {
      resizeCleanupRef.current?.();
    }
  }, [isWorkspacePopupOpen]);

  const getSeriesOptionsForRole = (role: DicomUploadRole) => {
    const roleUpload = roleDicomUploads[role];
    // Idle roles (no dedicated upload of their own yet) browse the series
    // already uploaded for this project - same as the "+ MR" roles - so
    // "+ CT" isn't stuck showing an empty picker until a fresh CT file is
    // uploaded through it.
    const series = roleUpload.status === 'idle' ? activeDicomSeries : roleUpload.series;
    // Each picker owns its modality, including mixed-study project uploads.
    return series.filter(item => isSeriesCompatibleWithRole(item, role));
  };

  const getDisplaySeriesOptionsForRole = (role: DicomUploadRole) => {
    const roleUpload = roleDicomUploads[role];
    // MR shows CT for context, but only compatible series can be selected.
    // Keep the +CT list and the confirmation paths modality-restricted.
    const seriesOptions = isCtRole(role)
      ? getSeriesOptionsForRole(role)
      : roleUpload.status === 'idle'
        ? activeDicomSeries
        : roleUpload.series;
    return seriesOptions.map(series => ({
      ...series,
      eligibleForFixed: series.eligibleForFixed && isSeriesCompatibleWithRole(series, role),
      hasUnsavedAlignment: Boolean(
        series.alignedNiftiAsset && editedAlignedAssetIds.has(series.alignedNiftiAsset.asset_id)
      ),
    }));
  };

  const getRoleUploadedFile = (role: DicomUploadRole): ToolUploadMeta | null => {
    if (toolUploads[role]) {
      return toolUploads[role];
    }

    if (!isNewProjectFlow) {
      return null;
    }

    if (roleDicomUploads[role].status !== 'idle') {
      return null;
    }

    // Until a role receives its own upload, expose the study used to create
    // the project in both +MR and +CT. Removing this card dismisses that
    // inherited source for this picker and reveals its upload drop area.
    const selectedSeries =
      activeDicomSeries.find(series => series.id === selectedSeriesByRole[role]) ??
      activeDicomSeries.find(series => isSeriesCompatibleWithRole(series, role)) ??
      activeDicomSeries[0];
    if (!selectedSeries) return null;
    const studySeries = activeDicomSeries.filter(
      series => series.studyInstanceUid === selectedSeries.studyInstanceUid
    );

    return {
      name: selectedSeries.studyDescription || `${getToolbarRoleLabel(role)} DICOM study`,
      count: Math.max(
        1,
        studySeries.reduce((total, series) => total + series.slices, 0)
      ),
      size: 0,
      source: 'folder',
    };
  };

  const getToolAssetStatus = (toolId: ToolMode): ViewerAssetStatus => {
    const selectedDicomSeriesId = isDicomUploadRole(toolId) ? selectedSeriesByRole[toolId] : null;
    const isLoaded = isDicomUploadRole(toolId)
      ? Boolean(
          selectedDicomSeriesId &&
            getSeriesOptionsForRole(toolId).some(series => series.id === selectedDicomSeriesId)
        )
      : Boolean(toolUploads[toolId]);

    return getViewerAssetStatus(isLoaded, false);
  };

  const getSeriesByViewerRole = (role: ViewerRole, variant: SeriesDisplayVariant = 'original') => {
    if (isAtlasRole(role)) {
      return atlasSeriesByRole[role] ?? null;
    }
    const originalSeries = resolveViewerRoleSeries(
      role,
      selectedSeriesByRole,
      activeDicomSeries,
      roleDicomUploads,
      preparedNiftiAssets
    );
    const roleUpload = !isAtlasRole(role) ? roleDicomUploads[role] : null;
    const resolvedUploadId =
      originalSeries?.uploadId ?? roleUpload?.uploadId ?? locationState?.uploadId;
    const hydratedSeries =
      originalSeries && resolvedUploadId && !originalSeries.uploadId
        ? { ...originalSeries, uploadId: resolvedUploadId }
        : originalSeries;
    const alignedAsset = alignedNiftiAssetsByRole[role];
    return hydratedSeries && variant === 'aligned' && alignedAsset
      ? { ...hydratedSeries, niftiAsset: alignedAsset }
      : hydratedSeries;
  };

  const getViewerRoleBySeriesId = (seriesId: string): DicomUploadRole | null => {
    const foundRole = viewerRoleOptions.find(
      (role): role is ViewerRoleOption & { id: DicomUploadRole } =>
        !isAtlasRole(role.id) && selectedSeriesByRole[role.id] === seriesId
    );

    return foundRole?.id ?? null;
  };

  const toggleActive = (id: string) => {
    const role = getViewerRoleBySeriesId(id);

    if (role) {
      setSelectedSeriesByRole(prev => ({
        ...prev,
        [role]: id,
      }));
      return;
    }

    const visibleSlots = renderedViewerSlots.filter(slot => visibleViewers[slot]);
    const targetSlot =
      visibleSlots.find(slot => !isAtlasRole(viewerRoles[slot])) ?? visibleSlots[0] ?? 'left';
    const selectedTargetRole = viewerRoles[targetSlot];
    const targetRole: DicomUploadRole = isAtlasRole(selectedTargetRole)
      ? 'fixed'
      : selectedTargetRole;

    const targetSeries = getSeriesOptionsForRole(targetRole).find(series => series.id === id);
    if (!targetSeries || !isSeriesCompatibleWithRole(targetSeries, targetRole)) return;

    setSelectedSeriesByRole(prev => ({
      ...prev,
      [targetRole]: id,
    }));
  };

  const toggleViewerSlot = (slot: ViewerSlot) => {
    if (maximizedViewerSlot === slot) {
      setMaximizedViewerSlot(null);
      setIsSeriesOpenWhileMaximized(false);
    }

    setVisibleViewers(prev => ({
      ...prev,
      [slot]: !prev[slot],
    }));
  };

  const toggleSeriesPanel = () => {
    if (maximizedViewerSlot) {
      setIsSeriesOpenWhileMaximized(current => !current);
      return;
    }

    setIsSeriesOpen(current => {
      const willOpen = !current;

      if (willOpen && renderedViewerSlots.every(slot => !visibleViewers[slot])) {
        setPanelWeights(null);
      }

      return willOpen;
    });
  };

  const toggleViewerMaximize = (slot: ViewerSlot) => {
    const isRestoringWorkspace = maximizedViewerSlot === slot;
    setMaximizedViewerSlot(isRestoringWorkspace ? null : slot);
    // Fullscreen is a workspace made of Series + the selected Viewer. Series
    // starts expanded and remains independently collapsible inside that shell.
    setIsSeriesOpenWhileMaximized(!isRestoringWorkspace);
  };

  const updateViewerRole = (
    slot: ViewerSlot,
    role: ViewerRole,
    variant: SeriesDisplayVariant = 'original',
    closeRegistration = true
  ) => {
    if (slot === 'center' && closeRegistration) {
      setIsCenterRegistrationOpen(false);
      setRegistrationFixedRole(null);
      setRegistrationFixedSeries(null);
    }
    if (parcelizationOwnerSlot === slot) {
      setIsParcelizationOpen(false);
      setParcelizationOwnerSlot(null);
    }
    if (orientationPopoverSlot === slot) {
      setOrientationPopoverSlot(null);
    }
    setViewerHasExplicitSelection(current => ({ ...current, [slot]: true }));
    setViewerRoles(prev => ({
      ...prev,
      [slot]: role,
    }));
    setViewerRoleVariants(prev => ({ ...prev, [slot]: variant }));
  };

  const updateRoleDisplayVariant = (role: DicomUploadRole, variant: SeriesDisplayVariant) => {
    setAlignedSeriesByRole(prev => ({ ...prev, [role]: variant === 'aligned' }));
    setViewerRoleVariants(prev => {
      const next = { ...prev };
      (Object.keys(next) as ViewerSlot[]).forEach(slot => {
        if (viewerRoles[slot] === role) next[slot] = variant;
      });
      return next;
    });
    if (centerSecondaryRole === role) setCenterSecondaryVariant(variant);
    if (centerTertiaryRole === role) setCenterTertiaryVariant(variant);
  };

  const selectFixedViewerLayout = (layout: FixedViewerLayoutId, parcelizationOnly = false) => {
    setFixedViewerLayout(layout);
    setActiveFixedViewerLayout(layout);
    setIsParcelization3DView(parcelizationOnly);
    setFixedViewerOrientationRequestVersion(version => version + 1);
    setVisibleViewers(prev => (prev.left ? prev : { ...prev, left: true }));
  };

  const handleProjectViewerActiveView = useCallback((view: ProjectViewerSelectedView) => {
    const layout =
      view === '3d'
        ? 'only3D'
        : (`single${view[0].toUpperCase()}${view.slice(1)}` as FixedViewerLayoutId);
    setActiveFixedViewerLayout(layout);
    // A regular 3D viewport and the parcelization-only viewport both report
    // the same `3d` active-view message. Do not use that ambiguous message
    // to clear an explicit parcelization selection (for example when the
    // user clicks outside its popover). Selecting the regular 3D thumbnail
    // already clears this state through selectFixedViewerLayout.
    if (view !== '3d') {
      setIsParcelization3DView(false);
    }
  }, []);

  const resetFixedViewerPan = (orientation: ProjectViewerResetTarget) => {
    setFixedViewerPanResetRequest(current => ({
      orientation,
      requestId: (current?.requestId ?? 0) + 1,
    }));
  };

  const selectLeftViewerGridLayout = (layout: LeftViewerGrid) => {
    setLeftViewerGrid(layout);

    // Every multi-viewport preset has Axial as its deterministic first and
    // active viewport. Keep the Series selection indicator aligned with it.
    if (layout !== 1) {
      setFixedViewerLayout('singleAxial');
      setActiveFixedViewerLayout('singleAxial');
      setIsParcelization3DView(false);
    }
  };

  const getViewerTitle = (slot: ViewerSlot) => {
    if (slot === 'left') return 'Left viewer';
    if (slot === 'center') return 'Center viewer';
    return 'Right viewer';
  };

  const selectedFixedSeries =
    getSeriesByViewerRole('fixed') ?? getSeriesByViewerRole('ct') ?? getSeriesByViewerRole('atlas');
  const selectedFixedSeriesKey = selectedFixedSeries
    ? buildDicomSeriesKey(
        selectedFixedSeries.studyInstanceUid,
        selectedFixedSeries.seriesInstanceUid
      )
    : null;
  const fixedLayoutThumbnailByOrientation = useMemo<
    Partial<Record<ProjectViewerOrientation, string>>
  >(() => {
    if (!selectedFixedSeriesKey) {
      return {};
    }

    return {
      axial: liveSeriesThumbnailByKey[buildOrientedThumbnailKey(selectedFixedSeriesKey, 'axial')],
      coronal:
        liveSeriesThumbnailByKey[buildOrientedThumbnailKey(selectedFixedSeriesKey, 'coronal')],
      sagittal:
        liveSeriesThumbnailByKey[buildOrientedThumbnailKey(selectedFixedSeriesKey, 'sagittal')],
    };
  }, [liveSeriesThumbnailByKey, selectedFixedSeriesKey]);
  const shouldShowFixedViewerLayoutSelector = Boolean(selectedFixedSeries);
  const isAtlasViewerActive = isAtlasRole(viewerRoles.left);
  const fixedViewerLayoutOptions = useMemo<readonly FixedViewerLayoutOption[]>(
    () =>
      isAtlasViewerActive
        ? [...FIXED_VIEWER_LAYOUT_OPTIONS, PARCELIZATION_LAYOUT_OPTION]
        : FIXED_VIEWER_LAYOUT_OPTIONS,
    [isAtlasViewerActive]
  );

  useEffect(() => {
    if (!isAtlasViewerActive && isParcelization3DView) {
      setIsParcelization3DView(false);
      if (fixedViewerLayout === 'only3D') {
        setFixedViewerLayout('singleAxial');
        setActiveFixedViewerLayout('singleAxial');
      }
    }
  }, [fixedViewerLayout, isAtlasViewerActive, isParcelization3DView]);

  const formatUploadSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const handleToolUpload = async (
    toolId: ToolMode,
    files: FileList | null,
    source: 'files' | 'folder'
  ) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const totalSize = fileArray.reduce((sum, file) => sum + file.size, 0);

    let uploadName = '';

    if (source === 'folder') {
      const firstFile = fileArray[0] as File & { webkitRelativePath?: string };
      const relativePath = firstFile.webkitRelativePath ?? '';
      uploadName = relativePath ? relativePath.split('/')[0] : `${fileArray.length} folder files`;
    } else {
      uploadName =
        fileArray.length === 1 ? fileArray[0].name : `${fileArray.length} files selected`;
    }

    setToolUploads(prev => ({
      ...prev,
      [toolId]: {
        name: uploadName,
        count: fileArray.length,
        size: totalSize,
        source,
      },
    }));

    setActiveTool(toolId);

    if (!isDicomUploadRole(toolId)) {
      return;
    }

    if (!accessToken) {
      setRoleDicomUploads(current => ({
        ...current,
        [toolId]: {
          ...current[toolId],
          status: 'error',
          error: 'Authentication expired.',
        },
      }));
      return;
    }

    const previousUpload = roleDicomUploads[toolId];
    roleUploadAbortControllersRef.current[toolId]?.abort();
    const controller = new AbortController();
    roleUploadAbortControllersRef.current[toolId] = controller;
    setRoleDicomUploads(current => ({
      ...current,
      [toolId]: {
        ...current[toolId],
        status: 'uploading',
        error: null,
      },
    }));

    try {
      const result = await analyzeDicomFiles(fileArray, accessToken, controller.signal);
      const uploadNiftiAssets = result.prepared_nifti_assets ?? [];
      const uploadAlignedNiftiAssets = result.aligned_nifti_assets ?? [];
      const analyzedSeries: DicomSeries[] = result.series.map((series, index) => {
        const displaySeries = mapApiSeriesToDisplaySeries(series, index);
        const niftiAsset = uploadNiftiAssets.find(
          asset => asset.series_instance_uid === series.series_instance_uid
        );
        return {
          ...displaySeries,
          id: `${result.upload_id}:${displaySeries.id}`,
          uploadId: result.upload_id,
          workspaceUpload: true,
          niftiAsset,
          alignedNiftiAsset: uploadAlignedNiftiAssets.find(
            asset =>
              asset.source_asset_id === niftiAsset?.asset_id ||
              asset.series_instance_uid === series.series_instance_uid
          ),
        };
      });
      mergePreparedNiftiAssets(uploadNiftiAssets);
      mergePreparedAlignedNiftiAssets(uploadAlignedNiftiAssets);
      const compatibleSeries = analyzedSeries.filter(series =>
        isSeriesCompatibleWithRole(series, toolId)
      );
      setRoleDicomUploads(current => ({
        ...current,
        [toolId]: {
          uploadId: result.upload_id,
          series: isCtRole(toolId) ? compatibleSeries : analyzedSeries,
          status: compatibleSeries.length ? 'success' : 'error',
          error: compatibleSeries.length
            ? null
            : `No ${isCtRole(toolId) ? 'CT' : 'MR'} series found. Please upload ${isCtRole(toolId) ? 'CT' : 'MR'} image data.`,
          alignmentStatus: result.alignment_status ?? 'ready',
        },
      }));
      if (previousUpload.uploadId && previousUpload.uploadId !== result.upload_id) {
        void deleteDicomUpload(previousUpload.uploadId, accessToken).catch(() => undefined);
      }
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }

      setRoleDicomUploads(current => ({
        ...current,
        [toolId]: {
          ...previousUpload,
          status: 'error',
          error: getWorkspaceUploadError(error),
        },
      }));
    } finally {
      if (roleUploadAbortControllersRef.current[toolId] === controller) {
        delete roleUploadAbortControllersRef.current[toolId];
      }
    }
  };

  const handleCancelUpload = (toolId: ToolMode) => {
    setToolUploads(prev => ({
      ...prev,
      [toolId]: null,
    }));

    if (!isDicomUploadRole(toolId)) {
      return;
    }

    roleUploadAbortControllersRef.current[toolId]?.abort();
    delete roleUploadAbortControllersRef.current[toolId];
    const currentUploadId = roleDicomUploads[toolId].uploadId;
    setRoleDicomUploads(current => ({
      ...current,
      [toolId]: {
        ...createEmptyRoleDicomUploads()[toolId],
        status: 'success',
      },
    }));
    if (isCtRole(toolId)) {
      setConfirmedCtRoles(current => {
        const next = new Set(current);
        next.delete(toolId);
        return next;
      });
    }
    setSelectedSeriesByRole(current => ({
      ...current,
      [toolId]: null,
    }));

    if (currentUploadId && accessToken) {
      void deleteDicomUpload(currentUploadId, accessToken).catch(() => undefined);
    }
  };

  const toggleToolDropdown = (toolId: ToolMode) => {
    setIsParcelizationOpen(false);
    setParcelizationOwnerSlot(null);
    setOrientationPopoverSlot(null);
    setOrientationPopoverSlot(null);
    setActiveTool(toolId);
    setOpenToolDropdown(current => (current === toolId ? null : toolId));
  };

  const setParcelizationEnabled = (enabled: boolean) => {
    setIsParcelizationActive(enabled);
  };

  const toggleParcelizationForSlot = (slot: ViewerSlot) => {
    setOpenToolDropdown(null);
    setOrientationPopoverSlot(null);
    const isCurrentControlOpen = isParcelizationOpen && parcelizationOwnerSlot === slot;
    if (!isCurrentControlOpen && parcelizationCatalogStatus === 'error') {
      setParcelizationCatalogStatus('idle');
    }
    setParcelizationOwnerSlot(isCurrentControlOpen ? null : slot);
    setIsParcelizationOpen(!isCurrentControlOpen);
  };

  const toggleParcellizationSelection = (id: string) => {
    if (!isParcelizationActive) return;
    setSelectedParcellizations(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const normalizedParcelizationSearch = parcelizationSearch.trim().toLocaleLowerCase();
  const parcelizationOptions =
    parcelizationCatalog.length > 0 ? parcelizationCatalog : PARCELLIZATION_OPTIONS;
  const visibleParcellizations = [...parcelizationOptions]
    .filter(option => option.label.toLocaleLowerCase().includes(normalizedParcelizationSearch))
    .sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: 'base' })
    );
  const selectedParcelizationOptions = visibleParcellizations.filter(option =>
    selectedParcellizations.has(option.id)
  );
  const allParcelizationOptions = visibleParcellizations;

  const activeParcelizationVolumes = useMemo<ProjectViewerParcelizationVolume[]>(
    () =>
      isParcelizationActive
        ? parcelizationCatalog
            .filter(option => selectedParcellizations.has(option.id))
            .flatMap(option =>
              option.volumes.map(volume => ({
                id: `${option.id}-${volume.hemisphere}`,
                label: option.label,
                color: option.color,
                hemisphere: volume.hemisphere,
                niftiUrl: volume.nifti_url,
              }))
            )
        : [],
    [isParcelizationActive, parcelizationCatalog, selectedParcellizations]
  );

  const addScanToAlign = () => {
    if (pendingScanToAlignRole) {
      setActiveTool(pendingScanToAlignRole);
      setOpenToolDropdown(pendingScanToAlignRole);
      return;
    }

    const nextRole = SCAN_TO_ALIGN_ROLES.find(role => !scanToAlignRoles.includes(role));

    if (!nextRole || scanToAlignRoles.length >= MAX_SCAN_TO_ALIGN_COUNT) return;

    setScanToAlignRoles(currentRoles => [...currentRoles, nextRole]);
    setPendingScanToAlignRole(nextRole);
    setActiveTool(nextRole);
    setOpenToolDropdown(nextRole);
  };

  const addCt = () => {
    const nextRole = CT_ROLES.find(role => !ctRoles.includes(role));
    if (!nextRole || ctRoles.length >= MAX_CT_COUNT) return;

    setCtRoles(currentRoles => [...currentRoles, nextRole]);
    setActiveTool(nextRole);
    setOpenToolDropdown(nextRole);
  };

  // A confirmed source must be visible, including when the current viewer
  // shows another modality. Prefer the maximized panel or an existing panel
  // for this role, then the first open panel.
  const showSelectedRole = (role: ViewerRole, variant: SeriesDisplayVariant = 'original') => {
    const visibleSlots = renderedViewerSlots.filter(slot => visibleViewers[slot]);
    const targetSlot =
      maximizedViewerSlot ??
      visibleSlots.find(slot => viewerRoles[slot] === role) ??
      visibleSlots[0] ??
      'left';
    setVisibleViewers(current => ({ ...current, [targetSlot]: true }));
    setViewerHasExplicitSelection(current => ({ ...current, [targetSlot]: true }));
    setViewerRoles(current => ({ ...current, [targetSlot]: role }));
    setViewerRoleVariants(current => ({ ...current, [targetSlot]: variant }));
    setViewerEditModes(current => {
      const next = { ...current, [targetSlot]: false };
      (Object.keys(next) as ViewerSlot[]).forEach(slot => {
        if (viewerRoles[slot] === role) next[slot] = false;
      });
      return next;
    });
    setViewerEditNotAlignedWarning(current => ({ ...current, [targetSlot]: false }));
    setActiveTool(role);
  };

  const confirmScanToAlign = async (
    role: DicomViewerRole,
    seriesId: string,
    variant: SeriesDisplayVariant = 'original'
  ) => {
    const roleUpload = roleDicomUploads[role];
    const seriesOptions = getSeriesOptionsForRole(role);
    const selectedSeries = seriesOptions.find(series => series.id === seriesId);
    if (!selectedSeries || !selectedSeries.uploadId || !accessToken) {
      setRoleDicomUploads(current => ({
        ...current,
        [role]: {
          ...current[role],
          error: 'The selected MR series could not be prepared for the viewer.',
        },
      }));
      return;
    }

    try {
      let niftiAsset = selectedSeries.niftiAsset;
      let alignedNiftiAsset = selectedSeries.alignedNiftiAsset;
      if (!niftiAsset) {
        const prepared = await prepareSelectedSeriesData(
          selectedSeries.uploadId,
          selectedSeries.studyInstanceUid,
          selectedSeries.seriesInstanceUid,
          accessToken
        );
        [niftiAsset] = prepared.assets;
        alignedNiftiAsset = prepared.aligned_assets.find(
          asset => asset.source_asset_id === niftiAsset?.asset_id
        );
        mergePreparedNiftiAssets(prepared.assets);
        mergePreparedAlignedNiftiAssets(prepared.aligned_assets);
      }

      if (variant === 'aligned' && !alignedNiftiAsset) {
        throw new Error('Oriented data for the selected series is not ready yet.');
      }

      if (roleUpload.status !== 'idle' && niftiAsset) {
        setRoleDicomUploads(current => ({
          ...current,
          [role]: {
            ...current[role],
            series: current[role].series.map(series =>
              series.id === seriesId ? { ...series, niftiAsset, alignedNiftiAsset } : series
            ),
          },
        }));
      }
      setSelectedSeriesByRole(current => ({
        ...current,
        [role]: seriesId,
      }));
      setAlignedSeriesByRole(current => ({ ...current, [role]: variant === 'aligned' }));
      setAlignedNiftiAssetsByRole(current => {
        const next = { ...current };
        if (alignedNiftiAsset) next[role] = alignedNiftiAsset;
        else delete next[role];
        return next;
      });
      updateRoleDisplayVariant(role, variant);
      showSelectedRole(role, variant);
      if (role === pendingScanToAlignRole) {
        setPendingScanToAlignRole(null);
      }
      setOpenToolDropdown(current => (current === role ? null : current));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }
      setRoleDicomUploads(current => ({
        ...current,
        [role]: {
          ...current[role],
          error: getWorkspaceUploadError(error),
        },
      }));
      console.error('MR NIfTI preparation failed.', error);
    }
  };

  const confirmCtSelection = async (
    role: CtRole,
    seriesId: string,
    variant: SeriesDisplayVariant = 'original'
  ) => {
    const roleUpload = roleDicomUploads[role];
    const seriesOptions = getSeriesOptionsForRole(role);
    const selectedSeries = seriesOptions.find(series => series.id === seriesId);
    if (!selectedSeries || !selectedSeries.uploadId || !accessToken) {
      setRoleDicomUploads(current => ({
        ...current,
        [role]: {
          ...current[role],
          error: 'The selected CT series could not be prepared for the viewer.',
        },
      }));
      return;
    }

    try {
      let niftiAsset = selectedSeries.niftiAsset;
      let alignedNiftiAsset = selectedSeries.alignedNiftiAsset;
      if (!niftiAsset) {
        const prepared = await prepareSelectedSeriesData(
          selectedSeries.uploadId,
          selectedSeries.studyInstanceUid,
          selectedSeries.seriesInstanceUid,
          accessToken
        );
        [niftiAsset] = prepared.assets;
        alignedNiftiAsset = prepared.aligned_assets.find(
          asset =>
            asset.source_asset_id === niftiAsset?.asset_id ||
            asset.series_instance_uid === selectedSeries.seriesInstanceUid
        );
        mergePreparedNiftiAssets(prepared.assets);
        mergePreparedAlignedNiftiAssets(prepared.aligned_assets);
      }

      if (variant === 'aligned' && !alignedNiftiAsset) {
        throw new Error('Oriented data for the selected series is not ready yet.');
      }

      if (roleUpload.status !== 'idle' && niftiAsset) {
        setRoleDicomUploads(current => ({
          ...current,
          [role]: {
            ...current[role],
            alignmentStatus: alignedNiftiAsset ? current[role].alignmentStatus : 'pending',
            series: current[role].series.map(series =>
              series.id === seriesId ? { ...series, niftiAsset, alignedNiftiAsset } : series
            ),
          },
        }));
      }
      if (!alignedNiftiAsset) {
        setRoleDicomUploads(current => ({
          ...current,
          [role]: {
            ...current[role],
            uploadId: selectedSeries.uploadId,
            alignmentStatus: 'pending',
          },
        }));
      }

      setSelectedSeriesByRole(current => ({ ...current, [role]: seriesId }));
      setAlignedSeriesByRole(current => ({ ...current, [role]: variant === 'aligned' }));
      setAlignedNiftiAssetsByRole(current => {
        const next = { ...current };
        if (alignedNiftiAsset) next[role] = alignedNiftiAsset;
        else delete next[role];
        return next;
      });
      updateRoleDisplayVariant(role, variant);
      showSelectedRole(role, variant);
      setConfirmedCtRoles(current => new Set(current).add(role));
      setOpenToolDropdown(current => (current === role ? null : current));
      if (!alignedNiftiAsset) {
        void startRemainingUploadAlignment(selectedSeries.uploadId, accessToken).catch(error => {
          if (error instanceof ApiError && error.status === 401) {
            logout();
            return;
          }
          console.warn('CT alignment could not be started.', error);
        });
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        logout();
        return;
      }
      setRoleDicomUploads(current => ({
        ...current,
        [role]: { ...current[role], error: getWorkspaceUploadError(error) },
      }));
      console.error('CT NIfTI preparation failed.', error);
    }
  };

  const selectAtlas = (role: AtlasRole, atlasId: string) => {
    const atlas = atlasCatalog.find(item => item.id === atlasId);
    if (!atlas) return;

    setSelectedAtlases({ atlas });
    setSelectedSeriesByRole(current => ({ ...current, [role]: `atlas:${atlasId}` }));
    setToolUploads(current => ({
      ...current,
      [role]: {
        name: getAtlasDisplayName(atlas),
        count: 1,
        size: atlas.template_size_bytes,
        source: 'files',
      },
    }));
    setActiveTool(role);
    setOpenToolDropdown(null);
    showSelectedRole(role);
  };

  const removeAtlas = (role: AtlasRole) => {
    setSelectedAtlases(current => {
      const next = { ...current };
      delete next[role];
      return next;
    });
    setSelectedSeriesByRole(current => ({ ...current, [role]: null }));
    setToolUploads(current => ({ ...current, [role]: null }));
    const fallback = 'fixed';
    setViewerRoles(current => ({
      left: current.left === role ? fallback : current.left,
      center: current.center === role ? fallback : current.center,
      right: current.right === role ? fallback : current.right,
    }));
    setCenterSecondaryRole(current => (current === role ? fallback : current));
    setCenterTertiaryRole(current => (current === role ? fallback : current));
    setActiveTool(current => (current === role ? fallback : current));
    setOpenToolDropdown(current => (current === role ? null : current));
  };

  const removeScanToAlign = (role: DicomViewerRole) => {
    if (role === 'fixed') return;

    setScanToAlignRoles(currentRoles => currentRoles.filter(currentRole => currentRole !== role));
    setPendingScanToAlignRole(current => (current === role ? null : current));
    setSelectedSeriesByRole(current => ({ ...current, [role]: null }));
    setAlignedSeriesByRole(current => ({ ...current, [role]: false }));
    setAlignedNiftiAssetsByRole(current => {
      const next = { ...current };
      delete next[role];
      return next;
    });
    setToolUploads(current => ({ ...current, [role]: null }));
    roleUploadAbortControllersRef.current[role]?.abort();
    delete roleUploadAbortControllersRef.current[role];
    const uploadIdToDelete = roleDicomUploads[role].uploadId;
    setRoleDicomUploads(current => ({
      ...current,
      [role]: createEmptyRoleDicomUploads()[role],
    }));
    if (uploadIdToDelete && accessToken) {
      void deleteDicomUpload(uploadIdToDelete, accessToken).catch(() => undefined);
    }
    setViewerRoles(current => ({
      left: current.left === role ? 'fixed' : current.left,
      center: current.center === role ? 'fixed' : current.center,
      right: current.right === role ? 'fixed' : current.right,
    }));
    setCenterSecondaryRole(current => (current === role ? 'fixed' : current));
    setCenterTertiaryRole(current => (current === role ? 'fixed' : current));
    setActiveTool(current => (current === role ? 'fixed' : current));
    setOpenToolDropdown(current => (current === role ? null : current));
  };

  const removeSelectedDicomRole = (role: DicomUploadRole) => {
    if (isCtRole(role)) {
      handleCancelUpload(role);
      if (role !== 'ct') {
        setCtRoles(currentRoles => currentRoles.filter(currentRole => currentRole !== role));
      }
      setAlignedSeriesByRole(current => ({ ...current, [role]: false }));
      setAlignedNiftiAssetsByRole(current => {
        const next = { ...current };
        delete next[role];
        return next;
      });
      setViewerRoles(current => ({
        left: current.left === role ? 'fixed' : current.left,
        center: current.center === role ? 'fixed' : current.center,
        right: current.right === role ? 'fixed' : current.right,
      }));
      setCenterSecondaryRole(current => (current === role ? 'fixed' : current));
      setCenterTertiaryRole(current => (current === role ? 'fixed' : current));
      setOpenToolDropdown(null);
      setActiveTool('fixed');
      return;
    }

    if (role !== 'fixed') {
      removeScanToAlign(role);
      return;
    }

    handleCancelUpload(role);
    setAlignedSeriesByRole(current => ({ ...current, fixed: false }));
    setAlignedNiftiAssetsByRole(current => {
      const next = { ...current };
      delete next.fixed;
      return next;
    });
    setOpenToolDropdown(null);
    setActiveTool('fixed');
  };

  const closeOpenDicomPopup = useCallback(() => {
    setOpenToolDropdown(null);
    setIsParcelizationOpen(false);
    setParcelizationOwnerSlot(null);
    setViewerInteractionSignal(current => current + 1);
  }, []);

  const startViewerEditMode = useCallback(
    (slot: ViewerSlot) => {
      if (viewerEditModes[slot]) {
        return;
      }

      const role = viewerRoles[slot];
      if (isAtlasRole(role)) return;
      const sourceSeries = resolveViewerRoleSeries(
        role,
        selectedSeriesByRole,
        activeDicomSeries,
        roleDicomUploads,
        preparedNiftiAssets
      );
      const alignedAsset = alignedNiftiAssetsByRole[role];
      // Never enable volume editing on an original image while waiting for
      // its aligned output. Availability, not the current display choice,
      // determines whether Edit can start.
      if (
        !sourceSeries ||
        !alignedAsset ||
        (alignedAsset.source_asset_id &&
          alignedAsset.source_asset_id !== sourceSeries.niftiAsset?.asset_id)
      ) {
        setViewerEditNotAlignedWarning(current => ({ ...current, [slot]: true }));
        const existingTimeout = viewerEditNotAlignedWarningTimeoutsRef.current[slot];
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }
        viewerEditNotAlignedWarningTimeoutsRef.current[slot] = setTimeout(() => {
          delete viewerEditNotAlignedWarningTimeoutsRef.current[slot];
          setViewerEditNotAlignedWarning(current => ({ ...current, [slot]: false }));
        }, 4000);
        return;
      }

      // Cancel should restore the oriented asset that was active when this
      // session opened, not a later reset baseline.
      orientationEditSessionAssetsRef.current[slot] = { role, asset: alignedAsset };

      setAlignedSeriesByRole(current => ({ ...current, [role]: true }));
      setViewerRoleVariants(current => {
        const next = { ...current };
        (Object.keys(next) as ViewerSlot[]).forEach(viewerSlot => {
          if (viewerRoles[viewerSlot] === role) next[viewerSlot] = 'aligned';
        });
        return next;
      });
      if (centerSecondaryRole === role) setCenterSecondaryVariant('aligned');
      if (centerTertiaryRole === role) setCenterTertiaryVariant('aligned');
      setViewerEditNotAlignedWarning(current => ({ ...current, [slot]: false }));
      setLeftViewerGrid(4);
      setFixedViewerLayout('singleAxial');
      setFixedViewerPanResetRequest(null);
      setVisibleViewers(current => ({ ...current, [slot]: true }));
      // A new iframe document discards camera/presentation state and old
      // unsaved actor transforms, even if this same aligned asset is open.
      // The asset remains aligned; only the viewing session is reset.
      setViewerReloadVersions(current => ({ ...current, [slot]: current[slot] + 1 }));
      setViewerEditRotations(current => ({ ...current, [slot]: [] }));
      setViewerEditErrors(current => ({ ...current, [slot]: null }));
      setViewerEditModes(current => ({ ...current, [slot]: true }));
    },
    [
      viewerEditModes,
      viewerRoles,
      selectedSeriesByRole,
      activeDicomSeries,
      roleDicomUploads,
      preparedNiftiAssets,
      alignedNiftiAssetsByRole,
      centerSecondaryRole,
      centerTertiaryRole,
      orientationEditSessionAssetsRef,
    ]
  );

  const handleViewerRotation = useCallback(
    (slot: ViewerSlot, rotation: ProjectViewerRotation) => {
      if (!viewerEditModes[slot]) {
        return;
      }

      const axisLength = Math.hypot(...rotation.axis);
      if (!Number.isFinite(axisLength) || axisLength < 1e-8) {
        return;
      }
      const operation: NiftiRotationOperation = {
        axis: rotation.axis.map(coordinate => coordinate / axisLength) as [number, number, number],
        angle_degrees: rotation.angleDegrees,
        ...(rotation.translation ? { translation: rotation.translation } : {}),
      };

      setViewerEditRotations(current => {
        const currentOperations = current[slot];
        const previous = currentOperations.at(-1);
        if (!previous) {
          return { ...current, [slot]: [operation] };
        }

        if (operation.translation || previous.translation) {
          if (!operation.translation || !previous.translation) {
            return { ...current, [slot]: [...currentOperations, operation] };
          }
          const translation = previous.translation.map(
            (value, index) => value + operation.translation[index]
          ) as [number, number, number];
          return {
            ...current,
            [slot]: translation.every(value => Math.abs(value) < 1e-8)
              ? currentOperations.slice(0, -1)
              : [...currentOperations.slice(0, -1), { ...operation, translation }],
          };
        }

        const axisDot = previous.axis.reduce(
          (sum, coordinate, index) => sum + coordinate * operation.axis[index],
          0
        );
        if (Math.abs(axisDot) < 0.9999) {
          return { ...current, [slot]: [...currentOperations, operation] };
        }

        const combinedAngle =
          previous.angle_degrees +
          (axisDot < 0 ? -operation.angle_degrees : operation.angle_degrees);
        const normalizedAngle = ((((combinedAngle + 180) % 360) + 360) % 360) - 180;
        const precedingOperations = currentOperations.slice(0, -1);
        return {
          ...current,
          [slot]:
            Math.abs(normalizedAngle) < 1e-8
              ? precedingOperations
              : [...precedingOperations, { ...previous, angle_degrees: normalizedAngle }],
        };
      });
      setViewerEditErrors(current => (current[slot] ? { ...current, [slot]: null } : current));
    },
    [viewerEditModes]
  );

  const applySavedNiftiAsset = useCallback(
    (
      uploadId: string,
      previousAsset: PreparedNiftiAsset,
      response: RotatePreparedNiftiResponse
    ) => {
      const invalidatedIds = new Set(response.invalidated_asset_ids);
      const replaceAsset = (asset: PreparedNiftiAsset) =>
        asset.asset_id === previousAsset.asset_id ? response.asset : asset;

      if (response.asset.variant === 'aligned') {
        setPreparedAlignedNiftiAssets(current => current.map(replaceAsset));
      } else {
        setPreparedNiftiAssets(current => current.map(replaceAsset));
        setPreparedAlignedNiftiAssets(current =>
          current.filter(asset => !invalidatedIds.has(asset.asset_id))
        );
      }

      setAlignedNiftiAssetsByRole(current => {
        const next = { ...current };
        (Object.keys(next) as DicomUploadRole[]).forEach(role => {
          const asset = next[role];
          if (!asset) return;
          if (asset.asset_id === previousAsset.asset_id) next[role] = response.asset;
          if (invalidatedIds.has(asset.asset_id)) delete next[role];
        });
        return next;
      });
      if (invalidatedIds.size > 0) {
        setAlignedSeriesByRole(current => {
          const next = { ...current };
          (Object.keys(next) as DicomUploadRole[]).forEach(role => {
            const asset = alignedNiftiAssetsByRole[role];
            if (asset && invalidatedIds.has(asset.asset_id)) next[role] = false;
          });
          return next;
        });
      }

      setRoleDicomUploads(current => {
        let stateChanged = false;
        const next = { ...current };
        (Object.keys(next) as DicomUploadRole[]).forEach(role => {
          const upload = current[role];
          if (upload.uploadId !== uploadId) return;
          const nextSeries = upload.series.map(series => {
            const niftiAsset = series.niftiAsset
              ? replaceAsset(series.niftiAsset)
              : series.niftiAsset;
            const alignedNiftiAsset = series.alignedNiftiAsset
              ? invalidatedIds.has(series.alignedNiftiAsset.asset_id)
                ? undefined
                : replaceAsset(series.alignedNiftiAsset)
              : undefined;
            return niftiAsset !== series.niftiAsset ||
              alignedNiftiAsset !== series.alignedNiftiAsset
              ? { ...series, niftiAsset, alignedNiftiAsset }
              : series;
          });
          if (
            nextSeries.some((series, index) => series !== upload.series[index]) ||
            upload.alignmentStatus !== response.alignment_status
          ) {
            stateChanged = true;
            next[role] = {
              ...upload,
              series: nextSeries,
              alignmentStatus: response.alignment_status,
            };
          }
        });
        return stateChanged ? next : current;
      });

      if (locationState?.uploadId === uploadId) {
        setInitialUploadAlignmentStatus(response.alignment_status);
        setShouldHydrateInitialUpload(response.alignment_status !== 'ready');
      }
    },
    [alignedNiftiAssetsByRole, locationState?.uploadId]
  );

  const cancelViewerEdit = useCallback(
    (slot: ViewerSlot, { userInitiated = true }: { userInitiated?: boolean } = {}) => {
      // Only an explicit Cancel click should stop the "Edit" workflow from
      // trying again - the automatic cancel below (triggered by the variant
      // merely passing through "original" while the aligned asset is still
      // settling) must not permanently give up on auto-starting edit mode.
      if (userInitiated && slot === 'left' && workspacePreset === 'edit') {
        editWorkflowCancelledRef.current = true;
      }
      const session = orientationEditSessionAssetsRef.current[slot];
      if (session) {
        setAlignedNiftiAssetsByRole(current => ({
          ...current,
          [session.role]: session.asset,
        }));
        delete orientationEditSessionAssetsRef.current[slot];
      }
      setViewerEditModes(current => ({ ...current, [slot]: false }));
      setViewerEditRotations(current => ({ ...current, [slot]: [] }));
      setViewerEditErrors(current => ({ ...current, [slot]: null }));
      setViewerReloadVersions(current => ({ ...current, [slot]: current[slot] + 1 }));
    },
    [orientationEditSessionAssetsRef, workspacePreset]
  );

  const resetViewerEdit = useCallback(
    (slot: ViewerSlot) => {
      const session = orientationEditSessionAssetsRef.current[slot];
      const initialAsset = session
        ? initialOrientedAssetsByRoleRef.current[session.role]
        : undefined;
      if (session && initialAsset) {
        setAlignedNiftiAssetsByRole(current => ({
          ...current,
          [session.role]: initialAsset,
        }));
      }
      setViewerEditRotations(current => ({ ...current, [slot]: [] }));
      setViewerEditErrors(current => ({ ...current, [slot]: null }));
      // Reload the current Oriented asset to discard viewport-side transforms
      // while keeping the user inside Orientation mode.
      setViewerReloadVersions(current => ({ ...current, [slot]: current[slot] + 1 }));
    },
    [initialOrientedAssetsByRoleRef, orientationEditSessionAssetsRef]
  );

  const setViewerOrientationEnabled = useCallback(
    (slot: ViewerSlot, enabled: boolean) => {
      const role = viewerRoles[slot];
      if (isAtlasRole(role)) return;

      if (!enabled) {
        cancelViewerEdit(slot);
        if (viewerRoleVariants[slot] === 'aligned') {
          setAlignedSeriesByRole(current => ({ ...current, [role]: false }));
          setViewerRoleVariants(current => {
            const next = { ...current };
            (Object.keys(next) as ViewerSlot[]).forEach(viewerSlot => {
              if (viewerRoles[viewerSlot] === role) next[viewerSlot] = 'original';
            });
            return next;
          });
          if (centerSecondaryRole === role) setCenterSecondaryVariant('original');
          if (centerTertiaryRole === role) setCenterTertiaryVariant('original');
        }
        return;
      }

      startViewerEditMode(slot);
    },
    [
      cancelViewerEdit,
      centerSecondaryRole,
      centerTertiaryRole,
      startViewerEditMode,
      viewerRoles,
      viewerRoleVariants,
    ]
  );

  const toggleOrientationPopover = (slot: ViewerSlot) => {
    setOpenToolDropdown(null);
    setIsParcelizationOpen(false);
    setParcelizationOwnerSlot(null);
    setOrientationPopoverSlot(current => (current === slot ? null : slot));
  };

  // Editing only ever applies to the aligned variant. Whatever switched a
  // slot away from it - the strip's role/variant select or a fresh series
  // pick elsewhere - should leave editing behind rather than keep it open
  // on the original image.
  useEffect(() => {
    (Object.keys(viewerEditModes) as ViewerSlot[]).forEach(slot => {
      if (viewerEditModes[slot] && viewerRoleVariants[slot] !== 'aligned') {
        cancelViewerEdit(slot, { userInitiated: false });
      }
    });
  }, [cancelViewerEdit, viewerEditModes, viewerRoleVariants]);

  // Dismiss the "not aligned yet" warning as soon as the slot actually
  // switches to the aligned variant, instead of leaving it up until its
  // timeout fires.
  useEffect(() => {
    (Object.keys(viewerRoleVariants) as ViewerSlot[]).forEach(slot => {
      if (viewerRoleVariants[slot] === 'aligned' && viewerEditNotAlignedWarning[slot]) {
        const pendingTimeout = viewerEditNotAlignedWarningTimeoutsRef.current[slot];
        if (pendingTimeout) {
          clearTimeout(pendingTimeout);
          delete viewerEditNotAlignedWarningTimeoutsRef.current[slot];
        }
        setViewerEditNotAlignedWarning(current => ({ ...current, [slot]: false }));
      }
    });
  }, [viewerRoleVariants, viewerEditNotAlignedWarning]);

  useEffect(() => {
    return () => {
      Object.values(viewerEditNotAlignedWarningTimeoutsRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  useEffect(() => {
    if (
      workspacePreset !== 'edit' ||
      editWorkflowCancelledRef.current ||
      viewerEditModes.left ||
      isAtlasRole(viewerRoles.left) ||
      !alignedNiftiAssetsByRole[viewerRoles.left]
    ) {
      return;
    }
    // Wait for the selected series' aligned output, then select it and enter
    // editing without requiring a separate Original/Aligned choice.
    startViewerEditMode('left');
  }, [
    startViewerEditMode,
    viewerEditModes.left,
    viewerRoles.left,
    alignedNiftiAssetsByRole,
    workspacePreset,
  ]);

  const saveViewerEdit = useCallback(
    async (slot: ViewerSlot, series: DicomSeries) => {
      const asset = series.niftiAsset;
      const uploadId = series.uploadId ?? (slot === 'left' ? locationState?.uploadId : undefined);
      const operations = viewerEditRotations[slot];
      if (!accessToken || !asset || !uploadId || operations.length === 0) {
        return;
      }

      setViewerEditSaveStatus(current => ({ ...current, [slot]: 'saving' }));
      setViewerEditErrors(current => ({ ...current, [slot]: null }));
      try {
        const response = await rotatePreparedNiftiAsset(uploadId, asset, operations, accessToken);
        applySavedNiftiAsset(uploadId, asset, response);
        delete orientationEditSessionAssetsRef.current[slot];
        setViewerEditRotations(current => ({ ...current, [slot]: [] }));
        setViewerEditModes(current => ({ ...current, [slot]: false }));
        setViewerReloadVersions(current => ({ ...current, [slot]: current[slot] + 1 }));
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
          return;
        }
        setViewerEditErrors(current => ({
          ...current,
          [slot]:
            error instanceof Error ? error.message : 'The rotated NIfTI image could not be saved.',
        }));
      } finally {
        setViewerEditSaveStatus(current => ({ ...current, [slot]: 'idle' }));
      }
    },
    [
      accessToken,
      applySavedNiftiAsset,
      locationState?.uploadId,
      logout,
      orientationEditSessionAssetsRef,
      viewerEditRotations,
    ]
  );

  return (
    <div
      className="pc-root"
      data-workspace-preset={workspacePreset}
    >
      <main className="pc-main">
        <div className="pc-wrapper">
          <Topbar
            title="Project Overview"
            className="pc-shared-topbar"
            leadingContent={
              <div
                className="pc-patient-menu"
                ref={patientInfoMenuRef}
              >
                <button
                  type="button"
                  className="pc-patient-block"
                  onClick={() => {
                    if (!isPatientInfoEditing) setIsPatientInfoOpen(current => !current);
                  }}
                  aria-label="Open patient information"
                  aria-haspopup="dialog"
                  aria-expanded={isPatientInfoOpen}
                >
                  <div className={`pc-patient-icon ${patientInfo.hasPhoto ? 'has-photo' : ''}`}>
                    <PatientPhoto
                      patientId={patientInfo.patientId}
                      hasPhoto={patientInfo.hasPhoto}
                      photoVersion={patientInfo.photoVersion}
                      token={accessToken}
                      alt={`${patientInfo.name} patient`}
                      className="pc-patient-avatar-image"
                      fallback={
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.8}
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 12a4 4 0 100-8 4 4 0 000 8z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 20a7.5 7.5 0 0115 0"
                          />
                        </svg>
                      }
                    />
                  </div>

                  <div className="pc-patient-summary">
                    <span className="pc-patient-name-row">
                      <span className="pc-patient-name">{patientInfo.name}</span>
                    </span>
                    <span className="pc-patient-meta">
                      {patientInfo.dob && <span>{patientInfo.dob}</span>}
                      {patientInfo.age !== undefined && <span>{patientInfo.age} years</span>}
                      {patientGenderIcon && (
                        <img
                          src={patientGenderIcon}
                          alt={patientGenderLabel}
                          title={patientGenderLabel}
                          className="pc-patient-gender-icon"
                          draggable={false}
                        />
                      )}
                    </span>
                  </div>
                </button>

                {isPatientInfoOpen && (
                  <div
                    className="pc-patient-info-popover"
                    role="dialog"
                    aria-label="Patient information"
                  >
                    <ViewerPatientInfo
                      key={patientInfo.patientId ?? 'unsaved'}
                      patient={{
                        ...patientInfo,
                        gender: patientGenderLabel || patientInfo.gender || '',
                      }}
                      token={accessToken}
                      onSaved={setSavedPatient}
                      onEditingChange={setIsPatientInfoEditing}
                      loading={patientLoading}
                      loadError={patientLoadError}
                      onRetry={() => setPatientLoadVersion(current => current + 1)}
                    />
                  </div>
                )}
              </div>
            }
          />

          <div className="pc-frame">
            <div className="pc-toolbar">
              <div className="pc-toolbar-left">
                <div
                  className="pc-toolbar-assets"
                  ref={toolUploadRef}
                >
                  <div className="pc-tool-upload-bar">
                    <span className="pc-toolbar-section-label">MR</span>
                    {dicomRoleToolButtons.map(tool => (
                      <RoleDicomDropdownTrigger
                        key={tool.id}
                        role={tool.id}
                        label={tool.label}
                        isPending={
                          tool.id === pendingScanToAlignRole || !selectedSeriesByRole[tool.id]
                        }
                        isActive={activeTool === tool.id}
                        isOpen={openToolDropdown === tool.id}
                        assetStatus={
                          tool.id === pendingScanToAlignRole
                            ? 'missing'
                            : getToolAssetStatus(tool.id)
                        }
                        isRemovable={false}
                        uploadedFile={getRoleUploadedFile(tool.id)}
                        uploadStatus={roleDicomUploads[tool.id].status}
                        uploadError={roleDicomUploads[tool.id].error}
                        uploadId={
                          roleDicomUploads[tool.id].uploadId ??
                          getSeriesOptionsForRole(tool.id).find(series => series.uploadId)
                            ?.uploadId ??
                          null
                        }
                        token={accessToken ?? ''}
                        isUploadRemovable={Boolean(getRoleUploadedFile(tool.id))}
                        selectedSeriesId={selectedSeriesByRole[tool.id]}
                        seriesOptions={getDisplaySeriesOptionsForRole(tool.id)}
                        onToggle={() => toggleToolDropdown(tool.id)}
                        onClose={() =>
                          setOpenToolDropdown(current => (current === tool.id ? null : current))
                        }
                        onRemove={() => removeSelectedDicomRole(tool.id)}
                        onUpload={(files, source) => {
                          void handleToolUpload(tool.id, files, source);
                        }}
                        onCancelUpload={() => handleCancelUpload(tool.id)}
                        onConfirmSelection={(seriesId, variant) =>
                          confirmScanToAlign(tool.id, seriesId, variant)
                        }
                      />
                    ))}

                    {!pendingScanToAlignRole &&
                      selectedSeriesByRole.fixed &&
                      scanToAlignRoles.length < MAX_SCAN_TO_ALIGN_COUNT && (
                        <button
                          type="button"
                          className="pc-add-scan-to-align"
                          onClick={addScanToAlign}
                          aria-label="Add MR"
                          title={`Add MR (${scanToAlignRoles.length}/${MAX_SCAN_TO_ALIGN_COUNT})`}
                        >
                          <span aria-hidden="true">+</span>
                          <span>MR</span>
                        </button>
                      )}
                  </div>

                  <span
                    className="pc-toolbar-action-divider"
                    aria-hidden="true"
                  />

                  <div className="pc-atlas-tools-group">
                    <span className="pc-toolbar-section-label">Brain Template</span>
                    <AtlasDropdownTrigger
                      label={
                        selectedAtlases.atlas
                          ? getAtlasFamilyTitle(getAtlasFamily(selectedAtlases.atlas))
                          : 'Brain Template'
                      }
                      isOpen={openToolDropdown === 'atlas'}
                      isActive={activeTool === 'atlas'}
                      atlases={atlasCatalog}
                      token={accessToken}
                      status={atlasCatalogStatus}
                      error={atlasCatalogError}
                      selectedAtlasId={selectedAtlases.atlas?.id ?? null}
                      selectedAtlas={selectedAtlases.atlas}
                      onToggle={() => toggleToolDropdown('atlas')}
                      onSelect={atlasId => selectAtlas('atlas', atlasId)}
                      onRemove={() => removeAtlas('atlas')}
                      onRetry={() => setAtlasCatalogReloadVersion(version => version + 1)}
                    />
                  </div>

                  <span
                    className="pc-toolbar-action-divider"
                    aria-hidden="true"
                  />

                  <div className="pc-ct-tools-group">
                    <span className="pc-toolbar-section-label">CT</span>
                    {ctRoles.map(role => {
                      const isConfirmed = confirmedCtRoles.has(role);
                      const roleLabel = getViewerRoleLabel(role);

                      return (
                        <RoleDicomDropdownTrigger
                          key={role}
                          role={role}
                          label={isConfirmed ? roleLabel : '+ CT'}
                          isPending={!isConfirmed}
                          isActive={activeTool === role}
                          isOpen={openToolDropdown === role}
                          assetStatus={isConfirmed ? getToolAssetStatus(role) : 'missing'}
                          isRemovable={false}
                          uploadedFile={getRoleUploadedFile(role)}
                          uploadStatus={roleDicomUploads[role].status}
                          uploadError={roleDicomUploads[role].error}
                          uploadId={
                            roleDicomUploads[role].uploadId ??
                            getSeriesOptionsForRole(role).find(series => series.uploadId)
                              ?.uploadId ??
                            null
                          }
                          token={accessToken ?? ''}
                          isUploadRemovable={Boolean(getRoleUploadedFile(role))}
                          selectedSeriesId={selectedSeriesByRole[role]}
                          seriesOptions={getDisplaySeriesOptionsForRole(role).map(series => ({
                            ...series,
                            eligibleForFixed: true,
                          }))}
                          onToggle={() => toggleToolDropdown(role)}
                          onClose={() =>
                            setOpenToolDropdown(current => (current === role ? null : current))
                          }
                          onRemove={() => removeSelectedDicomRole(role)}
                          onUpload={(files, source) => {
                            void handleToolUpload(role, files, source);
                          }}
                          onCancelUpload={() => handleCancelUpload(role)}
                          onConfirmSelection={(seriesId, variant) =>
                            confirmCtSelection(role, seriesId, variant)
                          }
                          seriesTypeLabel="CT"
                        />
                      );
                    })}

                    {confirmedCtRoles.has(ctRoles[ctRoles.length - 1]) &&
                      ctRoles.length < MAX_CT_COUNT && (
                        <button
                          type="button"
                          className="pc-add-ct"
                          onClick={addCt}
                          aria-label="Add CT"
                          title={`Add CT (${ctRoles.length}/${MAX_CT_COUNT})`}
                        >
                          <span aria-hidden="true">+</span>
                          <span>CT</span>
                        </button>
                      )}
                  </div>
                </div>
              </div>

              <div className="pc-toolbar-right">
                <span
                  className="pc-toolbar-action-divider pc-home-divider"
                  aria-hidden="true"
                />

                <button
                  className="pc-tool-btn pc-tool-home pc-icon-only"
                  onClick={() => navigate('/dashboard')}
                  type="button"
                  aria-label="Go to dashboard"
                  title="Home"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 10.5 12 3l9 7.5"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5.25 9.75V21h13.5V9.75"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 21v-6h6v6"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="pc-body">
              <div
                ref={viewersLayoutRef}
                data-open-panel-count={openPanelCount}
                data-resize-locked={isWorkspacePopupOpen ? 'true' : 'false'}
                className={`pc-viewers-strip-layout ${
                  maximizedViewerSlot ? 'pc-has-maximized-viewer' : ''
                } ${
                  maximizedViewerSlot && isSeriesPresentedOpen ? 'pc-maximized-series-open' : ''
                } ${isWorkspacePopupOpen ? 'pc-resize-locked' : ''}`}
              >
                {/* Series strip: fixed width, collapse/expand only - never resizable. */}
                <div
                  data-resizable-panel="series"
                  className={`pc-series-slot pc-resizable-panel ${
                    isSeriesPresentedOpen ? 'open' : 'closed'
                  }`}
                >
                  <div className={`pc-viewer-strip ${isSeriesPresentedOpen ? 'open' : 'closed'}`}>
                    <button
                      type="button"
                      className="pc-viewer-strip-toggle"
                      onClick={toggleSeriesPanel}
                      title={isSeriesPresentedOpen ? 'Hide series' : 'Show series'}
                      aria-label={isSeriesPresentedOpen ? 'Hide series' : 'Show series'}
                    >
                      {isSeriesPresentedOpen ? '−' : '+'}
                    </button>

                    {!isSeriesPresentedOpen && (
                      <span
                        className="pc-viewer-strip-series-icon"
                        aria-hidden="true"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.65}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect
                            x="3"
                            y="3"
                            width="18"
                            height="18"
                            rx="2"
                          />
                          <circle
                            cx="8.5"
                            cy="9"
                            r="1.5"
                          />
                          <path d="m5 17 4.5-5 3 3.2L16 11l3 4.5" />
                        </svg>
                      </span>
                    )}
                  </div>

                  {isSeriesPresentedOpen && (
                    <div className="pc-sidebar-content">
                      {shouldShowFixedViewerLayoutSelector ? (
                        <FixedViewerLayoutSelector
                          options={fixedViewerLayoutOptions}
                          selectedLayout={activeFixedViewerLayout}
                          selectedParcelization3D={isParcelization3DView}
                          uploadId={selectedFixedSeries?.uploadId ?? locationState?.uploadId}
                          series={selectedFixedSeries}
                          liveThumbnailByOrientation={fixedLayoutThumbnailByOrientation}
                          onSelectLayout={selectFixedViewerLayout}
                          onResetPan={resetFixedViewerPan}
                        />
                      ) : !selectedFixedSeries ? (
                        <div className="pc-sidebar-empty-state">
                          <ViewerEmptyState slot="series" />
                        </div>
                      ) : (
                        <div className="pc-thumb-list">
                          {displayedDicomSeries.map((s, i) => {
                            const isLoadedInViewer = selectedSeriesIds.includes(s.id);
                            const liveThumbnailSrc =
                              liveSeriesThumbnailByKey[
                                buildDicomSeriesKey(s.studyInstanceUid, s.seriesInstanceUid)
                              ];

                            return (
                              <div
                                key={s.id}
                                className={`pc-thumb-card ${isLoadedInViewer ? 'active' : ''} ${hoveredId === s.id ? 'hovered' : ''}`}
                                onClick={() => toggleActive(s.id)}
                                onMouseEnter={() => setHoveredId(s.id)}
                                onMouseLeave={() => setHoveredId(null)}
                                title={s.label}
                              >
                                {isLoadedInViewer && <span className="pc-active-dot" />}

                                <span className="pc-thumb-num">W{i + 1}</span>

                                <div className="pc-thumb-img">
                                  {liveThumbnailSrc ? (
                                    <img
                                      className="pc-thumb-live-image"
                                      src={liveThumbnailSrc}
                                      alt={`${s.label} current slice`}
                                    />
                                  ) : (
                                    <NeutralDicomThumbnail />
                                  )}
                                </div>

                                {hoveredId === s.id && (
                                  <>
                                    <div className="pc-thumb-cross pc-thumb-cross-h" />
                                    <div className="pc-thumb-cross pc-thumb-cross-v" />
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <PanelResizeHandle
                  boundaryIndex={RESIZE_BOUNDARIES[0]}
                  isEnabled={canResizeBoundary(RESIZE_BOUNDARIES[0])}
                  isActive={activeResizeBoundary === RESIZE_BOUNDARIES[0]}
                  onPointerDown={event => startPanelResize(event, RESIZE_BOUNDARIES[0])}
                  onDoubleClick={resetPanelSizes}
                />

                {renderedViewerSlots.map(slot => {
                  const isRegistrationSlot = slot === 'center';
                  const selectedRole =
                    isRegistrationSlot && isCenterRegistrationReady && registrationFixedRole
                      ? registrationFixedRole
                      : viewerRoles[slot];
                  const selectedRoleVariant =
                    isRegistrationSlot && isCenterRegistrationReady && registrationFixedRole
                      ? registrationFixedVariant
                      : viewerRoleVariants[slot];
                  const hasSelectedPrimarySource = Boolean(
                    isRegistrationSlot && isCenterRegistrationReady && registrationFixedSeries
                      ? registrationFixedSeries
                      : getSeriesByViewerRole(selectedRole, selectedRoleVariant)
                  );
                  const renderableRole =
                    isRegistrationSlot && !isCenterRegistrationReady
                      ? null
                      : resolveRenderableViewerRole(
                          slot,
                          selectedRole,
                          isRegistrationSlot ? centerSecondaryRole : undefined,
                          isRegistrationSlot ? centerTertiaryRole : undefined,
                          presetConfig.showCenterTertiaryRole,
                          viewerHasExplicitSelection[slot],
                          hasSelectedPrimarySource
                        );
                  const effectiveViewerVariant: SeriesDisplayVariant =
                    renderableRole === selectedRole ? selectedRoleVariant : 'original';
                  const selectedSeries = renderableRole
                    ? isRegistrationSlot && isCenterRegistrationReady && registrationFixedSeries
                      ? registrationFixedSeries
                      : getSeriesByViewerRole(renderableRole, effectiveViewerVariant)
                    : null;
                  const registrationSeries =
                    isRegistrationSlot && isCenterRegistrationReady
                      ? registrationMovingSeries
                      : null;
                  const isVisible = visibleViewers[slot];
                  const showEditNotAlignedWarning =
                    effectiveViewerVariant !== 'aligned' && viewerEditNotAlignedWarning[slot];
                  const canEditViewer = Boolean(
                    selectedSeries?.niftiAsset &&
                      (selectedSeries.uploadId || (slot === 'left' && locationState?.uploadId)) &&
                      !isAtlasRole(renderableRole)
                  );

                  const boundaryAfterSlot =
                    slot === 'left'
                      ? RESIZE_BOUNDARIES[1]
                      : slot === 'center'
                        ? RESIZE_BOUNDARIES[2]
                        : null;

                  return (
                    <React.Fragment key={slot}>
                      <div
                        data-resizable-panel={slot}
                        className={`pc-viewer-slot pc-resizable-panel ${
                          panelWeights && isVisible ? 'pc-has-custom-size' : ''
                        } ${isVisible ? 'open' : 'closed'} pc-viewer-slot-${slot} ${
                          maximizedViewerSlot === slot ? 'pc-viewer-slot-maximized' : ''
                        }`}
                        style={getPanelResizeStyle(slot, isVisible)}
                      >
                        <ViewerStrip
                          slot={slot}
                          title={getViewerTitle(slot)}
                          isVisible={isVisible}
                          onToggle={() => toggleViewerSlot(slot)}
                          isMaximized={maximizedViewerSlot === slot}
                          onToggleMaximize={() => toggleViewerMaximize(slot)}
                          canEdit={canEditViewer}
                          selectedRole={selectedRole}
                          selectedVariant={selectedRoleVariant}
                          roleOptions={viewerRoleOptions}
                          onSelectAtlasContrast={atlasId => {
                            const atlas = getSelectedAtlasChoices(
                              atlasCatalog,
                              selectedAtlases.atlas
                            ).find(item => item.id === atlasId);
                            if (atlas) {
                              setSelectedAtlases({ atlas });
                              setSelectedSeriesByRole(current => ({
                                ...current,
                                atlas: `atlas:${atlasId}`,
                              }));
                              setToolUploads(current => ({
                                ...current,
                                atlas: {
                                  name: getAtlasDisplayName(atlas),
                                  count: 1,
                                  size: atlas.template_size_bytes,
                                  source: 'files',
                                },
                              }));
                            }
                          }}
                          onSelectRole={(role, variant) => updateViewerRole(slot, role, variant)}
                          secondaryRole={slot === 'center' ? centerSecondaryRole : undefined}
                          secondaryVariant={slot === 'center' ? centerSecondaryVariant : undefined}
                          secondaryRoleOptions={
                            slot === 'center' ? registrationMovingOptions : undefined
                          }
                          secondaryOptionKey={
                            slot === 'center' ? registrationMovingOptionKey : undefined
                          }
                          onSelectSecondaryRole={
                            slot === 'center'
                              ? (role, variant, option) => {
                                  setCenterSecondaryRole(role);
                                  setCenterSecondaryVariant(variant);
                                  setRegistrationMovingRole(role);
                                  setRegistrationMovingVariant(variant);
                                  setRegistrationMovingOptionKey(option?.key ?? null);
                                  setRegistrationMovingSeries(
                                    option?.sourceSeries ?? getSeriesByViewerRole(role, variant)
                                  );
                                }
                              : undefined
                          }
                          tertiaryRole={slot === 'center' ? centerTertiaryRole : undefined}
                          tertiaryVariant={slot === 'center' ? centerTertiaryVariant : undefined}
                          onSelectTertiaryRole={
                            slot === 'center'
                              ? (role, variant) => {
                                  setCenterTertiaryRole(role);
                                  setCenterTertiaryVariant(variant);
                                }
                              : undefined
                          }
                          showTertiaryRole={
                            slot === 'center' ? presetConfig.showCenterTertiaryRole : false
                          }
                          leftGridLayout={slot === 'left' ? leftViewerGrid : undefined}
                          onSelectLeftGridLayout={
                            slot === 'left' ? selectLeftViewerGridLayout : undefined
                          }
                          closeRoleMenuSignal={viewerInteractionSignal}
                          parcelizationControl={
                            slot === 'left' && isAtlasRole(renderableRole) ? (
                              <ParcelizationControl
                                isOpen={isParcelizationOpen && parcelizationOwnerSlot === slot}
                                isActive={isParcelizationActive}
                                catalogStatus={parcelizationCatalogStatus}
                                selectedOptions={selectedParcelizationOptions}
                                allOptions={allParcelizationOptions}
                                selectedIds={selectedParcellizations}
                                search={parcelizationSearch}
                                iconSrc={`${publicUrl}atlas-image.png`}
                                controlRef={
                                  isParcelizationOpen && parcelizationOwnerSlot === slot
                                    ? parcelizationControlRef
                                    : undefined
                                }
                                onToggle={() => toggleParcelizationForSlot(slot)}
                                onSetEnabled={setParcelizationEnabled}
                                onSearchChange={setParcelizationSearch}
                                onToggleSelection={toggleParcellizationSelection}
                              />
                            ) : undefined
                          }
                          parcelizationControlOpen={
                            slot === 'left' &&
                            isAtlasRole(renderableRole) &&
                            isParcelizationOpen &&
                            parcelizationOwnerSlot === slot
                          }
                          orientationControl={
                            slot !== 'center' && canEditViewer && selectedSeries ? (
                              <OrientationControl
                                title={getViewerTitle(slot)}
                                isOpen={orientationPopoverSlot === slot}
                                isOn={effectiveViewerVariant === 'aligned'}
                                isEditing={viewerEditModes[slot]}
                                showNotAlignedWarning={showEditNotAlignedWarning}
                                editRotationCount={viewerEditRotations[slot].length}
                                editSaveStatus={viewerEditSaveStatus[slot]}
                                editError={viewerEditErrors[slot]}
                                onToggle={() => toggleOrientationPopover(slot)}
                                onClose={() => setOrientationPopoverSlot(null)}
                                onSetEnabled={enabled => setViewerOrientationEnabled(slot, enabled)}
                                onSave={() => void saveViewerEdit(slot, selectedSeries)}
                                onReset={() => resetViewerEdit(slot)}
                                onCancel={() => setViewerOrientationEnabled(slot, false)}
                              />
                            ) : undefined
                          }
                          orientationControlOpen={
                            slot !== 'center' && orientationPopoverSlot === slot
                          }
                          onCloseOrientationControl={() => setOrientationPopoverSlot(null)}
                          registrationControl={
                            slot === 'center' ? (
                              <RegistrationControl
                                isOpen={isCenterRegistrationOpen}
                                accessToken={accessToken}
                                onToggle={() => setIsCenterRegistrationOpen(current => !current)}
                                onClose={() => setIsCenterRegistrationOpen(false)}
                                roleOptions={viewerRoleOptions}
                                selectedMovingRole={registrationMovingRole ?? undefined}
                                selectedMovingVariant={registrationMovingVariant}
                                selectedMovingOptionKey={registrationMovingOptionKey}
                                onReadyChange={setIsCenterRegistrationReady}
                                onMovingOptionsChange={setRegistrationMovingOptions}
                                onSelectFixed={(role, variant, option) => {
                                  setRegistrationFixedRole(role);
                                  setRegistrationFixedVariant(variant);
                                  const resolvedSeries = getSeriesByViewerRole(role, variant);
                                  setRegistrationFixedSeries(
                                    option?.sourceSeries ?? resolvedSeries ?? null
                                  );
                                }}
                                onSelectMoving={(role, variant, option) => {
                                  setCenterSecondaryRole(role);
                                  setCenterSecondaryVariant(variant);
                                  setRegistrationMovingRole(role);
                                  setRegistrationMovingVariant(variant);
                                  setRegistrationMovingOptionKey(option?.key ?? null);
                                  setRegistrationMovingSeries(
                                    option?.sourceSeries ?? getSeriesByViewerRole(role, variant)
                                  );
                                }}
                                onClearMoving={() => {
                                  setCenterSecondaryRole(presetConfig.centerSecondaryRole);
                                  setCenterSecondaryVariant('original');
                                  setRegistrationMovingRole(null);
                                  setRegistrationMovingVariant('original');
                                  setRegistrationMovingOptionKey(null);
                                  setRegistrationMovingSeries(null);
                                }}
                              />
                            ) : undefined
                          }
                          registrationControlOpen={slot === 'center' && isCenterRegistrationOpen}
                          registrationTools={slot === 'center'}
                          registrationReady={slot === 'center' && isCenterRegistrationReady}
                          registrationAlignmentEnabled={
                            slot === 'center' && isCenterRegistrationAlignmentOn
                          }
                          onRegistrationAlignmentEnabledChange={
                            slot === 'center' ? setIsCenterRegistrationAlignmentOn : undefined
                          }
                        />

                        {renderableRole ? (
                          <ViewerPanel
                            slot={slot}
                            series={selectedSeries}
                            selectedRole={renderableRole}
                            registrationSeries={registrationSeries}
                            registrationRole={
                              slot === 'center' ? (registrationMovingRole ?? undefined) : undefined
                            }
                            registrationVariant={
                              slot === 'center' ? registrationMovingVariant : undefined
                            }
                            registrationAlignmentEnabled={
                              slot === 'center' && isCenterRegistrationAlignmentOn
                            }
                            isVisible={isVisible}
                            isEditing={viewerEditModes[slot] && !isAtlasRole(renderableRole)}
                            editRotationCount={viewerEditRotations[slot].length}
                            reloadVersion={viewerReloadVersions[slot]}
                            fixedViewerLayout={fixedViewerLayout}
                            fixedViewerOrientationRequestVersion={
                              fixedViewerOrientationRequestVersion
                            }
                            fixedViewerPanResetRequest={fixedViewerPanResetRequest}
                            fixedDicomViewportState={
                              slot === 'left'
                                ? {
                                    openedFrom: selectedSeriesByRole.fixed
                                      ? locationState?.openedFrom
                                      : undefined,
                                    uploadId: selectedSeriesByRole.fixed
                                      ? locationState?.uploadId
                                      : undefined,
                                    fixedStudyInstanceUid: selectedSeriesByRole.fixed
                                      ? locationState?.fixedStudyInstanceUid
                                      : null,
                                    fixedSeriesInstanceUid: selectedSeriesByRole.fixed
                                      ? locationState?.fixedSeriesInstanceUid
                                      : null,
                                  }
                                : undefined
                            }
                            onSelectRole={role => updateViewerRole(slot, role)}
                            roleOptions={viewerRoleOptions}
                            leftGridLayout={leftViewerGrid}
                            onSelectLeftGridLayout={selectLeftViewerGridLayout}
                            onSliceThumbnail={handleProjectViewerSliceThumbnail}
                            onViewerInteraction={closeOpenDicomPopup}
                            onActiveView={
                              slot === 'left' ? handleProjectViewerActiveView : undefined
                            }
                            onRotation={rotation => handleViewerRotation(slot, rotation)}
                            // Parcelizations live in the LeadDBS/MNI atlas space.
                            // Do not attach those actors to patient MR/CT viewports;
                            // they belong only on the currently rendered atlas
                            // panel (the intended base image is the atlas T2w).
                            parcelizations={
                              isAtlasRole(renderableRole) ? activeParcelizationVolumes : []
                            }
                            parcelizationOnly={
                              slot === 'left' &&
                              isParcelization3DView &&
                              isAtlasRole(renderableRole)
                            }
                          />
                        ) : (
                          <div
                            className={`pc-workflow-panel pc-workflow-panel-${slot}`}
                            aria-label={`${getViewerTitle(slot)} workspace`}
                          >
                            <div className="pc-viewer-body">
                              {isVisible &&
                                (isRegistrationSlot ? (
                                  <div
                                    className="pc-registration-start-state"
                                    role="status"
                                  >
                                    Select Fixed and Move to start
                                  </div>
                                ) : (
                                  <ViewerEmptyState slot={slot} />
                                ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {slot === 'center' && (
                        <PanelResizeHandle
                          boundaryIndex={RESIZE_BOUNDARIES[2]}
                          isEnabled={canResizeBoundary(RESIZE_BOUNDARIES[2])}
                          isActive={activeResizeBoundary === RESIZE_BOUNDARIES[2]}
                          onPointerDown={event => startPanelResize(event, RESIZE_BOUNDARIES[2])}
                          onDoubleClick={resetPanelSizes}
                        />
                      )}

                      {slot === 'center' && (
                        <div
                          data-resizable-panel="parcelization"
                          className={`pc-parcelization-slot pc-resizable-panel ${isParcelizationPanelOpen ? 'open' : 'closed'} ${panelWeights && isParcelizationPanelOpen ? 'pc-has-custom-size' : ''}`}
                          style={getPanelResizeStyle('parcelization', isParcelizationPanelOpen)}
                          data-parcelization-panel="true"
                        >
                          <div className="pc-parcelization-strip">
                            <button
                              type="button"
                              className="pc-viewer-strip-toggle"
                              onClick={() => setIsParcelizationPanelOpen(current => !current)}
                              title={
                                isParcelizationPanelOpen
                                  ? 'Close Parcelization'
                                  : 'Open Parcelization'
                              }
                              aria-label={
                                isParcelizationPanelOpen
                                  ? 'Close Parcelization'
                                  : 'Open Parcelization'
                              }
                            >
                              {isParcelizationPanelOpen ? '−' : '+'}
                            </button>
                            <span
                              className="pc-viewer-strip-workflow-icon"
                              aria-hidden="true"
                            >
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={1.65}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M4 5.5h16M4 12h16M4 18.5h16" />
                                <circle
                                  cx="8"
                                  cy="5.5"
                                  r="1.2"
                                />
                                <circle
                                  cx="15"
                                  cy="12"
                                  r="1.2"
                                />
                                <circle
                                  cx="11"
                                  cy="18.5"
                                  r="1.2"
                                />
                              </svg>
                            </span>
                          </div>
                          {isParcelizationPanelOpen && (
                            <div
                              className="pc-parcelization-panel-empty"
                              aria-label="Parcelization workspace"
                            />
                          )}
                        </div>
                      )}

                      {boundaryAfterSlot !== null && slot !== 'center' && (
                        <PanelResizeHandle
                          boundaryIndex={boundaryAfterSlot}
                          isEnabled={canResizeBoundary(boundaryAfterSlot)}
                          isActive={activeResizeBoundary === boundaryAfterSlot}
                          onPointerDown={event => startPanelResize(event, boundaryAfterSlot)}
                          onDoubleClick={resetPanelSizes}
                        />
                      )}

                      {slot === 'center' && (
                        <PanelResizeHandle
                          boundaryIndex={RESIZE_BOUNDARIES[3]}
                          isEnabled={canResizeBoundary(RESIZE_BOUNDARIES[3])}
                          isActive={activeResizeBoundary === RESIZE_BOUNDARIES[3]}
                          onPointerDown={event => startPanelResize(event, RESIZE_BOUNDARIES[3])}
                          onDoubleClick={resetPanelSizes}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

/* ── Workspace panel resize handle ── */
interface PanelResizeHandleProps {
  boundaryIndex: number;
  isEnabled: boolean;
  isActive: boolean;
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
}

const PanelResizeHandle: React.FC<PanelResizeHandleProps> = ({
  boundaryIndex,
  isEnabled,
  isActive,
  onPointerDown,
  onDoubleClick,
}) => {
  return (
    <div
      className={`pc-panel-resize-handle ${
        isEnabled ? 'enabled' : 'disabled'
      } ${isActive ? 'active' : ''}`}
      role="separator"
      aria-label={`Resize workspace panels at boundary ${boundaryIndex + 1}`}
      aria-orientation="vertical"
      title={isEnabled ? 'Drag to resize panels · Double-click to reset' : undefined}
      onPointerDown={isEnabled ? onPointerDown : undefined}
      onDoubleClick={isEnabled ? onDoubleClick : undefined}
    >
      <span
        className="pc-panel-resize-line"
        aria-hidden="true"
      />
      <span
        className="pc-panel-resize-grip"
        aria-hidden="true"
      >
        <i />
        <i />
        <i />
      </span>
    </div>
  );
};

/* ── MR DICOM dropdown ── */
interface RoleDicomDropdownTriggerProps {
  role: DicomUploadRole;
  label: string;
  isPending?: boolean;
  isActive: boolean;
  isOpen: boolean;
  assetStatus: ViewerAssetStatus;
  isRemovable?: boolean;
  uploadedFile: ToolUploadMeta | null;
  uploadStatus: RoleDicomUploadStatus;
  uploadError: string | null;
  uploadId: string | null;
  token: string;
  isUploadRemovable: boolean;
  selectedSeriesId: string | null;
  seriesOptions: DicomSeries[];
  onToggle: () => void;
  onClose: () => void;
  onRemove?: () => void;
  onUpload: (files: FileList | null, source: 'files' | 'folder') => void;
  onCancelUpload: () => void;
  onConfirmSelection: (seriesId: string, variant: SeriesDisplayVariant) => void | Promise<void>;
  seriesTypeLabel?: string;
}

const RoleDicomDropdownTrigger: React.FC<RoleDicomDropdownTriggerProps> = ({
  role,
  label,
  isPending = false,
  isActive,
  isOpen,
  assetStatus,
  isRemovable = false,
  uploadedFile,
  uploadStatus,
  uploadError,
  uploadId,
  token,
  isUploadRemovable,
  selectedSeriesId,
  seriesOptions,
  onToggle,
  onClose,
  onRemove,
  onUpload,
  onCancelUpload,
  onConfirmSelection,
  seriesTypeLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [draftSelectedSeriesId, setDraftSelectedSeriesId] = useState<string | null>(null);
  const [inspectedSeriesId, setInspectedSeriesId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(isPending);
  const [isConfirming, setIsConfirming] = useState(false);
  const seriesOptionIds = seriesOptions.map(series => series.id).join('|');

  useEffect(() => {
    setIsEditing(isOpen && isPending);
  }, [isOpen, isPending]);

  useEffect(() => {
    if (!isOpen) return;

    const confirmedSeriesIsAvailable = seriesOptions.some(series => series.id === selectedSeriesId);
    const initialDraftSeries =
      (confirmedSeriesIsAvailable
        ? seriesOptions.find(series => series.id === selectedSeriesId)
        : undefined) ??
      (isCtRole(role)
        ? seriesOptions.find(series => series.isVolumeCandidate ?? series.eligibleForFixed)
        : undefined);

    setDraftSelectedSeriesId(initialDraftSeries?.id ?? null);
    setInspectedSeriesId(initialDraftSeries?.id ?? null);
    // seriesOptionIds is a stable content signature. Depending on the array
    // itself would reset the user's draft when a caller maps CT eligibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedSeriesId, seriesOptionIds]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing) {
      onUpload(event.target.files, 'files');
    }
    event.target.value = '';
  };

  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isEditing) {
      onUpload(event.target.files, 'folder');
    }
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isEditing) {
      onUpload(event.dataTransfer.files, 'files');
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUploadAreaClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  const handleConfirmSelection = async (requestedSeries?: DisplayDicomSeries) => {
    if (isConfirming || !isEditing) return;

    const requestedSeriesId = requestedSeries?.id ?? draftSelectedSeriesId;
    if (!requestedSeriesId) return;

    const draftSeries = seriesOptions.find(series => series.id === requestedSeriesId);
    if (
      !draftSeries ||
      !isSeriesCompatibleWithRole(draftSeries, role) ||
      !(draftSeries.isVolumeCandidate ?? draftSeries.eligibleForFixed)
    ) {
      return;
    }
    if (!isCtRole(role) && !draftSeries.eligibleForFixed) return;
    if (!isPending && requestedSeriesId === selectedSeriesId) {
      return;
    }

    setDraftSelectedSeriesId(requestedSeriesId);
    setInspectedSeriesId(requestedSeriesId);

    // MR roles are committed only after their NIfTI asset is ready. This keeps
    // the viewer dropdown from exposing a role that cannot render yet.

    setIsConfirming(true);
    try {
      await onConfirmSelection(requestedSeriesId, 'original');
    } finally {
      setIsConfirming(false);
    }
  };
  const resolvedUploadStatus = uploadedFile && uploadStatus === 'idle' ? 'success' : uploadStatus;
  return (
    <div
      className={`pc-tool-upload-item ${isCtRole(role) ? 'pc-ct-dicom-item' : 'pc-role-dicom-item'}`}
    >
      <button
        type="button"
        className={`pc-tool-upload-trigger status-${assetStatus} ${
          isPending ? 'pending-mr' : ''
        } ${selectedSeriesId ? 'selected-uploaded' : ''}`}
        onClick={onToggle}
        data-asset-status={assetStatus}
        data-viewer-active={isActive ? 'true' : 'false'}
        title={`${label}: ${assetStatus}`}
      >
        {isPending ? (
          <span className="pc-tool-upload-label">{label}</span>
        ) : (
          <>
            <span className="pc-tool-upload-dot" />
            <span className="pc-tool-upload-label">{label}</span>

            <svg
              className={`pc-tool-upload-chevron ${isOpen ? 'open' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 9l6 6 6-6"
              />
            </svg>
          </>
        )}
      </button>

      {isRemovable && (
        <button
          type="button"
          className="pc-role-dicom-remove"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          title={`Remove ${label}`}
        >
          ×
        </button>
      )}

      {isOpen && (
        <div
          className={`pc-role-dicom-popover pc-role-dicom-popover-${role}`}
          aria-label={`${label} DICOM series`}
        >
          <div className="pc-role-dicom-popover-actions">
            <span className="pc-role-dicom-popover-title">{isCtRole(role) ? '+ CT' : '+ MR'}</span>
            <button
              type="button"
              className={`pc-role-dicom-popover-edit ${isEditing ? 'active' : ''}`}
              onClick={() => {
                if (isConfirming) return;
                setIsEditing(current => !current);
              }}
              aria-label="Toggle series selection lock"
              aria-pressed={isEditing}
              disabled={isConfirming}
              title={isEditing ? 'Lock series selection' : 'Unlock series selection'}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="11"
                  rx="2"
                />
                <path
                  d={isEditing ? 'M8 10V6a4 4 0 018-1' : 'M8 10V6a4 4 0 018 0v4'}
                  strokeLinecap="round"
                />
                <path
                  d="M12 14v3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="pc-role-dicom-popover-close"
              onClick={onClose}
              aria-label="Close DICOM series window"
              title="Close"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6L6 18"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <div
            className={`pc-role-dicom-editor ${isEditing ? 'is-editing' : 'is-readonly'}`}
            data-readonly={!isEditing ? 'true' : 'false'}
          >
            <DicomSeriesPanel
              className="pc-role-series-panel"
              uploadedFile={uploadedFile}
              uploadStatus={resolvedUploadStatus}
              uploadProgress={uploadStatus === 'uploading' ? 100 : 0}
              uploadError={uploadError}
              uploadId={uploadId}
              token={token}
              seriesList={seriesOptions}
              selectedFixedSeriesKey={draftSelectedSeriesId}
              expandedSeriesKey={inspectedSeriesId}
              fileInputRef={fileInputRef}
              folderInputRef={folderInputRef}
              onSelectSeries={series => {
                if (isConfirming || !isSeriesCompatibleWithRole(series, role)) return;
                setDraftSelectedSeriesId(series.id);
                setInspectedSeriesId(series.id);
              }}
              onFileChange={handleFileChange}
              onFolderInputChange={handleFolderChange}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onUploadAreaClick={handleUploadAreaClick}
              onRemoveUpload={() => {
                if (isEditing && !isConfirming) {
                  if (!isPending && onRemove) onRemove();
                  else onCancelUpload();
                }
              }}
              isSelectionLocked={!isEditing}
              isCreateDisabled={isConfirming}
              isSavingProject={isConfirming}
              createError={null}
              actionMode="select-series"
              allowIneligibleSelection={isCtRole(role)}
              onConfirmSelection={handleConfirmSelection}
              confirmedSeriesKey={isPending ? null : selectedSeriesId}
              isUploadRemovable={isUploadRemovable}
              seriesTypeLabel={seriesTypeLabel}
              removeActionIcon="trash"
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface AtlasDropdownTriggerProps {
  label: string;
  isOpen: boolean;
  isActive: boolean;
  atlases: AtlasCatalogItem[];
  token?: string | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
  selectedAtlasId: string | null;
  selectedAtlas?: AtlasCatalogItem;
  onToggle: () => void;
  onSelect: (atlasId: string) => void;
  onRemove: () => void;
  onRetry: () => void;
}

const AtlasDropdownTrigger: React.FC<AtlasDropdownTriggerProps> = ({
  label,
  isOpen,
  isActive,
  atlases,
  token,
  status,
  error,
  selectedAtlasId,
  selectedAtlas,
  onToggle,
  onSelect,
  onRemove,
  onRetry,
}) => {
  const [isUnlocked, setIsUnlocked] = useState(!selectedAtlasId);
  useEffect(() => {
    setIsUnlocked(!selectedAtlasId);
  }, [isOpen, selectedAtlasId]);

  return (
    <div className="pc-tool-upload-item pc-atlas-catalog-item">
      <button
        type="button"
        className={`pc-tool-upload-trigger pc-atlas-add-btn status-${selectedAtlasId ? 'loaded' : 'missing'} ${selectedAtlasId ? 'selected-uploaded' : ''}`}
        onClick={onToggle}
        aria-label="Open Atlas selection"
        aria-expanded={isOpen}
        data-viewer-active={isActive ? 'true' : 'false'}
        title={selectedAtlasId ? `Change Brain Template: ${label}` : 'Select Brain Template'}
      >
        {selectedAtlasId && <span className="pc-tool-upload-dot" />}
        {selectedAtlas?.contrast && (
          <span className="pc-tool-upload-label pc-tool-upload-contrast">
            {selectedAtlas.contrast}
          </span>
        )}
        {(!selectedAtlasId || !selectedAtlas?.contrast) && (
          <span className="pc-tool-upload-label">{label}</span>
        )}
        <svg
          className={`pc-tool-upload-chevron ${isOpen ? 'open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 9l6 6 6-6"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="pc-role-dicom-popover pc-atlas-catalog-popover"
          role="dialog"
          aria-label="Atlas selection"
        >
          <div className="pc-role-dicom-popover-actions">
            <span className="pc-role-dicom-popover-title">+ Brain Template</span>
            <button
              type="button"
              className={`pc-role-dicom-popover-edit ${isUnlocked ? 'active' : ''}`}
              aria-label="Toggle Brain Template selection lock"
              aria-pressed={isUnlocked}
              title={isUnlocked ? 'Lock template selection' : 'Unlock template selection'}
              onClick={() => setIsUnlocked(current => !current)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <rect
                  x="5"
                  y="10"
                  width="14"
                  height="11"
                  rx="2"
                />
                <path
                  d={isUnlocked ? 'M8 10V6a4 4 0 018-1' : 'M8 10V6a4 4 0 018 0v4'}
                  strokeLinecap="round"
                />
                <path
                  d="M12 14v3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="pc-role-dicom-popover-close"
              onClick={onToggle}
              aria-label="Close Atlas selection"
              title="Close"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6L6 18"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <div className={`pc-role-dicom-editor ${isUnlocked ? 'is-editing' : 'is-readonly'}`}>
            <section
              className="series-panel pc-role-series-panel pc-atlas-series-panel"
              aria-label="Atlas library"
            >
              {selectedAtlas && (
                <div className="series-upload-block pc-selected-atlas-summary">
                  <div
                    className="uploaded-file-card uploaded-file-card-right"
                    aria-label="Selected atlas"
                  >
                    <div className="uploaded-file-info">
                      <p
                        className="uploaded-file-name"
                        title={getAtlasFamilyTitle(getAtlasFamily(selectedAtlas))}
                      >
                        {getAtlasFamilyTitle(getAtlasFamily(selectedAtlas))}
                      </p>
                      <p className="uploaded-file-size">ATLAS · Selected</p>
                    </div>
                    <button
                      type="button"
                      className="uploaded-file-remove"
                      aria-label="Remove selected atlas"
                      title="Remove selected atlas"
                      disabled={!isUnlocked}
                      onClick={() => {
                        if (isUnlocked) onRemove();
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 7h16M9 7V4h6v3M6.5 7l.75 13h9.5l.75-13M10 11v5M14 11v5"
                          strokeWidth={2}
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              {(status === 'idle' || status === 'loading') && (
                <div
                  className="pc-atlas-catalog-state"
                  role="status"
                >
                  <span className="modal-submit-spinner" />
                  <span>Loading atlas library</span>
                </div>
              )}

              {status === 'error' && (
                <div className="pc-atlas-catalog-state error">
                  <span>{error ?? 'Atlas catalog could not be loaded.'}</span>
                  <button
                    type="button"
                    onClick={onRetry}
                  >
                    Retry
                  </button>
                </div>
              )}

              {status === 'success' && atlases.length === 0 && (
                <div
                  className="pc-atlas-catalog-state"
                  role="status"
                >
                  No atlases available
                </div>
              )}

              {status === 'success' && atlases.length > 0 && (
                <div
                  className="dicom-series-list pc-atlas-catalog-list"
                  aria-label="Available atlases"
                >
                  <AtlasSelectionSections
                    atlases={atlases}
                    token={token}
                    selectedAtlasId={selectedAtlasId}
                    selectionLocked={!isUnlocked}
                    onSelect={atlasId => {
                      if (isUnlocked) onSelect(atlasId);
                    }}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Atlas simple upload trigger ── */
interface SimpleToolUploadTriggerProps {
  tool: {
    id: ToolMode;
    label: string;
    accent: string;
  };
  isActive: boolean;
  isOpen: boolean;
  assetStatus: ViewerAssetStatus;
  uploadMeta: ToolUploadMeta | null;
  onToggle: () => void;
  onUpload: (files: FileList | null, source: 'files' | 'folder') => void;
  formatUploadSize: (bytes: number) => string;
}

const SimpleToolUploadTrigger: React.FC<SimpleToolUploadTriggerProps> = ({
  tool,
  isActive,
  isOpen,
  assetStatus,
  uploadMeta,
  onToggle,
  onUpload,
  formatUploadSize,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpload(event.target.files, 'files');
    event.target.value = '';
  };

  const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onUpload(event.target.files, 'folder');
    event.target.value = '';
  };

  const isNiftiTool = tool.id === 'nifti';
  const uploadSectionLabel = isNiftiTool ? 'NIfTI Upload' : 'DICOM Upload';
  const fileAccept = isNiftiTool
    ? '.nii,.nii.gz,application/gzip,application/octet-stream'
    : undefined;

  return (
    <div className="pc-tool-upload-item">
      <button
        type="button"
        className={`pc-tool-upload-trigger status-${assetStatus} ${isActive ? 'active' : ''}`}
        onClick={onToggle}
        data-asset-status={assetStatus}
        title={`${tool.label}: ${assetStatus}`}
      >
        <span className="pc-tool-upload-dot" />

        <span className="pc-tool-upload-label">{tool.label}</span>

        <svg
          className={`pc-tool-upload-chevron ${isOpen ? 'open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 9l6 6 6-6"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="pc-tool-upload-popover">
          <div className="pc-tool-upload-popover-header">
            <span
              className="pc-tool-upload-popover-dot"
              style={{ backgroundColor: tool.accent }}
            />
            <span className="pc-tool-upload-popover-title">{tool.label}</span>
          </div>

          {uploadMeta && (
            <div className="pc-tool-upload-current">
              <span className="pc-tool-upload-current-name">{uploadMeta.name}</span>
              <span className="pc-tool-upload-current-meta">
                {uploadMeta.count} item{uploadMeta.count > 1 ? 's' : ''} ·{' '}
                {formatUploadSize(uploadMeta.size)}
              </span>
            </div>
          )}

          <p className="pc-tool-upload-section-label">{uploadSectionLabel}</p>

          <div className="pc-tool-upload-actions">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={fileAccept}
              onChange={handleFilesChange}
            />

            <input
              ref={folderInputRef}
              type="file"
              multiple
              hidden
              {...({
                webkitdirectory: '',
                directory: '',
              } as React.InputHTMLAttributes<HTMLInputElement> & {
                webkitdirectory?: string;
                directory?: string;
              })}
              onChange={handleFolderChange}
            />

            <button
              type="button"
              className="pc-tool-upload-action primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 3h7l5 5v13H7V3z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14 3v5h5"
                />
              </svg>
              Select Files
            </button>

            <button
              type="button"
              className="pc-tool-upload-action"
              onClick={() => folderInputRef.current?.click()}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                />
              </svg>
              Select Folder
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface OrientationControlProps {
  title: string;
  isOpen: boolean;
  isOn: boolean;
  isEditing: boolean;
  showNotAlignedWarning: boolean;
  editRotationCount: number;
  editSaveStatus: ViewerEditSaveStatus;
  editError?: string | null;
  onToggle: () => void;
  onClose: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onSave: () => void;
  onReset: () => void;
  onCancel: () => void;
}

const OrientationControl: React.FC<OrientationControlProps> = ({
  title,
  isOpen,
  isOn,
  isEditing,
  showNotAlignedWarning,
  editRotationCount,
  editSaveStatus,
  editError,
  onToggle,
  onClose,
  onSetEnabled,
  onSave,
  onReset,
  onCancel,
}) => {
  const hasChanges = editRotationCount > 0;
  const isSaving = editSaveStatus === 'saving';

  return (
    <div className="pc-orientation-control">
      <button
        type="button"
        className={
          'pc-viewer-strip-edit' + (isOn ? ' active' : '') + (isEditing ? ' is-editing' : '')
        }
        onClick={onToggle}
        title={`Orientation settings for ${title}`}
        aria-label={'Edit ' + title}
        aria-pressed={isOn}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-orientation={isOn ? 'oriented' : 'original'}
      >
        <Icons.ByName
          name="OrientationSwitch"
          className="pc-viewer-orientation-icon"
        />
      </button>

      {isOpen && (
        <div
          className={`pc-orientation-popover ${editError ? 'has-error' : ''}`}
          role="dialog"
          aria-label={`${title} Orientation settings`}
          aria-busy={isSaving}
        >
          <div
            className="pc-orientation-segmented-toggle"
            role="group"
            aria-label="Orientation mode"
          >
            <button
              type="button"
              className={isOn ? 'active' : ''}
              aria-pressed={isOn}
              aria-label="Orientation toggle"
              disabled={isSaving}
              onClick={() => onSetEnabled(!isOn)}
            >
              {isOn ? 'On' : 'Off'}
            </button>
          </div>

          {(showNotAlignedWarning || editError) && (
            <div
              className={`pc-orientation-status ${editError ? 'error' : ''}`}
              role={editError ? 'alert' : 'status'}
            >
              {editError ?? "This series isn't oriented yet."}
            </div>
          )}

          <div
            className="pc-viewer-edit-actions pc-orientation-actions"
            role="group"
            aria-label="Edit mode actions"
          >
            <button
              type="button"
              className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-save"
              disabled={isSaving || !isEditing || !hasChanges}
              onClick={onSave}
              aria-label="Save Orientation changes"
              title="Save"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  d="M5 3.5h11l3 3V20.5H5z"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 3.5v5h8v-5M8.5 20.5v-5h7v5"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-reset"
              disabled={isSaving}
              onClick={onReset}
              aria-label="Reset Orientation changes"
              title="Reset"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.9}
                aria-hidden="true"
              >
                <path
                  d="M4.8 9A7.5 7.5 0 112 14"
                  strokeLinecap="round"
                />
                <path
                  d="M4.8 4.5V9H9.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-cancel"
              disabled={isSaving || !isEditing || !hasChanges}
              onClick={() => {
                onCancel();
                onClose();
              }}
              aria-label="Cancel Orientation editing"
              title="Cancel"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path
                  d="M6 6l12 12M18 6L6 18"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const getRegistrationSourceThumbnail = (option: ViewerRoleOption) => {
  if (option.thumbnailSrc) return option.thumbnailSrc;
  if (isCtRole(option.id)) return series3DThumbnail;
  if (isAtlasRole(option.id)) return seriesCoronalThumbnail;
  return seriesAxialThumbnail;
};

const RegistrationThumbnail = ({
  src,
  alt = '',
  variant,
  series,
  token,
}: {
  src: string;
  alt?: string;
  variant: 'source' | 'drop' | 'assigned';
  series?: ViewerRoleOption['thumbnailSeries'];
  token?: string | null;
}) => {
  const canResolveUploadedThumbnail = Boolean(
    series?.uploadId && token && series.studyInstanceUid && series.seriesInstanceUid
  );

  return (
    <div className={`pc-registration-thumb-frame pc-registration-thumb-frame-${variant}`}>
      {canResolveUploadedThumbnail ? (
        <DicomSeriesThumbnail
          uploadId={series.uploadId!}
          studyInstanceUid={series.studyInstanceUid}
          seriesInstanceUid={series.seriesInstanceUid}
          token={token!}
          label={series.label}
          thumbnailDataUrls={series.thumbnailDataUrls}
        />
      ) : (
        <img
          className={`pc-registration-${variant}-thumb`}
          src={src}
          alt={alt}
          draggable={false}
        />
      )}
    </div>
  );
};

interface RegistrationControlProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isProxy?: boolean;
  accessToken?: string | null;
  roleOptions?: ViewerRoleOption[];
  selectedMovingRole?: ViewerRole;
  selectedMovingVariant?: SeriesDisplayVariant;
  selectedMovingOptionKey?: string | null;
  onSelectFixed?: (
    role: ViewerRole,
    variant: SeriesDisplayVariant,
    option?: ViewerRoleOption
  ) => void;
  onSelectMoving?: (
    role: ViewerRole,
    variant: SeriesDisplayVariant,
    option?: ViewerRoleOption
  ) => void;
  onClearMoving?: () => void;
  onReadyChange?: (ready: boolean) => void;
  onMovingOptionsChange?: (options: ViewerRoleOption[]) => void;
}

/** Registration entry point for the center panel.
 *
 * The fixed and moving source selectors remain the first two controls. The
 * registration workflow gets its own control so opening its panel never
 * changes which image the center viewport is displaying.
 */
const RegistrationControl: React.FC<RegistrationControlProps> = ({
  isOpen,
  onToggle,
  onClose,
  isProxy = false,
  accessToken = null,
  roleOptions = [],
  selectedMovingRole,
  selectedMovingVariant = 'original',
  selectedMovingOptionKey = null,
  onSelectFixed,
  onSelectMoving,
  onClearMoving,
  onReadyChange,
  onMovingOptionsChange,
}) => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const [selectedSourceKey, setSelectedSourceKey] = useState<string | null>(null);
  const [activeDropTarget, setActiveDropTarget] = useState<'fixed' | 'moving' | null>(null);
  // Registration targets are deliberately independent from the viewer's
  // preset roles. A newly opened registration panel starts with two empty
  // targets so a preset cannot silently assign the same T1w source to both.
  const [assignedFixedKey, setAssignedFixedKey] = useState<string | null>(null);
  const [assignedMovingKeys, setAssignedMovingKeys] = useState<string[]>([]);
  const [activeMovingKey, setActiveMovingKey] = useState<string | null>(null);
  // Keep the assigned option objects alongside their keys. The role option
  // list can be rebuilt when the center viewer changes (for example after a
  // Move source is selected); resolving only through the new list could make
  // an already assigned Fixed source disappear from the drop zone.
  const [assignedOptionByKey, setAssignedOptionByKey] = useState<Record<string, ViewerRoleOption>>(
    {}
  );

  useEffect(() => {
    if (!isOpen || isProxy) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!controlRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isProxy, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedSourceKey(null);
      setActiveDropTarget(null);
    }
  }, [isOpen]);

  const resolveAssignedOption = useCallback(
    (key: string) => roleOptions.find(option => option.key === key) ?? assignedOptionByKey[key],
    [assignedOptionByKey, roleOptions]
  );

  // Patient sources can be selected here only after they have been dropped in
  // Move. Brain Template is the exception: its MR/CT choice belongs to the
  // strip's Selection control, so choosing one there adds/replaces the active
  // template without routing that decision back through this dialog.
  useEffect(() => {
    if (!selectedMovingRole) return;
    const selectedOption = roleOptions.find(
      option =>
        (selectedMovingOptionKey
          ? option.key === selectedMovingOptionKey
          : option.id === selectedMovingRole && option.variant === selectedMovingVariant) &&
        (isAtlasRole(option.id) || assignedMovingKeys.includes(option.key))
    );
    if (!selectedOption) return;

    if (isAtlasRole(selectedOption.id) && !assignedMovingKeys.includes(selectedOption.key)) {
      setAssignedOptionByKey(current =>
        current[selectedOption.key] === selectedOption
          ? current
          : { ...current, [selectedOption.key]: selectedOption }
      );
      setAssignedMovingKeys(current => {
        const selectedTemplateIsCt = isAtlasCtOption(selectedOption);
        const withoutPreviousTemplate = current.filter(key => {
          const currentOption = resolveAssignedOption(key);
          return (
            !currentOption ||
            !isAtlasRole(currentOption.id) ||
            isAtlasCtOption(currentOption) !== selectedTemplateIsCt
          );
        });
        const next = [...withoutPreviousTemplate, selectedOption.key];
        return next.length === current.length && next.every((key, index) => key === current[index])
          ? current
          : next;
      });
    }
    setActiveMovingKey(selectedOption.key);
  }, [
    assignedMovingKeys,
    resolveAssignedOption,
    roleOptions,
    selectedMovingOptionKey,
    selectedMovingRole,
    selectedMovingVariant,
  ]);

  const isRegistrationReady = Boolean(assignedFixedKey && assignedMovingKeys.length > 0);
  useEffect(() => {
    onReadyChange?.(isRegistrationReady);
  }, [isRegistrationReady, onReadyChange]);

  useEffect(() => {
    const assignedOptions = assignedMovingKeys
      .map(resolveAssignedOption)
      .filter((option): option is ViewerRoleOption => Boolean(option));
    const optionByKey = new Map(
      assignedOptions
        .filter(option => !isAtlasRole(option.id))
        .map(option => [option.key, option] as const)
    );

    // A generic Brain Template MR/CT card is assigned in Registration. Its
    // concrete MR contrast (T1w/T2w) is intentionally selected only here.
    const assignedTemplateKinds = new Set(
      assignedOptions
        .filter(option => isAtlasRole(option.id))
        .map(option => (isAtlasCtOption(option) ? 'ct' : 'mr'))
    );
    roleOptions
      .filter(
        option =>
          isAtlasRole(option.id) && assignedTemplateKinds.has(isAtlasCtOption(option) ? 'ct' : 'mr')
      )
      .forEach(option => {
        optionByKey.set(option.key, option);
      });
    onMovingOptionsChange?.([...optionByKey.values()]);
  }, [assignedMovingKeys, onMovingOptionsChange, resolveAssignedOption, roleOptions]);

  // Assigned sources live in exactly one side of the chooser. Once an item is
  // dropped into Fixed or Move, remove its duplicate from the left catalog;
  // replacing/removing the assignment naturally makes it available again.
  const assignedSourceKeys = new Set(assignedMovingKeys);
  if (assignedFixedKey) assignedSourceKeys.add(assignedFixedKey);
  const availableRoleOptions = roleOptions.filter(option => !assignedSourceKeys.has(option.key));
  const assignedAtlasOptions = [...assignedSourceKeys]
    .map(resolveAssignedOption)
    .filter((option): option is ViewerRoleOption => Boolean(option && isAtlasRole(option.id)));
  const atlasMrOptions = roleOptions.filter(
    option => isAtlasRole(option.id) && !isAtlasCtOption(option)
  );
  const atlasCtOptions = roleOptions.filter(option => isAtlasCtOption(option));
  const defaultAtlasMrOption =
    atlasMrOptions.find(option => option.isCurrentAtlas) ??
    atlasMrOptions.find(option => option.atlasTag?.toUpperCase().startsWith('T1W')) ??
    atlasMrOptions[0];
  const defaultAtlasCtOption =
    atlasCtOptions.find(option => option.isCurrentAtlas) ?? atlasCtOptions[0];
  const hasAssignedAtlasMr = assignedAtlasOptions.some(option => !isAtlasCtOption(option));
  const hasAssignedAtlasCt = assignedAtlasOptions.some(option => isAtlasCtOption(option));

  const sourceGroups = [
    {
      key: 'mr',
      label: 'MR',
      options: availableRoleOptions.filter(
        option => !isAtlasRole(option.id) && !isCtRole(option.id)
      ),
    },
    {
      key: 'atlas',
      label: 'Brain Template',
      options: [
        !hasAssignedAtlasMr ? defaultAtlasMrOption : null,
        !hasAssignedAtlasCt ? defaultAtlasCtOption : null,
      ].filter((option): option is ViewerRoleOption => Boolean(option)),
    },
    {
      key: 'ct',
      label: 'CT',
      options: availableRoleOptions.filter(option => isCtRole(option.id)),
    },
  ];

  const formatSourceLabel = (option?: ViewerRoleOption | null) => {
    if (!option) return 'Drop an image here';
    if (isAtlasRole(option.id)) {
      return [option.label, isAtlasCtOption(option) ? 'CT' : 'MR'].filter(Boolean).join(' · ');
    }
    const variantLabel = isAtlasRole(option.id)
      ? ''
      : getNiftiAlignmentLabel({
          variant: option.variant,
          alignment_mode: option.alignmentMode,
        });
    return [option.label, option.atlasTag, variantLabel].filter(Boolean).join(' · ');
  };

  const assignSource = (target: 'fixed' | 'moving', option: ViewerRoleOption) => {
    setAssignedOptionByKey(current => ({ ...current, [option.key]: option }));
    if (target === 'fixed') {
      setAssignedFixedKey(option.key);
      onSelectFixed?.(option.id, option.variant, option);
    } else {
      setAssignedMovingKeys(current =>
        current.includes(option.key) ? current : [...current, option.key]
      );
      setActiveMovingKey(option.key);
      onSelectMoving?.(option.id, option.variant, option);
    }
    setSelectedSourceKey(null);
    setActiveDropTarget(null);
  };

  const resolveDraggedOption = (event: React.DragEvent<HTMLDivElement>) => {
    const key = event.dataTransfer.getData('text/plain') || selectedSourceKey;
    return roleOptions.find(option => option.key === key) ?? null;
  };

  const renderDropTarget = (target: 'fixed' | 'moving') => {
    const assignedOptions =
      target === 'fixed'
        ? assignedFixedKey
          ? [resolveAssignedOption(assignedFixedKey)].filter((option): option is ViewerRoleOption =>
              Boolean(option)
            )
          : []
        : assignedMovingKeys
            .map(resolveAssignedOption)
            .filter((option): option is ViewerRoleOption => Boolean(option));
    const selectedOption =
      target === 'fixed'
        ? (assignedOptions[0] ?? null)
        : (assignedOptions.find(option => option.key === activeMovingKey) ??
          assignedOptions.at(-1) ??
          null);
    const targetLabel = target === 'fixed' ? 'Fixed' : 'Move';
    const isOver = activeDropTarget === target;
    const selectedPendingOption = roleOptions.find(option => option.key === selectedSourceKey);

    const removeMovingOption = (option: ViewerRoleOption) => {
      const remaining = assignedOptions.filter(item => item.key !== option.key);
      setAssignedMovingKeys(current => current.filter(key => key !== option.key));
      setAssignedOptionByKey(current => {
        if (assignedFixedKey !== option.key) {
          const next = { ...current };
          delete next[option.key];
          return next;
        }
        return current;
      });
      if (activeMovingKey === option.key) {
        const nextActive = remaining.at(-1) ?? null;
        setActiveMovingKey(nextActive?.key ?? null);
        if (nextActive) onSelectMoving?.(nextActive.id, nextActive.variant, nextActive);
        else onClearMoving?.();
      }
    };

    return (
      <div
        className={`pc-registration-drop-zone ${isOver ? 'is-over' : ''} ${
          selectedOption ? 'has-selection' : ''
        }`}
        onDragEnter={event => {
          event.preventDefault();
          setActiveDropTarget(target);
        }}
        onDragOver={event => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
          setActiveDropTarget(target);
        }}
        onDragLeave={event => {
          if (event.currentTarget === event.target) setActiveDropTarget(null);
        }}
        onDrop={event => {
          event.preventDefault();
          const option = resolveDraggedOption(event);
          if (option) assignSource(target, option);
          else setActiveDropTarget(null);
        }}
        onClick={() => {
          if (selectedPendingOption) assignSource(target, selectedPendingOption);
        }}
        role="button"
        tabIndex={0}
        aria-label={`${targetLabel} image drop area`}
        onKeyDown={event => {
          if ((event.key === 'Enter' || event.key === ' ') && selectedPendingOption) {
            event.preventDefault();
            assignSource(target, selectedPendingOption);
          }
        }}
      >
        <div className="pc-registration-drop-zone-header">
          <span
            className="pc-registration-drop-zone-icon"
            aria-hidden="true"
          >
            {target === 'fixed' ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              >
                <path d="M8 4h8M9 4v5l-3 3h12l-3-3V4M12 12v8M9 20h6" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
              >
                <path d="M12 3v18M3 12h18" />
                <path d="m9 6 3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
              </svg>
            )}
          </span>
          <span className="pc-registration-drop-zone-title">{targetLabel}</span>
        </div>
        {target === 'moving' && assignedOptions.length > 0 ? (
          <div className="pc-registration-assigned-list">
            {assignedOptions.map(option => (
              <div
                className={`pc-registration-assigned-item ${option.key === selectedOption?.key ? 'active' : ''}`}
                key={option.key}
                onClick={() => {
                  setActiveMovingKey(option.key);
                  onSelectMoving?.(option.id, option.variant, option);
                }}
              >
                <RegistrationThumbnail
                  src={getRegistrationSourceThumbnail(option)}
                  variant="assigned"
                  series={option.thumbnailSeries}
                  token={accessToken}
                />
                <span className="pc-registration-assigned-label">{formatSourceLabel(option)}</span>
                <button
                  type="button"
                  className="pc-registration-assigned-remove"
                  aria-label={`Remove ${formatSourceLabel(option)} from Move`}
                  onClick={event => {
                    event.stopPropagation();
                    removeMovingOption(option);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : selectedOption ? (
          <div className="pc-registration-drop-zone-preview">
            <RegistrationThumbnail
              src={getRegistrationSourceThumbnail(selectedOption)}
              variant="drop"
              series={selectedOption.thumbnailSeries}
              token={accessToken}
            />
            <span className="pc-registration-drop-zone-value">
              {formatSourceLabel(selectedOption)}
            </span>
          </div>
        ) : (
          <span className="pc-registration-drop-zone-value empty">Drop an image here</span>
        )}
        <span className="pc-registration-drop-zone-hint">
          {target === 'moving'
            ? 'Drag sources here; select the active one from Move'
            : selectedOption
              ? 'Drop a different source to replace it'
              : 'Drag a source from the left'}
        </span>
      </div>
    );
  };

  return (
    <div
      className="pc-registration-control"
      ref={controlRef}
    >
      <button
        type="button"
        className={`pc-viewer-strip-edit pc-registration-btn ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        aria-label="Registration"
        title="Registration"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.65}
          aria-hidden="true"
        >
          <circle
            cx="9"
            cy="12"
            r="5.2"
          />
          <circle
            cx="15"
            cy="12"
            r="5.2"
          />
          <path
            d="M12 8.5v7M8.5 12h7"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {isOpen && !isProxy && (
        <div
          className="pc-registration-popover"
          role="dialog"
          aria-label="Registration settings"
        >
          <div className="pc-registration-popover-heading">
            <div className="pc-registration-popover-title">Registration</div>
          </div>
          <div className="pc-registration-popover-content">
            <div className="pc-registration-source-panel">
              <div className="pc-registration-source-list">
                {sourceGroups.map((group, groupIndex) => (
                  <div
                    className="pc-registration-source-group"
                    key={group.key}
                  >
                    {groupIndex > 0 && <span className="pc-registration-source-separator" />}
                    <div className="pc-registration-source-group-title">{group.label}</div>
                    {group.options.length === 0 ? (
                      <div className="pc-registration-source-empty">No loaded source</div>
                    ) : (
                      group.options.map(option => {
                        const isSelected = selectedSourceKey === option.key;
                        return (
                          <button
                            type="button"
                            key={option.key}
                            className={`pc-registration-source-item ${isSelected ? 'selected' : ''}`}
                            draggable
                            onDragStart={event => {
                              setSelectedSourceKey(option.key);
                              event.dataTransfer.effectAllowed = 'copy';
                              event.dataTransfer.setData('text/plain', option.key);
                            }}
                            onDragEnd={() => setActiveDropTarget(null)}
                            onClick={() => {
                              if (activeDropTarget) assignSource(activeDropTarget, option);
                              else
                                setSelectedSourceKey(current =>
                                  current === option.key ? null : option.key
                                );
                            }}
                            title={`${formatSourceLabel(option)} — click to select, drag to assign`}
                            aria-pressed={isSelected}
                          >
                            <RegistrationThumbnail
                              src={getRegistrationSourceThumbnail(option)}
                              variant="source"
                              series={option.thumbnailSeries}
                              token={accessToken}
                            />
                            <span
                              className="pc-registration-source-grip"
                              aria-hidden="true"
                            >
                              ⋮⋮
                            </span>
                            <span className="pc-registration-source-item-label">
                              {formatSourceLabel(option)}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="pc-registration-target-panel">
              {renderDropTarget('fixed')}
              {renderDropTarget('moving')}
            </div>
          </div>
          <div className="pc-registration-popover-footer">
            <button
              type="button"
              onClick={onClose}
            >
              Registration
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface ParcelizationControlProps {
  isOpen: boolean;
  isActive: boolean;
  catalogStatus: 'idle' | 'loading' | 'success' | 'error';
  selectedOptions: ParcelizationCatalogItem[];
  allOptions: ParcelizationCatalogItem[];
  selectedIds: Set<string>;
  search: string;
  iconSrc: string;
  controlRef?: React.Ref<HTMLDivElement>;
  onToggle: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onSearchChange: (value: string) => void;
  onToggleSelection: (id: string) => void;
}

const ParcelizationControl: React.FC<ParcelizationControlProps> = ({
  isOpen,
  isActive,
  catalogStatus,
  selectedOptions,
  allOptions,
  selectedIds,
  search,
  iconSrc,
  controlRef,
  onToggle,
  onSetEnabled,
  onSearchChange,
  onToggleSelection,
}) => {
  const renderOption = (option: ParcelizationCatalogItem) => {
    const isChecked = selectedIds.has(option.id);
    return (
      <label
        key={option.id}
        className={`pc-parcellization-option ${isChecked ? 'active' : ''} ${!isActive ? 'disabled' : ''}`}
      >
        <span className="pc-parcellization-option-label">
          <span
            className="pc-parcellization-color"
            style={{ backgroundColor: `rgb(${option.color.join(',')})` }}
            aria-hidden="true"
          />
          {option.label}
        </span>
        <input
          type="checkbox"
          checked={isChecked}
          disabled={!isActive}
          onChange={() => onToggleSelection(option.id)}
        />
      </label>
    );
  };

  return (
    <div
      className="pc-parcelization-control pc-strip-parcelization-control"
      ref={controlRef}
    >
      <button
        type="button"
        className={`pc-viewer-strip-edit pc-parcelization-btn pc-atlas-image-btn ${isActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
        aria-pressed={isActive}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={onToggle}
        aria-label="Parcelization"
        title="Parcelization"
      >
        <img
          className="pc-atlas-image-icon"
          src={iconSrc}
          alt=""
          draggable={false}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className="pc-parcelization-popover"
          role="dialog"
          aria-label="Parcelization settings"
        >
          <div className="pc-parcelization-toggle-row">
            <div
              className="pc-orientation-segmented-toggle"
              role="group"
              aria-label="Parcelization visibility"
            >
              <button
                type="button"
                className={isActive ? 'active' : ''}
                aria-pressed={isActive}
                aria-label="Parcelization toggle"
                onClick={() => onSetEnabled(!isActive)}
              >
                {isActive ? 'On' : 'Off'}
              </button>
            </div>
          </div>

          <div className="pc-parcellization-search-wrap">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden="true"
            >
              <circle
                cx="11"
                cy="11"
                r="6.5"
              />
              <path
                d="m16 16 4 4"
                strokeLinecap="round"
              />
            </svg>
            <input
              type="search"
              value={search}
              onChange={event => onSearchChange(event.target.value)}
              placeholder="Search"
              aria-label="Search parcellizations"
            />
          </div>

          {catalogStatus === 'loading' && (
            <div
              className="pc-parcellization-catalog-status"
              role="status"
            >
              Loading LeadDBS data…
            </div>
          )}
          {catalogStatus === 'error' && (
            <div
              className="pc-parcellization-catalog-status error"
              role="alert"
            >
              LeadDBS data could not be loaded.
            </div>
          )}

          <div className="pc-parcellization-options-scroll">
            <section
              className="pc-parcellization-section pc-parcellization-selected-section"
              aria-label="Selected parcellizations"
            >
              <div className="pc-parcellization-section-heading">
                <span>Selected</span>
                <span>{selectedOptions.length}</span>
              </div>
              <div className="pc-parcellization-list">
                {selectedOptions.length > 0 ? (
                  selectedOptions.map(renderOption)
                ) : (
                  <span className="pc-parcellization-empty">No selected items</span>
                )}
              </div>
            </section>

            <section
              className="pc-parcellization-section pc-parcellization-all-section"
              aria-label="All parcellizations"
            >
              <div className="pc-parcellization-section-heading">
                <span>All</span>
                <span>{allOptions.length}</span>
              </div>
              <div className="pc-parcellization-list">
                {allOptions.length > 0 ? (
                  allOptions.map(renderOption)
                ) : (
                  <span className="pc-parcellization-empty">No matching items</span>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Viewer strip ── */
interface ViewerStripProps {
  slot: ViewerSlot;
  title: string;
  isVisible: boolean;
  onToggle: () => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
  canEdit: boolean;
  selectedRole: ViewerRole;
  selectedVariant: SeriesDisplayVariant;
  roleOptions: ViewerRoleOption[];
  onSelectAtlasContrast?: (atlasId: string) => void;
  onSelectRole: (role: ViewerRole, variant: SeriesDisplayVariant) => void;
  secondaryRole?: ViewerRole;
  secondaryVariant?: SeriesDisplayVariant;
  secondaryRoleOptions?: ViewerRoleOption[];
  secondaryOptionKey?: string;
  onSelectSecondaryRole?: (
    role: ViewerRole,
    variant: SeriesDisplayVariant,
    option?: ViewerRoleOption
  ) => void;
  tertiaryRole?: ViewerRole;
  tertiaryVariant?: SeriesDisplayVariant;
  onSelectTertiaryRole?: (role: ViewerRole, variant: SeriesDisplayVariant) => void;
  showTertiaryRole?: boolean;
  leftGridLayout?: LeftViewerGrid;
  onSelectLeftGridLayout?: (layout: LeftViewerGrid) => void;
  closeRoleMenuSignal?: number;
  parcelizationControl?: React.ReactNode;
  parcelizationControlOpen?: boolean;
  orientationControl?: React.ReactNode;
  orientationControlOpen?: boolean;
  onCloseOrientationControl?: () => void;
  registrationControl?: React.ReactNode;
  registrationControlOpen?: boolean;
  registrationTools?: boolean;
  registrationReady?: boolean;
  registrationAlignmentEnabled?: boolean;
  onRegistrationAlignmentEnabledChange?: (enabled: boolean) => void;
}

const ViewerStrip: React.FC<ViewerStripProps> = ({
  slot,
  title,
  isVisible,
  onToggle,
  isMaximized,
  onToggleMaximize,
  canEdit,
  selectedRole,
  selectedVariant,
  roleOptions,
  onSelectAtlasContrast,
  onSelectRole,
  secondaryRole,
  secondaryVariant = 'original',
  secondaryRoleOptions,
  secondaryOptionKey,
  onSelectSecondaryRole,
  leftGridLayout = 1,
  onSelectLeftGridLayout,
  closeRoleMenuSignal,
  parcelizationControl,
  parcelizationControlOpen = false,
  orientationControl,
  orientationControlOpen = false,
  onCloseOrientationControl,
  registrationControl,
  registrationControlOpen = false,
  registrationTools = false,
  registrationReady = false,
  registrationAlignmentEnabled = false,
  onRegistrationAlignmentEnabledChange,
}) => {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [openRoleMenu, setOpenRoleMenu] = useState<string | null>(null);
  const [isAlignmentOpen, setIsAlignmentOpen] = useState(false);

  // The MR viewport is a same-origin iframe, so clicks inside it never reach
  // this document's pointerdown listener below. ProjectCreation bumps this
  // signal on every click it detects inside that iframe so the menu closes
  // on that click too, not just on clicks elsewhere on the outer page.
  useEffect(() => {
    setOpenRoleMenu(null);
    setIsAlignmentOpen(false);
  }, [closeRoleMenuSignal]);

  useEffect(() => {
    if (!orientationControlOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      const orientationControlElement =
        target instanceof Element ? target.closest('.pc-orientation-control') : null;
      if (!orientationControlElement || !stripRef.current?.contains(orientationControlElement)) {
        onCloseOrientationControl?.();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onCloseOrientationControl, orientationControlOpen]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      const selectedWrap =
        target instanceof Element ? target.closest('.pc-strip-select-wrap') : null;

      if (!selectedWrap || !stripRef.current?.contains(selectedWrap)) {
        setOpenRoleMenu(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!isAlignmentOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      const alignmentControl =
        target instanceof Element ? target.closest('.pc-registration-alignment-control') : null;
      if (!alignmentControl || !stripRef.current?.contains(alignmentControl)) {
        setIsAlignmentOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isAlignmentOpen]);

  const renderRoleSelect = (
    value: ViewerRole,
    variant: SeriesDisplayVariant,
    onChange: (role: ViewerRole, variant: SeriesDisplayVariant, option?: ViewerRoleOption) => void,
    ariaLabel: string,
    orderLabel: string,
    disabled = false,
    availableOptions: ViewerRoleOption[] = roleOptions,
    selectedOptionKey?: string
  ) => {
    const isRegistrationMoveSelect = registrationTools && orderLabel === '2';
    const selectedOption = availableOptions.find(
      option =>
        (selectedOptionKey
          ? option.key === selectedOptionKey
          : option.id === value && option.variant === variant) &&
        (isRegistrationMoveSelect || option.isCurrentAtlas !== false)
    );
    const selectedSourceLabel = isAtlasRole(selectedOption?.id)
      ? ''
      : getNiftiAlignmentLabel({
          variant: selectedOption?.variant,
          alignment_mode: selectedOption?.alignmentMode,
        });
    const selectedLabel = selectedOption
      ? `${selectedOption.label}${selectedOption.atlasTag ? ` · ${selectedOption.atlasTag}` : ''}${selectedSourceLabel ? ` · ${selectedSourceLabel}` : ''}`
      : value;
    const sourceGroups = isRegistrationMoveSelect
      ? [
          {
            label: 'MR',
            options: availableOptions.filter(
              option => !isAtlasRole(option.id) && !isCtRole(option.id)
            ),
          },
          {
            label: 'Brain Template MR',
            options: availableOptions.filter(
              option => isAtlasRole(option.id) && !isAtlasCtOption(option)
            ),
          },
          {
            label: 'Brain Template CT',
            options: availableOptions.filter(option => isAtlasCtOption(option)),
          },
          {
            label: 'CT',
            options: availableOptions.filter(
              option => !isAtlasRole(option.id) && isCtRole(option.id)
            ),
          },
        ]
      : [
          {
            label: 'MR',
            options: availableOptions.filter(
              option => !isAtlasRole(option.id) && !isCtRole(option.id)
            ),
          },
          {
            label: 'Brain Template',
            options: availableOptions.filter(option => isAtlasRole(option.id)),
          },
          { label: 'CT', options: availableOptions.filter(option => isCtRole(option.id)) },
        ];
    const centerSourceLabel =
      slot === 'center' && orderLabel === '1'
        ? 'Fixed image'
        : slot === 'center' && orderLabel === '2'
          ? 'Selection'
          : null;
    const isCenterMovingSource = slot === 'center' && orderLabel === '2';
    const centerSourceIcon =
      centerSourceLabel === 'Fixed image' ? (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 4h8M9 4v5l-3 3h12l-3-3V4M12 12v8M9 20h6" />
        </svg>
      ) : isCenterMovingSource ? (
        <WorkflowIcon name="viewer" />
      ) : (
        <WorkflowIcon name="viewer" />
      );

    return (
      <div
        className="pc-strip-select-wrap"
        title={centerSourceLabel ? `${centerSourceLabel}: ${selectedLabel}` : selectedLabel}
        data-center-source={centerSourceLabel?.toLowerCase().replace(' ', '-')}
      >
        <button
          type="button"
          className={`pc-strip-role-trigger ${openRoleMenu === ariaLabel ? 'open' : ''}`}
          onClick={() => {
            if (!disabled) {
              setIsAlignmentOpen(false);
              setOpenRoleMenu(current => (current === ariaLabel ? null : ariaLabel));
            }
          }}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="menu"
          aria-expanded={openRoleMenu === ariaLabel}
        >
          <span
            className={`pc-strip-source-icon ${centerSourceLabel === 'Fixed image' ? 'pc-strip-source-icon-fixed' : ''} ${isCenterMovingSource ? 'pc-strip-source-icon-moving' : ''}`}
            aria-hidden="true"
          >
            {centerSourceIcon}
          </span>
          <span
            className="pc-strip-select-order"
            aria-hidden="true"
          >
            {orderLabel}
          </span>
          <svg
            className="pc-strip-role-chevron"
            viewBox="0 0 12 12"
            aria-hidden="true"
          >
            <path d="M3 4.5 6 7.5l3-3" />
          </svg>
        </button>

        {openRoleMenu === ariaLabel && (
          <div
            className="pc-strip-role-menu"
            role="menu"
            aria-label={`${ariaLabel} options`}
          >
            <div className="pc-strip-role-menu-options">
              {sourceGroups.map((group, groupIndex) => (
                <React.Fragment key={group.label}>
                  {groupIndex > 0 && (
                    <span
                      className="pc-strip-role-option-separator"
                      role="separator"
                      aria-orientation="horizontal"
                    />
                  )}
                  <div
                    className="pc-strip-source-group"
                    role="group"
                    aria-label={group.label}
                  >
                    <span className="pc-strip-source-group-label">{group.label}</span>
                    {group.options.length === 0 && (
                      <span className="pc-strip-source-group-empty">No {group.label} selected</span>
                    )}
                    {group.options.map(option => {
                      const isSelected =
                        (selectedOptionKey
                          ? option.key === selectedOptionKey
                          : option.id === value && option.variant === variant) &&
                        (isRegistrationMoveSelect || option.isCurrentAtlas !== false);
                      return (
                        <button
                          key={option.key}
                          type="button"
                          className={`pc-strip-role-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            if (option.atlasId && !isRegistrationMoveSelect) {
                              onSelectAtlasContrast?.(option.atlasId);
                            }
                            onChange(option.id, option.variant, option);
                            setOpenRoleMenu(null);
                          }}
                          role="menuitemradio"
                          aria-checked={isSelected}
                          title={[option.label, option.atlasTag].filter(Boolean).join(' · ')}
                          data-atlas-id={option.atlasId}
                        >
                          <span className="pc-strip-role-option-label">
                            {isAtlasRole(option.id)
                              ? (option.atlasTag ?? option.label)
                              : option.label}
                          </span>
                          <span className="pc-strip-role-option-variant">
                            {isAtlasRole(option.id)
                              ? option.atlasTag
                                ? option.label
                                : 'Original'
                              : getNiftiAlignmentLabel({
                                  variant: option.variant,
                                  alignment_mode: option.alignmentMode,
                                })}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={stripRef}
      className={`pc-viewer-strip ${isVisible ? 'open' : 'closed'} ${
        openRoleMenu ||
        isAlignmentOpen ||
        parcelizationControlOpen ||
        orientationControlOpen ||
        registrationControlOpen
          ? 'menu-open'
          : ''
      }`}
    >
      <button
        type="button"
        className="pc-viewer-strip-toggle"
        onClick={onToggle}
        title={isVisible ? `Close ${title}` : `Open ${title}`}
        aria-label={isVisible ? `Close ${title}` : `Open ${title}`}
      >
        {isVisible ? '−' : '+'}
      </button>

      {isVisible && (
        <button
          type="button"
          className={`pc-viewer-strip-maximize ${isMaximized ? 'active' : ''}`}
          onClick={onToggleMaximize}
          title={isMaximized ? `Restore ${title}` : `Maximize ${title}`}
          aria-label={isMaximized ? `Restore ${title}` : `Maximize ${title}`}
          aria-pressed={isMaximized}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden="true"
          >
            {isMaximized ? (
              <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" />
            ) : (
              <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
            )}
          </svg>
        </button>
      )}

      {isVisible && (
        <span
          className="pc-viewer-strip-control-separator"
          role="separator"
          aria-orientation="horizontal"
        />
      )}

      {!isVisible && (
        <span
          className="pc-viewer-strip-workflow-icon"
          aria-hidden="true"
        >
          <WorkflowIcon name={COLLAPSED_VIEWER_WORKFLOW_ICONS[slot]} />
        </span>
      )}

      {isVisible && (
        <div className="pc-viewer-strip-controls">
          {registrationTools && registrationControl && (
            <div className="pc-strip-edit-wrap pc-registration-primary-control">
              {registrationControl}
            </div>
          )}

          {!registrationTools &&
            renderRoleSelect(
              selectedRole,
              selectedVariant,
              onSelectRole,
              `${slot} viewer role selector`,
              slot === 'center' ? '1' : ''
            )}

          {slot === 'center' &&
            secondaryRole &&
            onSelectSecondaryRole &&
            renderRoleSelect(
              secondaryRole,
              secondaryVariant,
              onSelectSecondaryRole,
              'Selection',
              '2',
              (secondaryRoleOptions?.length ?? 0) === 0,
              secondaryRoleOptions,
              secondaryOptionKey
            )}

          {registrationTools && (
            <div className="pc-registration-alignment-control">
              <button
                type="button"
                className={`pc-registration-static-btn pc-registration-alignment-btn ${isAlignmentOpen ? 'open' : ''}`}
                aria-label="Alignment"
                title="Alignment"
                aria-haspopup="dialog"
                aria-expanded={isAlignmentOpen}
                onClick={() => {
                  setOpenRoleMenu(null);
                  setIsAlignmentOpen(current => !current);
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <rect
                    x="4"
                    y="5"
                    width="8"
                    height="8"
                    rx="1.5"
                  />
                  <rect
                    x="12"
                    y="11"
                    width="8"
                    height="8"
                    rx="1.5"
                  />
                  <path d="M8 17h3M11 17l-1.5-1.5M11 17l-1.5 1.5M16 7h-3M13 7l1.5-1.5M13 7l1.5 1.5" />
                </svg>
                <span>Alignment</span>
              </button>

              {isAlignmentOpen && (
                <div
                  className="pc-orientation-popover pc-registration-alignment-popover"
                  role="dialog"
                  aria-label="Alignment settings"
                >
                  <div
                    className="pc-orientation-segmented-toggle"
                    role="group"
                    aria-label="Alignment mode"
                  >
                    <button
                      type="button"
                      className={registrationAlignmentEnabled ? 'active' : ''}
                      aria-pressed={registrationAlignmentEnabled}
                      aria-label="Alignment toggle"
                      disabled={!registrationReady}
                      onClick={() =>
                        onRegistrationAlignmentEnabledChange?.(!registrationAlignmentEnabled)
                      }
                    >
                      {registrationAlignmentEnabled ? 'On' : 'Off'}
                    </button>
                  </div>

                  <div
                    className="pc-viewer-edit-actions pc-orientation-actions"
                    role="group"
                    aria-label="Alignment actions"
                  >
                    <button
                      type="button"
                      className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-save"
                      disabled={!registrationReady || !registrationAlignmentEnabled}
                      onClick={() => setIsAlignmentOpen(false)}
                      aria-label="Save Alignment changes"
                      title="Save"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                      >
                        <path
                          d="M5 3.5h11l3 3V20.5H5z"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 3.5v5h8v-5M8.5 20.5v-5h7v5"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-reset"
                      disabled={!registrationReady}
                      onClick={() => onRegistrationAlignmentEnabledChange?.(false)}
                      aria-label="Reset Alignment changes"
                      title="Reset"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.9}
                        aria-hidden="true"
                      >
                        <path
                          d="M4.8 9A7.5 7.5 0 112 14"
                          strokeLinecap="round"
                        />
                        <path
                          d="M4.8 4.5V9H9.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="pc-viewer-edit-action pc-viewer-edit-icon-action pc-viewer-edit-cancel"
                      disabled={!registrationReady || !registrationAlignmentEnabled}
                      onClick={() => {
                        onRegistrationAlignmentEnabledChange?.(false);
                        setIsAlignmentOpen(false);
                      }}
                      aria-label="Cancel Alignment editing"
                      title="Cancel"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        aria-hidden="true"
                      >
                        <path
                          d="M6 6l12 12M18 6L6 18"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {slot === 'left' && (
            <div
              className="pc-strip-grid-toggles"
              role="group"
              aria-label="Left viewer grid layout"
            >
              {LEFT_VIEWER_GRID_OPTIONS.map(layout => (
                <button
                  type="button"
                  key={layout}
                  className={`pc-strip-grid-toggle ${leftGridLayout === layout ? 'active' : ''}`}
                  data-layout={layout}
                  onClick={() => onSelectLeftGridLayout?.(layout)}
                  title={`${layout} viewport layout`}
                  aria-label={`${layout} viewport layout`}
                  aria-pressed={leftGridLayout === layout}
                >
                  <Icons.ByName
                    name={LEFT_VIEWER_GRID_ICONS[layout]}
                    className="pc-strip-grid-icon"
                  />
                </button>
              ))}
            </div>
          )}

          {parcelizationControl ? (
            <div className="pc-strip-edit-wrap">{parcelizationControl}</div>
          ) : canEdit && orientationControl ? (
            <div className="pc-strip-edit-wrap">{orientationControl}</div>
          ) : null}

          {!registrationTools && registrationControl && (
            <div className="pc-strip-edit-wrap">{registrationControl}</div>
          )}

          {registrationTools && (
            <div
              className="pc-registration-static-actions"
              role="group"
              aria-label="Registration tools"
            >
              <button
                type="button"
                className="pc-registration-static-btn"
                disabled={!registrationReady}
                aria-label="Similarity"
                title="Similarity"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8"
                  />
                  <path d="M8.5 9.5h.01M15.5 9.5h.01M9 14.5c1.8 1.35 4.2 1.35 6 0" />
                </svg>
                <span>Similarity</span>
              </button>
              <span
                className="pc-registration-tool-separator"
                role="separator"
                aria-orientation="horizontal"
              />
              <button
                type="button"
                className="pc-registration-static-btn"
                disabled={!registrationReady}
                aria-label="Distortion Correction"
                title="Distortion Correction"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.7}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 7h14M5 12h14M5 17h14" />
                  <path d="m8 5-3 2 3 2M16 15l3 2-3 2" />
                </svg>
                <span>Distortion correction</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface FixedViewerLayoutSelectorProps {
  options: readonly FixedViewerLayoutOption[];
  selectedLayout: FixedViewerLayoutId;
  selectedParcelization3D?: boolean;
  uploadId: string | null | undefined;
  series: DicomSeries | null;
  liveThumbnailSrc?: string;
  liveThumbnailByOrientation?: Partial<Record<ProjectViewerOrientation, string>>;
  onSelectLayout: (layout: FixedViewerLayoutId, parcelizationOnly?: boolean) => void;
  onResetPan?: (orientation: ProjectViewerResetTarget) => void;
}

export const FixedViewerLayoutSelector: React.FC<FixedViewerLayoutSelectorProps> = ({
  options,
  selectedLayout,
  selectedParcelization3D = false,
  uploadId,
  series,
  liveThumbnailSrc,
  liveThumbnailByOrientation,
  onSelectLayout,
  onResetPan,
}) => {
  const studyInstanceUid = series?.studyInstanceUid;
  const seriesInstanceUid = series?.seriesInstanceUid;
  const niftiAssetId = series?.niftiAsset?.asset_id;
  const thumbnailOptions = options.filter(option => !option.isParcelization);
  const hasCompleteStaticThumbnailSet = thumbnailOptions.every(option =>
    Boolean(SERIES_LAYOUT_THUMBNAILS[option.id])
  );
  const previewSrc = useMemo(
    () =>
      !hasCompleteStaticThumbnailSet &&
      uploadId &&
      studyInstanceUid &&
      seriesInstanceUid &&
      niftiAssetId
        ? buildOHIFViewerUrl(uploadId, studyInstanceUid, seriesInstanceUid, {
            hangingProtocolId: 'projectSeriesPreview',
            previewMode: true,
            niftiAssetId,
          })
        : null,
    [hasCompleteStaticThumbnailSet, niftiAssetId, seriesInstanceUid, studyInstanceUid, uploadId]
  );

  return (
    <div
      className="pc-fixed-layout-selector"
      data-testid="fixed-view-layout-selector"
    >
      <div
        className={`pc-fixed-layout-composite ${options.some(option => option.isParcelization) ? 'pc-fixed-layout-composite-with-parcelization' : ''}`}
      >
        {previewSrc ? (
          <iframe
            className="pc-ohif-preview-frame"
            loading="eager"
            src={previewSrc}
            title="Series view previews"
          />
        ) : (
          <div
            className="pc-fixed-layout-preview-fallback"
            aria-hidden="true"
          />
        )}

        <div
          className="pc-fixed-layout-hit-grid"
          style={{ gridTemplateRows: `repeat(${options.length}, minmax(0, 1fr))` }}
        >
          {options.map(option => {
            const isSelected = option.isParcelization
              ? selectedParcelization3D
              : !selectedParcelization3D && option.id === selectedLayout;
            const resetTarget = FIXED_VIEWER_LAYOUT_RESET_TARGETS[option.id];
            const orientation = resetTarget === '3d' ? undefined : resetTarget;
            const legacyDynamicThumbnailSrc = orientation
              ? (liveThumbnailByOrientation?.[orientation] ??
                (isSelected ? liveThumbnailSrc : undefined))
              : undefined;
            const optionThumbnailSrc = option.isParcelization
              ? parcelizationThumbnail
              : (SERIES_LAYOUT_THUMBNAILS[option.id] ?? legacyDynamicThumbnailSrc);

            return (
              <div
                key={`${option.id}-${option.isParcelization ? 'parcelization' : 'base'}`}
                className={`pc-fixed-layout-card ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectLayout(option.id, Boolean(option.isParcelization))}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectLayout(option.id, Boolean(option.isParcelization));
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`${option.label} gorunumunu sec`}
                aria-pressed={isSelected}
                data-layout-id={option.id}
                data-parcelization={option.isParcelization ? 'true' : undefined}
              >
                {optionThumbnailSrc && (
                  <img
                    className="pc-fixed-layout-live-image"
                    data-testid="series-static-thumbnail"
                    src={optionThumbnailSrc}
                    alt={`${option.label} thumbnail`}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <button
                  type="button"
                  className="pc-fixed-layout-pan-reset"
                  aria-label={`Reset ${option.label} pan`}
                  title={`Reset ${option.label} pan`}
                  onClick={event => {
                    event.stopPropagation();
                    onResetPan?.(resetTarget);
                  }}
                  onKeyDown={event => event.stopPropagation()}
                >
                  <img
                    src={resetIcon}
                    alt=""
                    draggable={false}
                    aria-hidden="true"
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── Viewer panel ── */
interface RegistrationOverlayFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

type RegistrationInteractionKind = 'drag' | 'resize-nw' | 'resize-ne' | 'resize-sw' | 'resize-se';

interface ViewerPanelProps {
  slot: ViewerSlot;
  series: DicomSeries | null;
  selectedRole: ViewerRole;
  registrationSeries?: DicomSeries | null;
  registrationRole?: ViewerRole;
  registrationVariant?: SeriesDisplayVariant;
  registrationAlignmentEnabled?: boolean;
  isVisible?: boolean;
  isEditing?: boolean;
  editRotationCount?: number;
  reloadVersion?: number;
  fixedDicomViewportState?: FixedDicomViewportState;
  fixedViewerLayout?: FixedViewerLayoutId;
  fixedViewerOrientationRequestVersion?: number;
  fixedViewerPanResetRequest?: ProjectViewerPanResetRequest | null;
  onSelectRole: (role: ViewerRole) => void;
  roleOptions?: { id: ViewerRole; label: string }[];
  secondaryRole?: ViewerRole;
  onSelectSecondaryRole?: (role: ViewerRole) => void;
  tertiaryRole?: ViewerRole;
  onSelectTertiaryRole?: (role: ViewerRole) => void;
  showTertiaryRole?: boolean;
  leftGridLayout?: LeftViewerGrid;
  onSelectLeftGridLayout?: (layout: LeftViewerGrid) => void;
  onSliceThumbnail?: (thumbnail: ProjectViewerSliceThumbnail) => void;
  onViewerInteraction?: () => void;
  onActiveView?: (view: ProjectViewerSelectedView) => void;
  onRotation?: (rotation: ProjectViewerRotation) => void;
  parcelizations?: ProjectViewerParcelizationVolume[];
  parcelizationOnly?: boolean;
}

export const ViewerPanel: React.FC<ViewerPanelProps> = ({
  slot,
  series,
  selectedRole,
  registrationSeries = null,
  registrationRole,
  registrationVariant = 'original',
  registrationAlignmentEnabled = false,
  isVisible = true,
  isEditing = false,
  editRotationCount = 0,
  reloadVersion = 0,
  fixedDicomViewportState,
  fixedViewerLayout = 'singleAxial',
  fixedViewerOrientationRequestVersion = 0,
  fixedViewerPanResetRequest,
  leftGridLayout = 1,
  onSliceThumbnail,
  onViewerInteraction,
  onActiveView,
  onRotation,
  parcelizations = [],
  parcelizationOnly = false,
}) => {
  const isLeftViewer = slot === 'left';
  const [registrationViewportState, setRegistrationViewportState] =
    useState<ProjectViewerRegistrationViewportState | null>(null);
  const [registrationTransformState, setRegistrationTransformState] = useState<{
    sourceKey: string;
    transforms: ProjectViewerRegistrationTransform[];
  } | null>(null);
  const registrationBodyRef = useRef<HTMLDivElement | null>(null);
  const registrationInteractionRef = useRef<{
    kind: RegistrationInteractionKind;
    startX: number;
    startY: number;
    bodyWidth: number;
    bodyHeight: number;
    frame: RegistrationOverlayFrame;
  } | null>(null);
  const [registrationFrame, setRegistrationFrame] = useState<RegistrationOverlayFrame>({
    left: 13,
    top: 10,
    width: 72,
    height: 72,
  });
  const fixedSourceViewport = resolveFixedDicomViewportState(
    slot,
    selectedRole,
    fixedDicomViewportState,
    series
  );
  const registrationSeriesKey =
    slot === 'center' && registrationSeries && registrationRole
      ? [
          registrationRole,
          registrationVariant,
          registrationSeries.uploadId,
          registrationSeries.studyInstanceUid,
          registrationSeries.seriesInstanceUid,
          registrationSeries.niftiAsset?.asset_id,
          registrationSeries.alignedNiftiAsset?.asset_id,
          registrationSeries.viewerConfigPath,
        ]
          .filter(Boolean)
          .join(':')
      : null;
  const registrationFixedSourceKey =
    slot === 'center'
      ? [
          selectedRole,
          series?.uploadId,
          series?.studyInstanceUid,
          series?.seriesInstanceUid,
          series?.niftiAsset?.asset_id,
          series?.alignedNiftiAsset?.asset_id,
          series?.viewerConfigPath,
        ]
          .filter(Boolean)
          .join(':')
      : null;
  const registrationTransformSourceKey = `${registrationFixedSourceKey ?? ''}|${registrationSeriesKey ?? ''}`;
  const registrationTransforms =
    registrationTransformState?.sourceKey === registrationTransformSourceKey
      ? registrationTransformState.transforms
      : [];
  const moveSourceViewport =
    registrationSeries && registrationRole
      ? resolveFixedDicomViewportState('center', registrationRole, undefined, registrationSeries)
      : null;
  const canRenderRegistrationOverlay = Boolean(
    slot === 'center' &&
      registrationSeriesKey &&
      fixedSourceViewport.shouldRender &&
      moveSourceViewport?.shouldRender &&
      moveSourceViewport.uploadId &&
      moveSourceViewport.studyInstanceUid &&
      moveSourceViewport.seriesInstanceUid
  );
  // Keep Fixed as the native base viewport and Move as the synchronized
  // overlay. The Move layer receives an inverse rectangular mask below, so it
  // remains visible outside the movable window while the window reveals the
  // already-rendered Fixed source underneath. This avoids mounting Fixed in a
  // clipped secondary iframe, which can stay black while WebGL initializes.
  const fixedDicomViewport = fixedSourceViewport;
  const registrationViewport = canRenderRegistrationOverlay ? moveSourceViewport : null;
  const shouldRenderEmbeddedOhifViewer = fixedDicomViewport.shouldRender;
  const renderedViewerRole = selectedRole;
  const renderedViewerSeries = series;
  // The Move iframe sits above the Fixed iframe. Besides the movable reveal,
  // punch through the left and right control rails so the Fixed viewport's
  // single, synchronized tool set remains visible and interactive. Recreating
  // those controls on the Move iframe would introduce a second source of
  // camera/slice/VOI state.
  const registrationMaskPositionX =
    registrationFrame.width >= 100
      ? 0
      : (registrationFrame.left / (100 - registrationFrame.width)) * 100;
  const registrationMaskPositionY =
    registrationFrame.height >= 100
      ? 0
      : (registrationFrame.top / (100 - registrationFrame.height)) * 100;
  const registrationMoveMask = Array.from({ length: 5 }, () => 'linear-gradient(#000 0 0)').join(
    ', '
  );
  const registrationMoveMaskSize = [
    '100% 100%',
    `${registrationFrame.width}% ${registrationFrame.height}%`,
    '30px 100%',
    // Includes the rotation arc and the orientation marker shifted beside it.
    '100px 100%',
    '124px 84px',
  ].join(', ');
  const registrationMoveMaskPosition = [
    '0 0',
    `${registrationMaskPositionX}% ${registrationMaskPositionY}%`,
    'left top',
    'right top',
    'right bottom',
  ].join(', ');

  useEffect(() => {
    setRegistrationFrame({ left: 13, top: 10, width: 72, height: 72 });
    setRegistrationViewportState(null);
    setRegistrationTransformState(null);
  }, [registrationFixedSourceKey, registrationSeriesKey]);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const interaction = registrationInteractionRef.current;
      const body = registrationBodyRef.current;
      if (!interaction || !body) return;

      const deltaX = ((event.clientX - interaction.startX) / interaction.bodyWidth) * 100;
      const deltaY = ((event.clientY - interaction.startY) / interaction.bodyHeight) * 100;
      const frame = interaction.frame;

      if (interaction.kind === 'drag') {
        setRegistrationFrame(current => ({
          ...current,
          left: Math.min(100 - current.width, Math.max(0, frame.left + deltaX)),
          top: Math.min(100 - current.height, Math.max(0, frame.top + deltaY)),
        }));
      } else {
        const startRight = frame.left + frame.width;
        const startBottom = frame.top + frame.height;
        const isLeft = interaction.kind.endsWith('-nw') || interaction.kind.endsWith('-sw');
        const isTop = interaction.kind.endsWith('-nw') || interaction.kind.endsWith('-ne');
        const nextLeft = isLeft
          ? Math.min(startRight, Math.max(0, frame.left + deltaX))
          : frame.left;
        const nextTop = isTop ? Math.min(startBottom, Math.max(0, frame.top + deltaY)) : frame.top;
        const nextRight = isLeft
          ? startRight
          : Math.max(frame.left, Math.min(100, startRight + deltaX));
        const nextBottom = isTop
          ? startBottom
          : Math.max(frame.top, Math.min(100, startBottom + deltaY));

        setRegistrationFrame({
          left: nextLeft,
          top: nextTop,
          width: Math.max(0, nextRight - nextLeft),
          height: Math.max(0, nextBottom - nextTop),
        });
      }
    };
    const handlePointerUp = () => {
      registrationInteractionRef.current = null;
      document.body.style.removeProperty('user-select');
      document.body.style.removeProperty('cursor');
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const beginRegistrationInteraction = (
    event: React.PointerEvent<HTMLElement>,
    kind: RegistrationInteractionKind
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    const body = registrationBodyRef.current;
    if (!body) return;
    const rect = body.getBoundingClientRect();
    registrationInteractionRef.current = {
      kind,
      startX: event.clientX,
      startY: event.clientY,
      bodyWidth: Math.max(rect.width, 1),
      bodyHeight: Math.max(rect.height, 1),
      frame: registrationFrame,
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor =
      kind === 'drag'
        ? 'grabbing'
        : kind.replace('resize-', '') === 'nw' || kind.replace('resize-', '') === 'se'
          ? 'nwse-resize'
          : 'nesw-resize';
  };

  const handleRegistrationViewportState = useCallback(
    (state: ProjectViewerRegistrationViewportState) => {
      setRegistrationViewportState(current => {
        if (current && JSON.stringify(current) === JSON.stringify(state)) {
          return current;
        }
        return state;
      });
    },
    []
  );

  const handleRegistrationTransform = useCallback(
    (transform: ProjectViewerRegistrationTransform) => {
      setRegistrationTransformState(current => ({
        sourceKey: registrationTransformSourceKey,
        transforms:
          current?.sourceKey === registrationTransformSourceKey
            ? [...current.transforms, transform]
            : [transform],
      }));
    },
    [registrationTransformSourceKey]
  );

  const normalizeRegistrationModalityLabel = (label: string) => {
    const normalized = label.trim().toUpperCase();
    if (normalized === 'T1W') return 'T1w';
    if (normalized === 'T2W') return 'T2w';
    if (normalized === 'CT') return 'CT';
    return label;
  };
  const isRegistrationModalityTag = (label: string) =>
    ['T1W', 'T2W', 'CT'].includes(label.trim().toUpperCase());
  const registrationSummary =
    slot === 'center' && series && registrationSeries && registrationRole
      ? {
          fixedModality: normalizeRegistrationModalityLabel(
            isAtlasRole(selectedRole)
              ? (series.atlasContrast ?? series.modality)
              : (series.modality ?? selectedRole)
          ),
          fixedVariant: isAtlasRole(selectedRole)
            ? 'Original'
            : series.niftiAsset?.variant === 'aligned'
              ? 'Registered'
              : 'Original',
          movingModality: normalizeRegistrationModalityLabel(
            isAtlasRole(registrationRole)
              ? (registrationSeries.atlasContrast ?? registrationSeries.modality)
              : registrationSeries.modality
          ),
          movingVariant: isAtlasRole(registrationRole)
            ? 'Original'
            : registrationVariant === 'aligned'
              ? 'Registered'
              : 'Original',
        }
      : null;
  return (
    <div
      className={`pc-viewer-panel pc-viewer-${slot} ${isVisible ? 'visible' : 'kept-alive'}`}
      aria-hidden={!isVisible}
    >
      {series && (
        <div
          className={`pc-viewer-edit-bar ${isEditing ? 'is-editing' : ''}`}
          role="status"
          aria-live="polite"
        >
          {isLeftViewer && <span className="pc-viewer-panel-heading">Panel Viewer</span>}
          {registrationSummary ? (
            <span
              className="pc-viewer-registration-summary"
              data-testid="viewer-registration-summary"
              aria-label={`${registrationSummary.fixedModality} ${registrationSummary.fixedVariant} with ${registrationSummary.movingModality} ${registrationSummary.movingVariant}`}
            >
              <span
                className={
                  isRegistrationModalityTag(registrationSummary.fixedModality)
                    ? 'pc-viewer-series-tag'
                    : 'pc-viewer-registration-text'
                }
              >
                {registrationSummary.fixedModality}
              </span>
              <span className="pc-viewer-registration-text">
                {registrationSummary.fixedVariant}
              </span>
              <span className="pc-viewer-registration-connector">with</span>
              <span
                className={
                  isRegistrationModalityTag(registrationSummary.movingModality)
                    ? 'pc-viewer-series-tag'
                    : 'pc-viewer-registration-text'
                }
              >
                {registrationSummary.movingModality}
              </span>
              <span className="pc-viewer-registration-text">
                {registrationSummary.movingVariant}
              </span>
            </span>
          ) : (
            <div className="pc-viewer-series-summary">
              <span
                className="pc-viewer-series-tag"
                title={
                  isAtlasRole(selectedRole)
                    ? (series.atlasContrast ?? series.modality)
                    : series.modality
                }
              >
                {isAtlasRole(selectedRole)
                  ? (series.atlasContrast ?? series.modality)
                  : series.modality}
              </span>
              {!isAtlasRole(selectedRole) && (
                <span
                  className="pc-viewer-series-variant"
                  data-testid="viewer-series-variant"
                >
                  {getNiftiAlignmentLabel(series.niftiAsset, isEditing && editRotationCount > 0)}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      <div
        ref={registrationBodyRef}
        className={`pc-viewer-body ${
          isLeftViewer && series && !shouldRenderEmbeddedOhifViewer
            ? `pc-viewer-body-grid pc-viewer-grid-${leftGridLayout}`
            : ''
        }`}
      >
        {fixedDicomViewport.hasMissingIdentifiers ? (
          <ViewerEmptyState
            slot={slot}
            error
          />
        ) : shouldRenderEmbeddedOhifViewer ? (
          <OHIFEmbeddedViewer
            key={[
              fixedDicomViewport.uploadId,
              fixedDicomViewport.seriesInstanceUid,
              fixedDicomViewport.niftiAssetId ?? 'dicom',
            ].join(':')}
            hangingProtocolId={fixedViewerLayout}
            gridLayout={slot === 'center' ? 1 : leftGridLayout}
            orientationRequestVersion={fixedViewerOrientationRequestVersion}
            panResetRequest={
              slot === 'left' && !isAtlasRole(selectedRole) ? fixedViewerPanResetRequest : null
            }
            uploadId={fixedDicomViewport.uploadId!}
            studyInstanceUid={fixedDicomViewport.studyInstanceUid!}
            seriesInstanceUid={fixedDicomViewport.seriesInstanceUid!}
            niftiAssetId={fixedDicomViewport.niftiAssetId ?? undefined}
            modality={isCtRole(renderedViewerRole) ? 'CT' : renderedViewerSeries?.modality}
            viewerConfigPath={fixedDicomViewport.viewerConfigPath ?? undefined}
            onSliceThumbnail={onSliceThumbnail}
            onViewerInteraction={onViewerInteraction}
            onActiveView={onActiveView}
            onRotation={isEditing ? onRotation : undefined}
            registrationSyncRole={
              slot === 'center' && canRenderRegistrationOverlay ? 'master' : undefined
            }
            onRegistrationState={
              slot === 'center' && canRenderRegistrationOverlay
                ? handleRegistrationViewportState
                : undefined
            }
            registrationAlignmentEnabled={registrationAlignmentEnabled}
            onRegistrationTransform={
              slot === 'center' && canRenderRegistrationOverlay
                ? handleRegistrationTransform
                : undefined
            }
            reloadVersion={reloadVersion}
            isEditing={isEditing}
            parcelizations={parcelizations}
            parcelizationOnly={parcelizationOnly}
          />
        ) : series ? (
          isLeftViewer ? (
            Array.from({ length: leftGridLayout }).map((_, viewportIndex) => {
              const sliceNumber = Math.min(
                series.slices,
                Math.max(
                  1,
                  Math.round(((viewportIndex + 1) / (leftGridLayout + 1)) * series.slices)
                )
              );

              return (
                <div
                  key={viewportIndex}
                  id={`left-viewport-${viewportIndex + 1}`}
                  className="pc-left-grid-cell"
                  data-viewport-index={viewportIndex}
                >
                  <span className="pc-grid-cell-index">V{viewportIndex + 1}</span>
                  <span className="pc-orientation-label pc-orientation-left">L</span>
                  <span className="pc-orientation-label pc-orientation-right">R</span>

                  <div className="pc-viewer-brain pc-grid-cell-brain">
                    <NeutralDicomThumbnail />
                  </div>

                  <div className="pc-crosshair pc-crosshair-h" />
                  <div className="pc-crosshair pc-crosshair-v" />

                  <span className="pc-slice-indicator">
                    {sliceNumber}/{series.slices}
                  </span>
                </div>
              );
            })
          ) : (
            <>
              <span className="pc-orientation-label pc-orientation-left">L</span>
              <span className="pc-orientation-label pc-orientation-right">R</span>

              <div className="pc-viewer-brain">
                <NeutralDicomThumbnail />
              </div>

              <div className="pc-crosshair pc-crosshair-h" />
              <div className="pc-crosshair pc-crosshair-v" />

              <span className="pc-slice-indicator">1/{series.slices}</span>
            </>
          )
        ) : (
          <ViewerEmptyState slot={slot} />
        )}

        {canRenderRegistrationOverlay && registrationViewport && (
          <div
            className="pc-registration-overlay"
            style={{
              WebkitMaskImage: registrationMoveMask,
              WebkitMaskSize: registrationMoveMaskSize,
              WebkitMaskPosition: registrationMoveMaskPosition,
              WebkitMaskRepeat: 'no-repeat',
              WebkitMaskComposite: 'xor, source-over, source-over, source-over, source-over',
              maskImage: registrationMoveMask,
              maskSize: registrationMoveMaskSize,
              maskPosition: registrationMoveMaskPosition,
              maskRepeat: 'no-repeat',
              maskComposite: 'exclude, add, add, add, add',
            }}
            aria-hidden="true"
          >
            <div className="pc-registration-overlay-body">
              <OHIFEmbeddedViewer
                key={[
                  'registration-overlay',
                  registrationViewport.uploadId,
                  registrationViewport.studyInstanceUid,
                  registrationViewport.seriesInstanceUid,
                  registrationViewport.niftiAssetId ?? 'dicom',
                ].join(':')}
                hangingProtocolId={fixedViewerLayout}
                gridLayout={1}
                uploadId={registrationViewport.uploadId!}
                studyInstanceUid={registrationViewport.studyInstanceUid!}
                seriesInstanceUid={registrationViewport.seriesInstanceUid!}
                niftiAssetId={registrationViewport.niftiAssetId ?? undefined}
                modality={
                  registrationRole && isCtRole(registrationRole)
                    ? 'CT'
                    : registrationSeries?.modality
                }
                viewerConfigPath={registrationViewport.viewerConfigPath ?? undefined}
                registrationSyncRole="slave"
                registrationState={registrationViewportState}
                registrationTransforms={registrationTransforms}
                registrationSyncVoi={true}
                registrationSyncCamera={!registrationAlignmentEnabled}
                reloadVersion={reloadVersion}
                isEditing={false}
              />
            </div>
          </div>
        )}

        {canRenderRegistrationOverlay && (
          <div
            className="pc-registration-window-hitbox"
            style={{
              left: `${registrationFrame.left}%`,
              top: `${registrationFrame.top}%`,
              width: `${registrationFrame.width}%`,
              height: `${registrationFrame.height}%`,
            }}
            aria-hidden="true"
            onPointerDown={event => beginRegistrationInteraction(event, 'drag')}
          >
            <span
              className="pc-registration-overlay-resize pc-registration-overlay-resize-nw"
              aria-hidden="true"
              onPointerDown={event => beginRegistrationInteraction(event, 'resize-nw')}
            />
            <span
              className="pc-registration-overlay-resize pc-registration-overlay-resize-ne"
              aria-hidden="true"
              onPointerDown={event => beginRegistrationInteraction(event, 'resize-ne')}
            />
            <span
              className="pc-registration-overlay-resize pc-registration-overlay-resize-sw"
              aria-hidden="true"
              onPointerDown={event => beginRegistrationInteraction(event, 'resize-sw')}
            />
            <span
              className="pc-registration-overlay-resize pc-registration-overlay-resize-se"
              aria-hidden="true"
              onPointerDown={event => beginRegistrationInteraction(event, 'resize-se')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectCreation;
